import axios, { type AxiosInstance } from 'axios';
import { cachedBlobPost, stableKey } from '@/utils/renderCache';
import type {
  LoginResponse,
  EstimateReviewResult,
  BillItem,
  CatalogueItem,
  Job,
  Order,
  OrderUpdate,
  Supplier,
  CostCentre,
  CostCentreWithBudget,
  ExplodedItem,
  AssemblyLineItem,
  AssemblyItemsResult,
  Contact,
  ContactGroup,
  ContactTypeDef,
  ContactTag,
  ContactRelationship,
  ContactActivity,
  ContactLetterCount,
  ContactReminder,
  DocumentAlert,
  ContactDocument,
  Project,
  ProjectTask,
  WorkingCalendar,
  ProjectColumn,
  ProjectSummary,
  TaskDependency,
  GanttTask,
  LinkedItem,
  TaskStatus,
  // Accounting types
  Transaction,
  TransactionSearchOptions,
  CreateTransactionData,
  SupplierInvoice,
  OutstandingClaim,
  PaymentBatch,
  PaymentBatchStatus,
  PayableInvoice,
  ABASettings,
  AgedPayable,
  AgedReceivable,
  SupplierStatement,
  QuickBooksStatus,
  QuickBooksSyncResult,
  GLMapping,
  QuickBooksAccount,
  // Claims types
  ClaimableCostCentre,
  ClaimProgressItem,
  CreateClaimData,
  ClaimPreviewResult,
  ClaimHistoryItem,
  ClaimSummary,
  // Invoice Matching types
  OrderReceipt,
  InvoiceMatch,
  VarianceAction,
  Backorder,
  OrderMatchData,
  InvoiceLineMatchInput,
  InvoiceSmartMatchResult,
  ReportDefinition,
  ReportDefinitionRecord,
  ReportWriterTemplateRecord,
  ReportBrandingDefaults,
  SaveReportDefinitionInput,
  QueryBlockRecord,
  CreateQueryBlockInput,
  UpdateQueryBlockInput,
  InvoiceMatchResult,
  VarianceActionRequest,
  ApplyRetentionRequest,
  ReleaseRetentionRequest,
  RecordGRNRequest,
  BackorderReceiptInput,
  InvoiceMatchFilters,
  InvoiceMatchingSummary,
  // Job Communications types
  JobCommunication,
  // Contract Claims types
  ClaimTemplate,
  CreateClaimTemplateData,
  UpdateClaimTemplateData,
  ClaimScheduleStage,
  ContractSummary,
  ContractVariation,
  CreateVariationData,
  PostedClaim,
  ContractReceipt,
  RecordReceiptData,
  ContractTransaction,
  // Document Management types
  Document,
  DocumentVersion,
  DocumentCollection,
  DocumentSavedView,
  DocumentMarkup,
  DocumentComment,
  DocumentAttributeDefinition,
  AttributeType,
  WorkflowTemplate,
  WorkflowInstance,
  WorkflowTask,
  WorkflowNotification,
  CreateDocumentData,
  UpdateDocumentData,
  CreateCollectionData,
  UpdateCollectionData,
  CreateSavedViewData,
  UpdateSavedViewData,
  CreateMarkupData,
  UpdateMarkupData,
  CreateWorkflowTemplateData,
  UpdateWorkflowTemplateData,
  StartWorkflowData,
  // Sales CRM types
  Lead,
  LeadActivity,
  // Tender types
  TenderData,
  TenderMarginAnalysis,
  Quote,
  // Transmittal types
  Transmittal,
  TransmittalDocument,
  TransmittalDocumentUsage,
  TransmittalAccessLink,
  TransmittalAccessLog,
  TransmittalAcknowledgement,
  TransmittalRecipientStatus,
  TransmittalFolder,
  TransmittalFolderType,
  CreateTransmittalData,
  UpdateTransmittalData,
  CreateAccessLinkData,
  CreateTransmittalFolderData,
  UpdateTransmittalFolderData,
  // Daily Log types
  DailyLog,
  DailyLogPhoto,
  CreateDailyLogData,
  UpdateDailyLogData,
  DailyLogFilters,
  // RFI types
  RFI,
  RFINote,
  RFIRecipient,
  CreateRFIData,
  UpdateRFIData,
  // RFQ types
  RFQ,
  RFQStatus,
  RFQItem,
  RFQSupplier,
  CreateRFQData,
  UpdateRFQData,
  CreateRFQItemData,
  RecordQuoteData,
  QuoteComparisonData,
  // Integration types
  IntegrationPlatform,
  IntegrationSettings,
  IntegrationSyncResult,
  IntegrationSyncRecord,
  GLMappingEntry,
  RemoteAccount,
  DocusignConnectionStatus,
  // Settings types
  CompanySettings,
  CompanySettingsMetadata,
  PortalContentInput,
  UnitFull,
  CostCentreFull,
  UserConfig,
  AuditLogEntry,
  AuditLogSearchOptions,
  ChecklistTemplate,
  EmployeeCertification,
  Induction,
  SwmsEntry,
  Incident,
  ToolboxTalk,
  ToolboxTalkAttendee,
  Inspection,
  InspectionItem,
  InspectionSchedule,
  InspectionStats,
  Defect,
  DefectStatus,
  InspectionStatus,
} from '@dbx/shared';

// ── Selection templates + job spec sections (client-side shapes) ─────────────
export interface SpecInclusion { id: string; name: string; description?: string; sortOrder?: number }
export interface SpecSection { id: string; name: string; sortOrder?: number; inclusions?: SpecInclusion[] }
export interface TemplateOption {
  name: string; description?: string; priceExGst?: number; unitRate?: number;
  imageUrl?: string; isStandardInclusion?: boolean; addOns?: Array<{ label: string; rate?: number; flat?: number }>; sortOrder?: number;
}
export interface TemplateItem {
  id: string; type: 'selection' | 'inclusion'; name: string; description?: string; sortOrder?: number;
  pricingMode?: 'flat' | 'qty_rate'; required?: boolean; defaultQuantity?: number; estimateExGst?: number;
  costCentreCode?: string; options?: TemplateOption[];
}
export interface TemplateSection { id: string; name: string; sortOrder?: number; items: TemplateItem[] }
export interface SelectionTemplate { id: string; name: string; description: string | null; sections: TemplateSection[] }
export interface SelectionTemplateSummary { id: string; name: string; description: string | null; updatedAt: string; sectionCount: number; itemCount: number }
export interface SelectionTemplateBody { name: string; description?: string; sections: TemplateSection[] }

// Attribute Definition types (not in shared package)
export interface CreateAttributeDefinitionData {
  attributeName: string;
  displayName: string;
  attributeType: AttributeType;
  options?: string[];
  isRequired?: boolean;
  isFilterable?: boolean;
  isSearchable?: boolean;
  defaultValue?: string;
  sortOrder?: number;
  appliesTo?: string[];
}

export interface UpdateAttributeDefinitionData {
  displayName?: string;
  attributeType?: AttributeType;
  options?: string[];
  isRequired?: boolean;
  isFilterable?: boolean;
  isSearchable?: boolean;
  defaultValue?: string;
  sortOrder?: number;
  appliesTo?: string[];
}

// Invitation types
export interface Invitation {
  id: string;
  email: string;
  tenantId: string;
  tenantName?: string;
  role: string;
  securityLevel: number;
  orderLimit: number | null;
  budgetLimit: number | null;
  variationLimit: number | null;
  canSetup: boolean;
  canPost: boolean;
  status: string;
  expiresAt: string;
  invitedByName?: string;
  createdAt: string;
}

export interface CreateInvitationData {
  email: string;
  role?: string;
  securityLevel?: number;
  orderLimit?: number | null;
  budgetLimit?: number | null;
  variationLimit?: number | null;
  canSetup?: boolean;
  canPost?: boolean;
}

export interface CreateInvitationResponse {
  success: boolean;
  data: {
    invitation: Invitation;
    inviteLink: string;
    emailSent: boolean;
    message: string;
  };
}

export interface InvitationValidation {
  email: string;
  tenant: { id: string; name: string; slug: string };
  role: string;
}

// Classification Import types
export interface ClassificationTable {
  id: string;
  name: string;
  description?: string;
  url?: string;
  format: 'json' | 'csv';
  recommended?: boolean;
}

export interface ClassificationSystem {
  id: string;
  name: string;
  description: string;
  country: string;
  sourceType: 'github' | 'url' | 'file';
  tables: ClassificationTable[];
}

export interface FlatClassificationItem {
  code: string;
  name: string;
  tier: 1 | 2;
  parentCode?: string;
  sortOrder: number;
}

export interface ClassificationConflict {
  code: string;
  existingName: string;
  newName: string;
  tier: 1 | 2;
}

export interface ClassificationPreview {
  system: string;
  table: string;
  tradeGroups: number;
  costCentres: number;
  conflicts: ClassificationConflict[];
  items: FlatClassificationItem[];
}

export interface ClassificationImportOptions {
  conflictHandling: 'skip' | 'replace' | 'merge';
  codePrefix?: string;
  archiveExisting?: boolean;
}

export interface ClassificationImportResult {
  success: boolean;
  tradeGroupsCreated: number;
  costCentresCreated: number;
  skipped: number;
  replaced: number;
  errors: Array<{ code: string; error: string }>;
}

// Cost Centre Reassignment Types
export interface CostCentreWithUsage {
  id: string;
  code: string;
  name: string;
  tier: number;
  parentCode: string | null;
  isArchived: boolean;
  catalogueItemCount: number;
  billItemCount: number;
  metadata: Record<string, unknown> | null;
}

export interface ReassignmentMapping {
  fromCostCentreId: string;
  toCostCentreId: string;
}

export interface ReassignmentPreview {
  mappings: Array<{
    from: { id: string; code: string; name: string };
    to: { id: string; code: string; name: string };
    catalogueItemCount: number;
    billItemCount: number;
  }>;
  totalCatalogueItems: number;
  totalBillItems: number;
  unmappedCostCentres: Array<{
    id: string;
    code: string;
    name: string;
    catalogueItemCount: number;
    billItemCount: number;
  }>;
}

export interface ReassignmentResult {
  success: boolean;
  catalogueItemsUpdated: number;
  billItemsUpdated: number;
  costCentresArchived: number;
  errors: Array<{ code: string; error: string }>;
}

// Classification Seed Types (Local Files with Sample Data)
export interface ClassificationFile {
  fileName: string;
  systemName: string;
  version: string;
  description?: string;
  totalItems: number;
}

export interface SeedPreview {
  fileName: string;
  systemName: string;
  version: string;
  maxLevel: number;
  totalItems: number;
  itemsToImport: number;
  tier1Count: number;
  tier2Count: number;
  estimatedCatalogueItems: number;
  sampleItems: Array<{ code: string; name: string; level: number }>;
}

export interface SeedResult {
  success: boolean;
  costCentresCreated: number;
  catalogueItemsCreated: number;
  systemName: string;
  version: string;
  errors: string[];
}

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

let apiClient: AxiosInstance | null = null;

export function getApiClient(): AxiosInstance {
  if (!apiClient) {
    apiClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor for auth
    apiClient.interceptors.request.use((config) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Response interceptor for token refresh. Concurrent 401s share ONE in-flight
    // refresh (each /auth/refresh does a bcrypt scan of all sessions server-side),
    // so we don't fire a burst of parallel refreshes.
    let refreshPromise: Promise<string> | null = null;
    apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;

          const refreshToken = localStorage.getItem('refreshToken');
          if (!refreshToken) {
            // No session to refresh — go to login rather than leaving the tab stuck
            // on stale data (e.g. after a logout in another tab).
            localStorage.removeItem('accessToken');
            if (!window.location.pathname.startsWith('/login')) {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          try {
            refreshPromise ??= axios
              .post(`${API_BASE_URL}/auth/refresh`, { refreshToken })
              .then((r) => {
                localStorage.setItem('accessToken', r.data.accessToken);
                return r.data.accessToken as string;
              })
              .finally(() => { refreshPromise = null; });

            const accessToken = await refreshPromise;
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            return apiClient!.request(originalRequest);
          } catch {
            // Refresh failed, redirect to login
            localStorage.removeItem('accessToken');
            localStorage.removeItem('refreshToken');
            window.location.href = '/login';
          }
        }

        return Promise.reject(error);
      }
    );
  }

  return apiClient;
}

// API methods organized by namespace (matching useElectronAPI pattern)
export type ApiKeyScopeAction = 'none' | 'read' | 'read_write';

export interface WebhookSummaryDto {
  id: string;
  url: string;
  secretPrefix: string;
  events: string[];
  active: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WebhookDeliveryDto {
  id: string;
  eventType: string;
  status: string;
  attemptCount: number;
  lastStatusCode: number | null;
  lastError: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApiActivityRowDto {
  id: string;
  apiKeyId: string;
  keyName: string | null;
  method: string;
  path: string;
  status: number;
  ip: string | null;
  responseTimeMs: number | null;
  createdAt: string;
}

export interface ApiKeySummaryDto {
  id: string;
  tenantId: string;
  tenantSlug: string | null;
  name: string;
  keyPrefix: string;
  scope: string;
  isAdmin: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Per-domain scope map; {} ⇒ full access (no restriction). */
  scopes?: Record<string, ApiKeyScopeAction>;
}

export const api = {
  // Self-service public-API keys (/api/v1/keys — JWT-only).
  apiKeys: {
    list: async (): Promise<{ items: ApiKeySummaryDto[] }> => {
      const { data } = await getApiClient().get('/v1/keys');
      return data;
    },
    create: async (payload: { name: string; scope: 'read_only' | 'read_write' }): Promise<{ key: string; summary: ApiKeySummaryDto }> => {
      const { data } = await getApiClient().post('/v1/keys', payload);
      return data;
    },
    revoke: async (id: string): Promise<{ revoked: boolean; id: string }> => {
      const { data } = await getApiClient().delete(`/v1/keys/${id}`);
      return data;
    },
    setScopes: async (id: string, scopes: Record<string, ApiKeyScopeAction>): Promise<{ id: string; scopes: Record<string, ApiKeyScopeAction> }> => {
      const { data } = await getApiClient().patch(`/v1/keys/${id}/scopes`, { scopes });
      return data;
    },
    activity: async (): Promise<{ items: ApiActivityRowDto[] }> => {
      const { data } = await getApiClient().get('/v1/keys/activity');
      return data;
    },
  },

  // Outbound webhooks (/api/v1/webhooks — JWT-only).
  webhooks: {
    list: async (): Promise<{ items: WebhookSummaryDto[] }> => {
      const { data } = await getApiClient().get('/v1/webhooks');
      return data;
    },
    create: async (payload: { url: string; events: string[]; description?: string }): Promise<{ summary: WebhookSummaryDto; secret: string }> => {
      const { data } = await getApiClient().post('/v1/webhooks', payload);
      return data;
    },
    update: async (id: string, payload: { url?: string; events?: string[]; description?: string; active?: boolean }): Promise<WebhookSummaryDto> => {
      const { data } = await getApiClient().patch(`/v1/webhooks/${id}`, payload);
      return data;
    },
    remove: async (id: string): Promise<{ removed: boolean; id: string }> => {
      const { data } = await getApiClient().delete(`/v1/webhooks/${id}`);
      return data;
    },
    deliveries: async (id: string): Promise<{ items: WebhookDeliveryDto[] }> => {
      const { data } = await getApiClient().get(`/v1/webhooks/${id}/deliveries`);
      return data;
    },
    test: async (id: string): Promise<{ ok: boolean; statusCode: number | null; error: string | null }> => {
      const { data } = await getApiClient().post(`/v1/webhooks/${id}/test`);
      return data;
    },
  },

  auth: {
    login: async (email: string, password: string, tenantId?: string): Promise<LoginResponse> => {
      const { data } = await getApiClient().post('/auth/login', { email, password, tenantId });
      return data;
    },

    logout: async (refreshToken: string): Promise<void> => {
      await getApiClient().post('/auth/logout', { refreshToken });
    },

    refresh: async (refreshToken: string): Promise<{ accessToken: string }> => {
      const { data } = await getApiClient().post('/auth/refresh', { refreshToken });
      return data;
    },

    me: async () => {
      const { data } = await getApiClient().get('/auth/me');
      return data;
    },

    switchTenant: async (tenantId: string) => {
      const { data } = await getApiClient().post('/auth/switch-tenant', { tenantId });
      return data;
    },

    changePassword: async (currentPassword: string, newPassword: string) => {
      const { data } = await getApiClient().put('/auth/change-password', { currentPassword, newPassword });
      return data;
    },

    updateProfile: async (name: string): Promise<{ id: string; email: string; name: string }> => {
      const { data } = await getApiClient().patch('/auth/profile', { name });
      return data;
    },

    // Social sign-in (Google/Microsoft). `oauthProviders` returns only the
    // providers with credentials configured, so the UI stays empty otherwise.
    oauthProviders: async (): Promise<{ providers: Array<{ key: string; label: string }> }> => {
      const { data } = await getApiClient().get('/auth/oauth/providers');
      return data;
    },

    // Optional inviteToken → invitation-accept flow (may create the account
    // and joins the inviting workspace).
    oauthStart: async (provider: string, opts?: { inviteToken?: string }): Promise<{ url: string }> => {
      const q = opts?.inviteToken ? `?invite=${encodeURIComponent(opts.inviteToken)}` : '';
      const { data } = await getApiClient().get(`/auth/oauth/${provider}/start${q}`);
      return data;
    },

    oauthComplete: async (code: string): Promise<LoginResponse> => {
      const { data } = await getApiClient().post('/auth/oauth/complete', { code });
      return data;
    },

    oauthLinked: async (): Promise<{ providers: Array<{ key: string; label: string }> }> => {
      const { data } = await getApiClient().get('/auth/oauth/identities');
      return data;
    },
  },

  abn: {
    /**
     * Look up an ABN using the Australian Business Register
     * Returns business name, entity type, GST status, etc.
     */
    lookup: async (abn: string): Promise<{
      abn: string;
      abnStatus: string;
      abnStatusEffectiveFrom: string | null;
      entityName: string;
      entityTypeCode: string;
      entityTypeDescription: string;
      gstRegistered: boolean;
      gstRegisteredFrom: string | null;
      state: string | null;
      postcode: string | null;
      businessNames: string[];
      message: string | null;
    } | {
      error: true;
      message: string;
      code: string;
    }> => {
      const { data } = await getApiClient().get(`/abn/${encodeURIComponent(abn)}`);
      return data;
    },

    /**
     * Quick validation of ABN checksum (doesn't call ABR API)
     */
    validate: async (abn: string): Promise<{
      valid: boolean;
      abn: string;
      formatted: string;
      message: string;
    }> => {
      const { data } = await getApiClient().get(`/abn/validate/${encodeURIComponent(abn)}`);
      return data;
    },

    /**
     * Search for businesses by name using the Australian Business Register
     * Returns a list of matching businesses with their ABNs
     */
    searchByName: async (
      name: string,
      options?: { state?: string; postcode?: string; maxResults?: number }
    ): Promise<{
      query: string;
      results: Array<{
        abn: string;
        abnStatus: string;
        entityName: string;
        entityType: string;
        state: string | null;
        postcode: string | null;
        score: number;
      }>;
      totalResults: number;
    } | {
      error: true;
      message: string;
      code: string;
    }> => {
      const params = new URLSearchParams({ name });
      if (options?.state) params.append('state', options.state);
      if (options?.postcode) params.append('postcode', options.postcode);
      if (options?.maxResults) params.append('maxResults', options.maxResults.toString());

      const { data } = await getApiClient().get(`/abn/search?${params}`);
      return data;
    },
  },

  allowances: {
    listForJob: async (jobId: string) => {
      const { data } = await getApiClient().get(`/allowances/jobs/${jobId}`);
      return data.data;
    },
    getJobDefaultMargin: async (jobId: string): Promise<number> => {
      const { data } = await getApiClient().get(`/allowances/jobs/${jobId}/default-margin`);
      return data.data.marginPercent;
    },
    setJobDefaultMargin: async (jobId: string, marginPercent: number) => {
      const { data } = await getApiClient().patch(`/allowances/jobs/${jobId}/default-margin`, { marginPercent });
      return data.data;
    },
    /** Job-level free-form Allowances notes (rendered at top of Allowances PDF). */
    getJobNotes: async (jobId: string): Promise<string> => {
      const { data } = await getApiClient().get(`/allowances/jobs/${jobId}/notes`);
      return (data.data?.notes as string) ?? '';
    },
    setJobNotes: async (jobId: string, notes: string): Promise<void> => {
      await getApiClient().patch(`/allowances/jobs/${jobId}/notes`, { notes });
    },
    get: async (billItemId: string) => {
      const { data } = await getApiClient().get(`/allowances/bill-items/${billItemId}`);
      return data.data;
    },
    mark: async (billItemId: string, payload: { type: 'PS' | 'PC'; notes?: string }) => {
      const { data } = await getApiClient().post(`/allowances/bill-items/${billItemId}/mark`, payload);
      return data.data;
    },
    update: async (billItemId: string, updates: { type?: 'PS' | 'PC'; notes?: string; marginPercent?: number | null }) => {
      const { data } = await getApiClient().patch(`/allowances/bill-items/${billItemId}`, updates);
      return data.data;
    },
    unmark: async (billItemId: string) => {
      await getApiClient().post(`/allowances/bill-items/${billItemId}/unmark`);
    },
    listReconciliations: async (billItemId: string) => {
      const { data } = await getApiClient().get(`/allowances/bill-items/${billItemId}/reconciliations`);
      return data.data;
    },
    recordReconciliation: async (billItemId: string, payload: {
      actualCostExGst: number;
      sourceType?: 'manual' | 'order' | 'invoice' | 'variation';
      sourceId?: string;
      notes?: string;
      recordedAt?: string;
    }) => {
      const { data } = await getApiClient().post(`/allowances/bill-items/${billItemId}/reconciliations`, payload);
      return data.data;
    },
    updateReconciliation: async (id: string, updates: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/allowances/reconciliations/${id}`, updates);
      return data.data;
    },
    deleteReconciliation: async (id: string) => {
      await getApiClient().delete(`/allowances/reconciliations/${id}`);
    },
    convert: async (billItemId: string, payload: {
      finalAmountExGst: number;
      createVariation?: boolean;
      variationDescription?: string;
    }) => {
      const { data } = await getApiClient().post(`/allowances/bill-items/${billItemId}/convert`, payload);
      return data.data;
    },
    createAdhoc: async (jobId: string, payload: {
      type: 'PS' | 'PC';
      description: string;
      amountExGst: number;
      costCentreId?: string;
      notes?: string;
      marginPercent?: number;
    }) => {
      const { data } = await getApiClient().post(`/allowances/jobs/${jobId}/adhoc`, payload);
      return data.data;
    },
    allocate: async (childBillItemId: string, allowanceBillItemId: string | null) => {
      await getApiClient().post(`/allowances/bill-items/${childBillItemId}/allocate`, { allowanceBillItemId });
    },
    listAllocations: async (allowanceBillItemId: string) => {
      const { data } = await getApiClient().get(`/allowances/bill-items/${allowanceBillItemId}/allocations`);
      return data.data;
    },
    /** Phase 5: timesheet labour entries allocated to this allowance. */
    listLabourAllocations: async (allowanceBillItemId: string) => {
      const { data } = await getApiClient().get(`/allowances/bill-items/${allowanceBillItemId}/labour`);
      return data.data;
    },
    /** Customer-facing notes on a bill item (rendered on Allowances + Variation PDFs).
     *  Stored at bill_items.metadata.clientNotes — kept separate from workup. */
    setClientNotes: async (billItemId: string, notes: string): Promise<void> => {
      await getApiClient().patch(`/allowances/bill-items/${billItemId}/client-notes`, { notes });
    },
  },

  selections: {
    listForJob: async (jobId: string) => {
      const { data } = await getApiClient().get(`/selections/jobs/${jobId}`);
      return data.data;
    },
    enable: async (billItemId: string, payload: { required?: boolean; deadline?: string | null; pricingMode?: 'flat' | 'qty_rate' }) => {
      const { data } = await getApiClient().post(`/selections/bill-items/${billItemId}/enable`, payload);
      return data.data;
    },
    disable: async (billItemId: string) => {
      await getApiClient().post(`/selections/bill-items/${billItemId}/disable`);
    },
    listOptions: async (billItemId: string) => {
      const { data } = await getApiClient().get(`/selections/bill-items/${billItemId}/options`);
      return data.data;
    },
    addOption: async (billItemId: string, payload: {
      name: string; description?: string; priceExGst: number; unitRate?: number;
      isStandardInclusion?: boolean; addOns?: Array<{ label: string; rate?: number; flat?: number }>;
      imageUrl?: string; isDefault?: boolean; sortOrder?: number;
    }) => {
      const { data } = await getApiClient().post(`/selections/bill-items/${billItemId}/options`, payload);
      return data.data;
    },
    updateOption: async (optionId: string, payload: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/selections/options/${optionId}`, payload);
      return data.data;
    },
    deleteOption: async (optionId: string) => {
      await getApiClient().delete(`/selections/options/${optionId}`);
    },
    // Staff set/clear the client's pick on their behalf (optionId null = clear).
    setChoice: async (billItemId: string, optionId: string | null) => {
      const { data } = await getApiClient().patch(`/selections/bill-items/${billItemId}/choice`, { optionId });
      return data.data;
    },
    // Assign a selection to a section (sectionId null = ungrouped).
    setSection: async (billItemId: string, sectionId: string | null, sortOrder?: number) => {
      await getApiClient().patch(`/selections/bill-items/${billItemId}/section`, { sectionId, sortOrder });
    },
    // Job spec document: section headings + standard-inclusion rows.
    getSpec: async (jobId: string): Promise<{ sections: SpecSection[] }> => {
      const { data } = await getApiClient().get(`/selections/jobs/${jobId}/spec`);
      return data.data;
    },
    saveSpec: async (jobId: string, sections: SpecSection[]): Promise<{ sections: SpecSection[] }> => {
      const { data } = await getApiClient().put(`/selections/jobs/${jobId}/spec`, { sections });
      return data.data;
    },
    duplicateJobSection: async (jobId: string, sectionId: string): Promise<SpecSection> => {
      const { data } = await getApiClient().post(`/selections/jobs/${jobId}/sections/${sectionId}/duplicate`);
      return data.data;
    },
    // Reusable template library.
    listTemplates: async (): Promise<SelectionTemplateSummary[]> => {
      const { data } = await getApiClient().get('/selections/templates');
      return data.data;
    },
    getTemplate: async (id: string): Promise<SelectionTemplate> => {
      const { data } = await getApiClient().get(`/selections/templates/${id}`);
      return data.data;
    },
    createTemplate: async (body: SelectionTemplateBody): Promise<{ id: string }> => {
      const { data } = await getApiClient().post('/selections/templates', body);
      return data.data;
    },
    updateTemplate: async (id: string, body: SelectionTemplateBody): Promise<{ id: string }> => {
      const { data } = await getApiClient().put(`/selections/templates/${id}`, body);
      return data.data;
    },
    duplicateTemplate: async (id: string, name?: string): Promise<{ id: string }> => {
      const { data } = await getApiClient().post(`/selections/templates/${id}/duplicate`, { name });
      return data.data;
    },
    deleteTemplate: async (id: string): Promise<void> => {
      await getApiClient().delete(`/selections/templates/${id}`);
    },
    applyTemplate: async (templateId: string, jobId: string): Promise<{ sectionsAdded: number; selectionsAdded: number }> => {
      const { data } = await getApiClient().post(`/selections/templates/${templateId}/apply`, { jobId });
      return data.data;
    },
  },

  boq: {
    /** Deterministic estimate sanity-check for a job's BOQ. */
    reviewEstimate: async (jobNo: string): Promise<EstimateReviewResult> => {
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/review`);
      return data.data;
    },

    getJobBill: async (
      jobNo: string,
      costCentre?: string,
      bLoad?: number,
      priceLevel?: number
    ): Promise<BillItem[]> => {
      const params = new URLSearchParams();
      if (costCentre) params.append('costCentre', costCentre);
      if (bLoad) params.append('bLoad', bLoad.toString());
      if (priceLevel) params.append('priceLevel', priceLevel.toString());

      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/bill?${params}`);
      return data;
    },

    /** "Copy BOQ from another job" preview: the source bill grouped by cost
     *  centre, plus how many lines this job already has per cost centre. */
    getCopyPreview: async (jobNo: string, sourceJobNo: string): Promise<{
      sourceJobNo: string;
      targetJobNo: string;
      costCentres: Array<{
        code: string;
        name: string;
        total: number;
        targetExistingCount: number;
        items: Array<{
          id: string;
          bLoad: number;
          lineNumber: number;
          priceCode: string | null;
          description: string | null;
          quantity: number;
          unitCode: string | null;
          unitPrice: number;
          lineTotal: number;
          isHeading: boolean;
        }>;
        /** Read-only: what the destination job already holds in this cost centre. */
        targetItems: Array<{
          id: string;
          bLoad: number;
          lineNumber: number;
          priceCode: string | null;
          description: string | null;
          quantity: number;
          unitCode: string | null;
          unitPrice: number;
          lineTotal: number;
          isHeading: boolean;
        }>;
      }>;
    }> => {
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/copy-preview`, {
        params: { source: sourceJobNo },
      });
      return data;
    },

    /** Append selected lines from another job's bill into this job. */
    copyFromJob: async (
      jobNo: string,
      payload: { sourceJobNo: string; itemIds: string[]; repriceToCurrent: boolean; zeroQuantities: boolean; copySuppliers: boolean; copyNotes: boolean }
    ): Promise<{
      copied: number;
      costCentres: number;
      loads: number;
      skippedOrdered: number;
      skippedOrderedLoads: string[];
    }> => {
      const { data } = await getApiClient().post(`/boq/jobs/${jobNo}/copy-from`, payload);
      return data;
    },

    getCostCentresWithBudgets: async (jobNo: string): Promise<CostCentreWithBudget[]> => {
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/cost-centres`);
      return data;
    },

    getCostCentreTotalsByLoad: async (jobNo: string): Promise<Array<{
      id: string;
      code: string;
      name: string;
      sortOrder: number;
      tier: number;
      parentCode: string | null;
      total: number;
      itemCount: number;
      loads: Array<{
        load: number;
        supplierName: string | null;
        supplierId: string | null;
        isOrdered: boolean;
        itemCount: number;
        total: number;
      }>;
    }>> => {
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/cost-centres-by-load`);
      return data;
    },

    getLoads: async (jobNo: string, costCentre?: string) => {
      const params = costCentre ? `?costCentre=${costCentre}` : '';
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/loads${params}`);
      return data;
    },

    getAvailableLoads: async (jobNo: string, costCentre: string): Promise<{
      orderedLoads: number[];
      usedLoads: number[];
      nextAvailableLoad: number;
    }> => {
      const { data } = await getApiClient().get(`/boq/jobs/${jobNo}/available-loads?costCentre=${costCentre}`);
      return data;
    },

    addItem: async (item: {
      jobNo: string;
      priceCode: string;
      costCentre?: string | null;
      quantity?: number;
      unitPrice?: number;
      bLoad?: number;
      supplierId?: string | null;
    }): Promise<BillItem> => {
      const { data } = await getApiClient().post('/boq/items', item);
      return data;
    },

    updateItem: async (id: string, updates: Partial<BillItem>): Promise<BillItem> => {
      const { data } = await getApiClient().patch(`/boq/items/${id}`, updates);
      return data;
    },

    deleteItem: async (id: string): Promise<void> => {
      await getApiClient().delete(`/boq/items/${id}`);
    },

    changeItemCode: async (id: string, priceCode: string, keepPrice = false): Promise<BillItem> => {
      const { data } = await getApiClient().patch(`/boq/items/${id}/change-code`, { priceCode, keepPrice });
      return data;
    },

    // Override a line's unit without re-coding it (unitId null clears the override → catalogue unit)
    setItemUnit: async (id: string, unitId: string | null): Promise<{ id: string; unitId: string | null }> => {
      const { data } = await getApiClient().patch(`/boq/items/${id}/unit`, { unitId });
      return data;
    },

    // Admin override: unlock/re-lock ordered lines (locked because their load is on a PO)
    unlockItem: async (id: string): Promise<{ id: string; unlocked: boolean }> => {
      const { data } = await getApiClient().post(`/boq/items/${id}/unlock`);
      return data;
    },
    relockItem: async (id: string): Promise<{ id: string; unlocked: boolean }> => {
      const { data } = await getApiClient().post(`/boq/items/${id}/relock`);
      return data;
    },
    bulkUnlock: async (ids: string[]): Promise<{ count: number; unlocked: boolean }> => {
      const { data } = await getApiClient().post('/boq/items/bulk-unlock', { ids });
      return data;
    },
    bulkRelock: async (ids: string[]): Promise<{ count: number; unlocked: boolean }> => {
      const { data } = await getApiClient().post('/boq/items/bulk-relock', { ids });
      return data;
    },

    duplicateItem: async (id: string): Promise<BillItem> => {
      const { data } = await getApiClient().post(`/boq/items/${id}/duplicate`);
      return data;
    },

    /**
     * Move items to a new cost centre with proper load handling.
     * If an item's current bLoad is already ordered in the target cost centre,
     * it will be automatically assigned to the next available load.
     */
    reorderItems: async (jobNo: string, items: { id: string; lineNumber: number }[]): Promise<void> => {
      await getApiClient().post('/boq/items/reorder', { jobNo, items });
    },

    moveItems: async (
      itemIds: string[],
      targetCostCentreCode: string,
      jobNo: string
    ): Promise<{
      movedCount: number;
      newLoadAssignments: Array<{ itemId: string; oldLoad: number; newLoad: number }>;
    }> => {
      const { data } = await getApiClient().post('/boq/items/move', {
        itemIds,
        targetCostCentreCode,
        jobNo,
      });
      return data;
    },

    addAdhocItem: async (item: {
      jobNo: string;
      costCentre: string;
      description: string;
      quantity: number;
      unitCode: string;
      unitPrice: number;
      bLoad?: number;
      workup?: string;
    }): Promise<BillItem> => {
      const { data } = await getApiClient().post('/boq/adhoc-items', item);
      return data;
    },

    // Add a Heading / Sub-heading line to a cost centre (text editable inline after)
    addHeading: async (item: {
      jobNo: string;
      costCentre: string;
      bLoad?: number;
      subHeading?: boolean;
      text?: string;
    }): Promise<BillItem> => {
      const { data } = await getApiClient().post('/boq/headings', item);
      return data;
    },

    repriceBill: async (jobNo: string, options: Record<string, unknown>) => {
      const { data } = await getApiClient().post(`/boq/jobs/${jobNo}/reprice`, options);
      return data;
    },

    repriceItems: async (itemIds: string[], priceLevel: number = 1): Promise<{
      updated: number;
      results: Array<{ id: string; newPrice: number | null; source: 'supplier' | 'catalogue' | 'unchanged' }>;
    }> => {
      const { data } = await getApiClient().post('/boq/items/reprice', { itemIds, priceLevel });
      return data;
    },

    explodeRecipe: async (
      priceCode: string,
      quantity: number,
      variableValues: Record<string, number>,
      options: Record<string, unknown>,
      billItemId?: string
    ): Promise<ExplodedItem[]> => {
      const { data } = await getApiClient().post(`/boq/recipes/${priceCode}/explode`, {
        quantity,
        variableValues,
        options,
        billItemId,
      });
      return data;
    },

    getRecipeVariables: async (priceCode: string): Promise<string[]> => {
      const { data } = await getApiClient().get(`/boq/recipes/${priceCode}/variables`);
      return data;
    },

    // ── Assembly items on a BOQ line (editable copy; "Assembly" = catalogue recipe) ──
    getAssemblyItems: async (id: string, priceLevel: number = 1): Promise<AssemblyItemsResult> => {
      const { data } = await getApiClient().get(`/boq/items/${id}/assembly-items`, { params: { priceLevel } });
      return data;
    },

    saveAssemblyItems: async (
      id: string,
      payload: { name?: string | null; items: AssemblyLineItem[] }
    ): Promise<BillItem & { unitPrice: number }> => {
      const { data } = await getApiClient().put(`/boq/items/${id}/assembly-items`, payload);
      return data;
    },

    saveLineAsAssembly: async (
      id: string,
      payload: { name: string }
    ): Promise<{ priceCode: string; repointed: boolean; unitPrice: number }> => {
      const { data } = await getApiClient().post(`/boq/items/${id}/save-as-assembly`, payload);
      return data;
    },

    updateSourceAssembly: async (
      id: string,
      payload: { name?: string | null }
    ): Promise<{ priceCode: string; unitPrice: number; repriced: boolean }> => {
      const { data } = await getApiClient().post(`/boq/items/${id}/update-assembly`, payload);
      return data;
    },

    createLoad: async (jobNo: string, costCentre: string, supplierId?: string, copyFrom?: number) => {
      const { data } = await getApiClient().post(`/boq/jobs/${jobNo}/create-load`, {
        costCentre,
        supplierId,
        copyFrom,
      });
      return data;
    },

    exportToExcel: async (jobNo: string, costCentre?: string, priceLevel?: number): Promise<Blob> => {
      const params = new URLSearchParams();
      if (costCentre) params.append('costCentre', costCentre);
      if (priceLevel) params.append('priceLevel', priceLevel.toString());

      const response = await getApiClient().get(`/boq/jobs/${jobNo}/export`, {
        params,
        responseType: 'blob',
      });
      return response.data;
    },

    /** Download the client-facing Estimate PDF.
     *  When `allowancesOnly` is true, generates a Provisional Sums & Prime Cost
     *  schedule (no firm BOQ items) — used by the Allowances tab. */
    downloadEstimatePDF: async (jobNo: string, allowancesOnly = false): Promise<Blob> => {
      const params: Record<string, string> = {};
      if (allowancesOnly) params.allowancesOnly = '1';
      const response = await getApiClient().get(`/boq/jobs/${jobNo}/estimate-pdf`, {
        responseType: 'blob',
        params,
      });
      return response.data;
    },

    /** Fetch the Estimate PDF as HTML for in-app preview (faster than PDF). */
    getEstimatePreviewHTML: async (jobNo: string, allowancesOnly = false): Promise<string> => {
      const params: Record<string, string> = {};
      if (allowancesOnly) params.allowancesOnly = '1';
      const response = await getApiClient().get(`/boq/jobs/${jobNo}/estimate-preview`, {
        responseType: 'text',
        params,
      });
      return response.data as string;
    },

    // BOQ Import
    getImportFields: async (): Promise<Array<{ field: string; label: string; required: boolean }>> => {
      const { data } = await getApiClient().get('/boq/import/fields');
      return data;
    },

    parseImportFile: async (file: File): Promise<{
      headers: string[];
      sampleRows: unknown[][];
      totalRows: number;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getApiClient().post('/boq/import/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    linkImportItems: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      jobNo: string,
      hasHeaderRow: boolean = true
    ): Promise<{
      rows: Array<{ rowNumber: number; data: Record<string, unknown> }>;
      links: Array<{
        rowNumber: number;
        sourceValue: string;
        matchedPriceCode: string | null;
        matchedDescription: string | null;
        matchConfidence: 'exact' | 'partial' | 'none';
      }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('jobNo', jobNo);
      formData.append('hasHeaderRow', String(hasHeaderRow));

      const { data } = await getApiClient().post('/boq/import/link', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    previewImport: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      jobNo: string,
      hasHeaderRow: boolean = true
    ): Promise<{
      totalRows: number;
      matchedRows: number;
      unmatchedRows: number;
      items: unknown[];
      costCentreErrors: Array<{ row: number; costCentre: string; error: string }>;
      errors: Array<{ row: number; error: string }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('jobNo', jobNo);
      formData.append('hasHeaderRow', String(hasHeaderRow));

      const { data } = await getApiClient().post('/boq/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    executeImport: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      jobNo: string,
      hasHeaderRow: boolean,
      options: {
        defaultBLoad?: number;
        createAdhocForUnmatched?: boolean;
      }
    ): Promise<{
      success: boolean;
      totalRows: number;
      imported: number;
      adhocCreated: number;
      skipped: number;
      errors: Array<{ row: number; field: string; error: string }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('jobNo', jobNo);
      formData.append('hasHeaderRow', String(hasHeaderRow));
      if (options.defaultBLoad != null) formData.append('defaultBLoad', String(options.defaultBLoad));
      if (options.createAdhocForUnmatched) formData.append('createAdhocForUnmatched', 'true');

      const { data } = await getApiClient().post('/boq/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },
  },

  catalogue: {
    getItems: async (params: Record<string, unknown>): Promise<CatalogueItem[]> => {
      const { data } = await getApiClient().get('/catalogue/items', { params });
      return data;
    },

    getItemsPaginated: async (params: Record<string, unknown>): Promise<{
      items: CatalogueItem[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }> => {
      const { data } = await getApiClient().get('/catalogue/items', { params });
      return data;
    },

    getItem: async (priceCode: string, priceLevel?: number): Promise<CatalogueItem> => {
      const params = priceLevel ? `?priceLevel=${priceLevel}` : '';
      const { data } = await getApiClient().get(`/catalogue/items/${priceCode}${params}`);
      return data;
    },

    createItem: async (item: Partial<CatalogueItem>): Promise<CatalogueItem> => {
      const { data } = await getApiClient().post('/catalogue/items', item);
      return data;
    },

    updateItem: async (priceCode: string, updates: Partial<CatalogueItem>): Promise<CatalogueItem> => {
      const { data } = await getApiClient().patch(`/catalogue/items/${priceCode}`, updates);
      return data;
    },

    deleteItem: async (priceCode: string): Promise<void> => {
      await getApiClient().delete(`/catalogue/items/${priceCode}`);
    },

    getRecipe: async (priceCode: string): Promise<Array<{
      priceCode: string;
      description: string;
      quantity: number;
      formula: string | null;
      notes: string | null;
      sortOrder: number;
      unitCode: string | null;
      costCentreOverride: string | null;
    }>> => {
      const { data } = await getApiClient().get(`/catalogue/items/${priceCode}/recipe`);
      return data;
    },

    updateRecipe: async (priceCode: string, components: Array<{
      priceCode: string;
      quantity: number;
      formula?: string | null;
      notes?: string | null;
      sortOrder?: number;
      costCentreOverride?: string | null;
    }>) => {
      const { data } = await getApiClient().put(`/catalogue/items/${priceCode}/recipe`, { components });
      return data;
    },

    addRecipeComponent: async (mainPriceCode: string, component: {
      priceCode: string;
      quantity: number;
      formula?: string | null;
      notes?: string | null;
      sortOrder?: number;
      costCentreOverride?: string | null;
    }) => {
      // Get existing components, add new one, and update
      const existing = await api.catalogue.getRecipe(mainPriceCode);
      const maxSortOrder = existing.length > 0 ? Math.max(...existing.map(c => c.sortOrder || 0)) : 0;
      const newComponents = [
        ...existing.map(c => ({
          priceCode: c.priceCode,
          quantity: c.quantity,
          formula: c.formula,
          notes: c.notes,
          sortOrder: c.sortOrder,
          costCentreOverride: c.costCentreOverride,
        })),
        {
          priceCode: component.priceCode,
          quantity: component.quantity,
          formula: component.formula || null,
          notes: component.notes || null,
          sortOrder: component.sortOrder ?? (maxSortOrder + 1),
          costCentreOverride: component.costCentreOverride || null,
        }
      ];
      return api.catalogue.updateRecipe(mainPriceCode, newComponents);
    },

    updateRecipeComponent: async (mainPriceCode: string, componentPriceCode: string, updates: {
      quantity?: number;
      formula?: string | null;
      notes?: string | null;
      costCentreOverride?: string | null;
    }) => {
      const existing = await api.catalogue.getRecipe(mainPriceCode);
      const updatedComponents = existing.map(c => {
        if (c.priceCode === componentPriceCode) {
          return {
            priceCode: c.priceCode,
            quantity: updates.quantity ?? c.quantity,
            formula: updates.formula !== undefined ? updates.formula : c.formula,
            notes: updates.notes !== undefined ? updates.notes : c.notes,
            sortOrder: c.sortOrder,
            costCentreOverride: updates.costCentreOverride !== undefined ? updates.costCentreOverride : c.costCentreOverride,
          };
        }
        return {
          priceCode: c.priceCode,
          quantity: c.quantity,
          formula: c.formula,
          notes: c.notes,
          sortOrder: c.sortOrder,
          costCentreOverride: c.costCentreOverride,
        };
      });
      return api.catalogue.updateRecipe(mainPriceCode, updatedComponents);
    },

    deleteRecipeComponent: async (mainPriceCode: string, componentPriceCode: string) => {
      const existing = await api.catalogue.getRecipe(mainPriceCode);
      const updatedComponents = existing
        .filter(c => c.priceCode !== componentPriceCode)
        .map(c => ({
          priceCode: c.priceCode,
          quantity: c.quantity,
          formula: c.formula,
          notes: c.notes,
          sortOrder: c.sortOrder,
          costCentreOverride: c.costCentreOverride,
        }));
      return api.catalogue.updateRecipe(mainPriceCode, updatedComponents);
    },

    getPriceHistory: async (priceCode: string) => {
      const { data } = await getApiClient().get(`/catalogue/items/${priceCode}/prices`);
      return data;
    },

    addPrice: async (priceCode: string, priceData: { price: number; priceLevel: number; effectiveDate?: string }) => {
      const { data } = await getApiClient().post(`/catalogue/items/${priceCode}/prices`, priceData);
      return data;
    },

    getSupplierPrices: async (priceCode: string): Promise<{
      suppliers: Array<{
        supplierId: string;
        supplierCode: string;
        supplierName: string;
        currentPrice: number | null;
        currentSupplierRef: string | null;
        priceCount: number;
        history: Array<{
          id: string;
          supplierReference: string | null;
          price: number;
          effectiveDate: string | null;
          expiryDate: string | null;
          priceLevel: number;
          area: string | null;
          comments: string | null;
          createdAt: string;
        }>;
      }>;
      totalRecords: number;
    }> => {
      const { data } = await getApiClient().get(`/catalogue/items/${priceCode}/supplier-prices`);
      return data;
    },

    addSupplierPrice: async (priceCode: string, data: {
      supplierCode: string;
      supplierReference?: string;
      price: number;
      effectiveDate?: string;
      expiryDate?: string;
      priceLevel?: number;
      area?: string;
      comments?: string;
    }) => {
      const response = await getApiClient().post(`/catalogue/items/${priceCode}/supplier-prices`, data);
      return response.data;
    },

    updateSupplierPrice: async (priceCode: string, priceId: string, updates: {
      supplierCode: string;
      supplierReference?: string;
      price?: number;
      effectiveDate?: string;
      expiryDate?: string;
      priceLevel?: number;
      area?: string;
      comments?: string;
    }) => {
      const { data } = await getApiClient().patch(`/catalogue/items/${priceCode}/supplier-prices/${priceId}`, updates);
      return data;
    },

    deleteSupplierPrice: async (priceCode: string, priceId: string, supplierCode: string): Promise<void> => {
      await getApiClient().delete(`/catalogue/items/${priceCode}/supplier-prices/${priceId}`, {
        data: { supplierCode },
      });
    },

    getCostCentres: async (): Promise<CostCentre[]> => {
      const { data } = await getApiClient().get('/catalogue/cost-centres');
      return data;
    },

    getUnits: async () => {
      const { data } = await getApiClient().get('/catalogue/units');
      return data;
    },

    // Export
    exportCatalogue: async (options: {
      format?: 'csv' | 'xlsx';
      costCentre?: string;
      includeArchived?: boolean;
      includePrices?: boolean;
    }): Promise<Blob> => {
      const response = await getApiClient().get('/catalogue/export', {
        params: options,
        responseType: 'blob',
      });
      return response.data;
    },

    exportForZztakeoff: async (costCentres?: string[], priceCodes?: string[]) => {
      const params: Record<string, string> = {};
      if (costCentres && costCentres.length > 0) {
        params.costCentres = costCentres.join(',');
      }
      if (priceCodes && priceCodes.length > 0) {
        params.priceCodes = priceCodes.join(',');
      }
      const { data } = await getApiClient().get('/catalogue/export/zztakeoff', { params });
      return data;
    },

    getImportTemplate: async (format: 'csv' | 'xlsx' = 'xlsx'): Promise<Blob> => {
      const response = await getApiClient().get('/catalogue/export/template', {
        params: { format },
        responseType: 'blob',
      });
      return response.data;
    },

    // Import
    getImportFields: async (importType: string) => {
      const { data } = await getApiClient().get('/catalogue/import/fields', { params: { importType } });
      return data;
    },

    parseImportFile: async (file: File): Promise<{
      headers: string[];
      sampleRows: unknown[][];
      totalRows: number;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getApiClient().post('/catalogue/import/parse', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    linkImportItems: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      importType: string,
      supplierCode?: string,
      hasHeaderRow: boolean = true
    ): Promise<{
      rows: Array<{ rowNumber: number; data: Record<string, unknown> }>;
      links: Array<{
        rowNumber: number;
        sourceValue: string;
        matchedPriceCode: string | null;
        matchedDescription: string | null;
        matchConfidence: 'exact' | 'partial' | 'none';
        price?: number;
      }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('importType', importType);
      if (supplierCode) formData.append('supplierCode', supplierCode);
      formData.append('hasHeaderRow', String(hasHeaderRow));

      const { data } = await getApiClient().post('/catalogue/import/link', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    previewImport: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      importType: string,
      supplierCode?: string,
      hasHeaderRow: boolean = true
    ): Promise<{
      totalRows: number;
      matchedRows: number;
      unmatchedRows: number;
      newItems: number;
      updateItems: number;
      items: unknown[];
      errors: Array<{ row: number; error: string }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('importType', importType);
      if (supplierCode) formData.append('supplierCode', supplierCode);
      formData.append('hasHeaderRow', String(hasHeaderRow));

      const { data } = await getApiClient().post('/catalogue/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    executeImport: async (
      file: File,
      mappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>,
      importType: string,
      supplierCode: string | undefined,
      hasHeaderRow: boolean,
      links: unknown[],
      options: {
        updateExisting?: boolean;
        defaultEffectiveDate?: string;
        defaultPriceLevel?: number;
      }
    ): Promise<{
      success: boolean;
      imported: number;
      updated: number;
      skipped: number;
      errors: Array<{ row: number; field: string; error: string }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('mappings', JSON.stringify(mappings));
      formData.append('importType', importType);
      if (supplierCode) formData.append('supplierCode', supplierCode);
      formData.append('hasHeaderRow', String(hasHeaderRow));
      formData.append('links', JSON.stringify(links));
      if (options.updateExisting) formData.append('updateExisting', 'true');
      if (options.defaultEffectiveDate) formData.append('defaultEffectiveDate', options.defaultEffectiveDate);
      if (options.defaultPriceLevel) formData.append('defaultPriceLevel', String(options.defaultPriceLevel));

      const { data } = await getApiClient().post('/catalogue/import/execute', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    // Import Templates
    getImportTemplates: async (importType?: string) => {
      const { data } = await getApiClient().get('/catalogue/import-templates', { params: { importType } });
      return data;
    },

    getSavedMappingTemplate: async (id: string) => {
      const { data } = await getApiClient().get(`/catalogue/import-templates/${id}`);
      return data;
    },

    saveImportTemplate: async (template: {
      name: string;
      importType: string;
      supplierCode?: string;
      columnMappings: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>;
      hasHeaderRow?: boolean;
      dateFormat?: string;
    }) => {
      const { data } = await getApiClient().post('/catalogue/import-templates', template);
      return data;
    },

    updateImportTemplate: async (id: string, updates: {
      name?: string;
      columnMappings?: Array<{ sourceColumn: string; targetField: string; isRequired: boolean }>;
      hasHeaderRow?: boolean;
      dateFormat?: string;
    }) => {
      const { data } = await getApiClient().patch(`/catalogue/import-templates/${id}`, updates);
      return data;
    },

    deleteImportTemplate: async (id: string): Promise<void> => {
      await getApiClient().delete(`/catalogue/import-templates/${id}`);
    },

    // Bulk Operations
    getBulkPriceItems: async (criteria: {
      priceType: 'estimate' | 'supplier';
      costCentre?: string;
      costCentres?: string[]; // Support multiple cost centres
      searchTerm?: string;
      supplierCode?: string;
      priceLevels?: number[];
      excludeZeroPrices?: boolean;
    }) => {
      const { data } = await getApiClient().post('/catalogue/bulk/price-items', criteria);
      return data;
    },

    applyBulkPriceChanges: async (request: {
      priceType: 'estimate' | 'supplier';
      changes: Array<{
        priceCode: string;
        level: number;
        currentPrice: number;
        newPrice: number;
        change: number;
        changePercent: number;
        supplierCode?: string;
        priceId?: string;
      }>;
      validFrom?: string;
      estimator?: string;
      notes?: string;
    }) => {
      const { data } = await getApiClient().post('/catalogue/bulk/price-changes', request);
      return data;
    },

    getManagementItems: async (options: {
      costCentres?: string[];
      searchTerm?: string;
      showArchived?: boolean;
      recipesOnly?: boolean;
      dateFrom?: string;
      dateTo?: string;
    }) => {
      const { data } = await getApiClient().post('/catalogue/bulk/management-items', options);
      return data;
    },

    bulkChangeCostCentre: async (priceCodes: string[], costCentreCode: string): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/catalogue/bulk/change-cost-centre', { priceCodes, costCentreCode });
      return data.data;
    },
    bulkDelete: async (priceCodes: string[]) => {
      const { data } = await getApiClient().post('/catalogue/bulk/delete', { priceCodes });
      return data;
    },

    bulkArchive: async (priceCodes: string[], archive: boolean = true) => {
      const { data } = await getApiClient().post('/catalogue/bulk/archive', { priceCodes, archive });
      return data;
    },

    bulkUpdateCostCentre: async (priceCodes: string[], costCentreCode: string) => {
      const { data } = await getApiClient().post('/catalogue/bulk/cost-centre', { priceCodes, costCentreCode });
      return data;
    },

    bulkUpdateUnit: async (priceCodes: string[], unitCode: string) => {
      const { data } = await getApiClient().post('/catalogue/bulk/unit', { priceCodes, unitCode });
      return data;
    },

    bulkFindReplace: async (priceCodes: string[], findText: string, replaceText: string) => {
      const { data } = await getApiClient().post('/catalogue/bulk/find-replace', { priceCodes, findText, replaceText });
      return data;
    },

    bulkAppendDescription: async (priceCodes: string[], appendText: string) => {
      const { data } = await getApiClient().post('/catalogue/bulk/append-description', { priceCodes, appendText });
      return data;
    },

    bulkSetTypes: async (priceCodes: string[], costType: string | null, takeoffType: string | null) => {
      const { data } = await getApiClient().post('/catalogue/bulk/set-types', { priceCodes, costType, takeoffType });
      return data;
    },

    bulkReprice: async (priceCodes: string[], priceLevel: number, changeType: 'percent' | 'fixed' | 'set', changeValue: number) => {
      const { data } = await getApiClient().post('/catalogue/bulk/reprice', { priceCodes, priceLevel, changeType, changeValue });
      return data;
    },

    bulkCopyPriceLevel: async (priceCodes: string[], sourceLevel: number, targetLevel: number) => {
      const { data } = await getApiClient().post('/catalogue/bulk/copy-level', { priceCodes, sourceLevel, targetLevel });
      return data;
    },

    // Auto-classify bulk actions
    getAutoClassifyPreview: async () => {
      const { data } = await getApiClient().get('/catalogue/bulk/auto-classify/preview');
      return data.data;
    },

    bulkAutoTakeoffType: async (priceCodes: string[] | null, skipExisting: boolean) => {
      const { data } = await getApiClient().post('/catalogue/bulk/auto-takeoff-type', { priceCodes, skipExisting });
      return data;
    },

    bulkAutoCostType: async (request: {
      priceCodes: string[] | null;
      groupMapping: Record<string, string>;
      skipExisting: boolean;
      fallbackFromRoutine: boolean;
      saveMapping: boolean;
    }) => {
      const { data } = await getApiClient().post('/catalogue/bulk/auto-cost-type', request);
      return data;
    },
  },

  jobs: {
    getList: async (options: { showArchived?: boolean; includeArchived?: boolean; showAllStatuses?: boolean; limit?: number; search?: string } | boolean = false): Promise<Job[]> => {
      // Support both legacy boolean and new options object
      const params: Record<string, unknown> = {};
      if (typeof options === 'boolean') {
        params.showArchived = options;
      } else {
        if (options.showArchived !== undefined) params.showArchived = options.showArchived;
        if (options.includeArchived !== undefined) params.showArchived = options.includeArchived; // map includeArchived to showArchived
        if (options.showAllStatuses) params.showAllStatuses = true; // include internal/hidden-status jobs
        if (options.limit !== undefined) params.limit = options.limit;
        if (options.search !== undefined) params.search = options.search;
      }
      const { data } = await getApiClient().get('/jobs', { params });
      return data;
    },

    getJob: async (jobNo: string): Promise<Job> => {
      const { data } = await getApiClient().get(`/jobs/${jobNo}`);
      return data;
    },

    createJob: async (jobData: Partial<Job>): Promise<Job> => {
      const { data } = await getApiClient().post('/jobs', jobData);
      return data;
    },

    updateJob: async (jobNo: string, updates: Partial<Job>): Promise<Job> => {
      const { data } = await getApiClient().patch(`/jobs/${jobNo}`, updates);
      return data;
    },

    deleteJob: async (jobNo: string): Promise<void> => {
      await getApiClient().delete(`/jobs/${jobNo}`);
    },

    // Client portal access (staff-managed).
    portalInvite: async (jobNo: string, payload: { email: string; name?: string; contactId?: string }) => {
      const { data } = await getApiClient().post(`/jobs/${jobNo}/portal/invite`, payload);
      return data;
    },
    portalAccess: async (
      jobNo: string,
    ): Promise<Array<{ portalUserId: string; email: string; name: string | null; status: string; lastLogin: string | null; grantedAt: string }>> => {
      const { data } = await getApiClient().get(`/jobs/${jobNo}/portal/access`);
      return data.data;
    },
    portalRevokeAccess: async (jobNo: string, portalUserId: string): Promise<void> => {
      await getApiClient().delete(`/jobs/${jobNo}/portal/access/${portalUserId}`);
    },
    // Tenant-wide: every portal client + their job grants (powers the View-as picker).
    portalClients: async (): Promise<
      Array<{
        portalUserId: string;
        email: string;
        name: string | null;
        status: string;
        lastLogin: string | null;
        jobs: Array<{ jobNo: string; jobName: string | null }>;
      }>
    > => {
      const { data } = await getApiClient().get('/jobs/portal/clients');
      return data.data;
    },
    // Staff "View as client": mints a short-lived READ-ONLY portal token.
    portalPreview: async (
      portalUserId: string,
    ): Promise<{ previewToken: string; portalUser: { id: string; email: string; name: string | null }; tenantSlug: string }> => {
      const { data } = await getApiClient().post(`/jobs/portal/clients/${portalUserId}/preview`);
      return data.data;
    },

    copyJob: async (jobNo: string, newJobNo: string, copyOptions: Record<string, unknown>) => {
      const { data } = await getApiClient().post(`/jobs/${jobNo}/copy`, { newJobNo, copyOptions });
      return data;
    },

    getSummary: async (jobNo: string) => {
      const { data } = await getApiClient().get(`/jobs/${jobNo}/summary`);
      return data;
    },

    getStatuses: async (): Promise<string[]> => {
      const { data } = await getApiClient().get('/jobs/statuses');
      return data;
    },

    renameStatus: async (oldName: string, newName: string): Promise<{ success: boolean; updated: number }> => {
      const { data } = await getApiClient().post('/jobs/statuses/rename', { oldName, newName });
      return data;
    },
  },

  orders: {
    search: async (query: string, limit = 5): Promise<Array<{
      id: string;
      orderNumber: string;
      jobNo: string;
      jobName: string;
      costCentreCode: string | null;
      supplierCode: string | null;
      supplierName: string | null;
      status: string;
      orderDate: string | null;
    }>> => {
      const { data } = await getApiClient().get('/orders/search', { params: { q: query, limit } });
      return data;
    },

    getOrdersForJob: async (jobNo: string, options?: Record<string, unknown>): Promise<Order[]> => {
      const { data } = await getApiClient().get(`/orders/jobs/${jobNo}`, { params: options });
      return data;
    },

    getOrdersBySupplier: async (supplierCode: string, options?: { status?: string; limit?: number; jobNo?: string }): Promise<Order[]> => {
      const { data } = await getApiClient().get(`/orders/supplier/${supplierCode}`, { params: options });
      return data;
    },

    getOrder: async (orderNumber: string): Promise<Order> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().get(`/orders/${encoded}`);
      return data;
    },

    getOrderItems: async (orderNumber: string) => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().get(`/orders/${encoded}/items`);
      return data;
    },

    createOrder: async (orderData: Partial<Order>): Promise<Order> => {
      const { data } = await getApiClient().post('/orders', orderData);
      return data;
    },

    updateOrder: async (orderNumber: string, updates: OrderUpdate): Promise<Order> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().patch(`/orders/${encoded}`, updates);
      return data;
    },

    sendOrder: async (
      orderNumber: string,
      emailSettings?: { email?: boolean; to?: string; cc?: string; subject?: string; message?: string; transmittalId?: string }
    ): Promise<{ success: boolean; message: string; emailSent?: boolean; emailTo?: string; emailError?: string }> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encoded}/send`, { emailSettings });
      return data;
    },

    // Re-send the PO PDF for an already-sent order (no status change)
    emailOrder: async (
      orderNumber: string,
      emailSettings: { to?: string; cc?: string; subject?: string; message?: string; transmittalId?: string }
    ): Promise<{ success: boolean; message: string; emailSent?: boolean; emailTo?: string; emailError?: string }> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encoded}/email`, { emailSettings });
      return data;
    },

    cancelOrder: async (orderNumber: string, reason: string) => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encoded}/cancel`, { reason });
      return data;
    },

    deleteOrder: async (orderNumber: string) => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().delete(`/orders/${encoded}`);
      return data;
    },

    reinstateOrder: async (orderNumber: string) => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encoded}/reinstate`);
      return data;
    },

    // Admin: clear the load's migrated order snapshot so the PO reflects the live BoQ
    resyncFromBoq: async (orderNumber: string): Promise<{ success: boolean; clearedRows: number }> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encoded}/resync-from-boq`);
      return data;
    },

    updateOrderMetadata: async (orderNumber: string, metadata: Record<string, unknown>): Promise<{ success: boolean }> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().patch(`/orders/${encoded}/metadata`, { metadata });
      return data;
    },

    getPDF: async (orderNumber: string, options?: Record<string, unknown>): Promise<Blob> => {
      const encoded = encodeURIComponent(orderNumber);
      const response = await getApiClient().get(`/orders/${encoded}/pdf`, {
        params: options,
        responseType: 'blob',
      });
      return response.data;
    },

    getPreviewHTML: async (orderNumber: string, options?: Record<string, unknown>): Promise<string> => {
      const encoded = encodeURIComponent(orderNumber);
      const response = await getApiClient().get(`/orders/${encoded}/preview`, {
        params: options,
      });
      return response.data;
    },
  },

  suppliers: {
    getList: async (options?: Record<string, unknown>): Promise<Supplier[]> => {
      const { data } = await getApiClient().get('/suppliers', { params: options });
      return data;
    },

    getNominatedSuppliers: async (priceCode: string): Promise<Array<{
      id: string;
      code: string;
      name: string;
      phone: string | null;
      email: string | null;
      price: number;
      supplierReference: string | null;
    }>> => {
      const { data } = await getApiClient().get(`/suppliers/nominated/${encodeURIComponent(priceCode)}`);
      return data;
    },

    getSupplier: async (code: string): Promise<Supplier> => {
      const { data } = await getApiClient().get(`/suppliers/${code}`);
      return data;
    },

    createSupplier: async (supplierData: Partial<Supplier>): Promise<Supplier> => {
      const { data } = await getApiClient().post('/suppliers', supplierData);
      return data;
    },

    updateSupplier: async (code: string, updates: Partial<Supplier>): Promise<Supplier> => {
      const { data } = await getApiClient().patch(`/suppliers/${code}`, updates);
      return data;
    },

    deleteSupplier: async (code: string): Promise<void> => {
      await getApiClient().delete(`/suppliers/${code}`);
    },

    // Documents (the supplier's own document home — mirrors contacts)
    getDocuments: async (code: string): Promise<ContactDocument[]> => {
      const { data } = await getApiClient().get(`/suppliers/${code}/documents`);
      return data;
    },

    uploadDocument: async (code: string, file: File, metadata: {
      name: string;
      category: string;
      expiryDate?: string;
      description?: string;
      tags?: string[];
    }): Promise<ContactDocument> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', metadata.name);
      formData.append('category', metadata.category);
      if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

      const { data } = await getApiClient().post(`/suppliers/${code}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    downloadDocument: async (code: string, documentId: string): Promise<Blob> => {
      const response = await getApiClient().get(`/suppliers/${code}/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },

    updateDocument: async (code: string, documentId: string, updates: {
      name?: string;
      category?: string;
      expiryDate?: string;
      description?: string;
      tags?: string[];
    }): Promise<ContactDocument> => {
      const { data } = await getApiClient().patch(`/suppliers/${code}/documents/${documentId}`, updates);
      return data;
    },

    deleteDocument: async (code: string, documentId: string): Promise<void> => {
      await getApiClient().delete(`/suppliers/${code}/documents/${documentId}`);
    },

    getPrices: async (code: string, options?: Record<string, unknown>) => {
      const { data } = await getApiClient().get(`/suppliers/${code}/prices`, { params: options });
      return data;
    },

    createPrice: async (code: string, priceData: {
      priceCode: string;
      supplierReference?: string;
      price: number;
      effectiveDate?: string;
      expiryDate?: string;
      priceLevel?: number;
      area?: string;
      comments?: string;
    }) => {
      const { data } = await getApiClient().post(`/suppliers/${code}/prices`, priceData);
      return data;
    },

    updatePrice: async (code: string, priceId: string, updates: {
      supplierReference?: string;
      price?: number;
      effectiveDate?: string;
      expiryDate?: string;
      priceLevel?: number;
      area?: string;
      comments?: string;
    }) => {
      const { data } = await getApiClient().patch(`/suppliers/${code}/prices/${priceId}`, updates);
      return data;
    },

    deletePrice: async (code: string, priceId: string): Promise<void> => {
      await getApiClient().delete(`/suppliers/${code}/prices/${priceId}`);
    },

    importPrices: async (file: File, options: {
      supplierCode: string;
      preview?: boolean;
      updateExisting?: boolean;
      defaultPriceLevel?: number;
      defaultEffectiveDate?: string;
    }): Promise<{
      success: boolean;
      totalRows?: number;
      imported?: number;
      skipped?: number;
      validRows?: number;
      preview?: boolean;
      sample?: Array<Record<string, unknown>>;
      parseErrors?: Array<{ row: number; error: string }>;
      errors?: Array<{ row: number; priceCode?: string; error: string }>;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      if (options.preview) formData.append('preview', 'true');
      if (options.updateExisting) formData.append('updateExisting', 'true');
      if (options.defaultPriceLevel) formData.append('defaultPriceLevel', String(options.defaultPriceLevel));
      if (options.defaultEffectiveDate) formData.append('defaultEffectiveDate', options.defaultEffectiveDate);

      const { data } = await getApiClient().post(`/suppliers/${options.supplierCode}/prices/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    getImportTemplate: async (): Promise<Blob> => {
      const response = await getApiClient().get('/suppliers/prices/import/template', {
        responseType: 'blob',
      });
      return response.data;
    },

    getOrders: async (code: string, options?: { dateFrom?: string; dateTo?: string }) => {
      const { data } = await getApiClient().get(`/suppliers/${code}/orders`, { params: options });
      return data;
    },

    getTransactions: async (code: string, options?: { dateFrom?: string; dateTo?: string }) => {
      const { data } = await getApiClient().get(`/suppliers/${code}/transactions`, { params: options });
      return data;
    },

    getTransactionSummary: async (code: string) => {
      const { data } = await getApiClient().get(`/suppliers/${code}/transactions/summary`);
      return data;
    },

    getGroups: async () => {
      const { data } = await getApiClient().get('/suppliers/groups');
      return data;
    },
  },

  // Canned order comments (the Databuild StandardNotes equivalent) — inserted
  // into an order's notes from a dropdown; managed in Settings → Standard Notes.
  // Sales CRM — leads pipeline
  leads: {
    getList: async (params?: {
      stage?: string;
      ownerUserId?: string;
      search?: string;
      includeClosed?: boolean;
      limit?: number;
      offset?: number;
    }): Promise<Lead[]> => {
      const { data } = await getApiClient().get('/leads', { params });
      return data.data || [];
    },

    getStats: async (): Promise<Array<{ stage: string; count: number; value: number }>> => {
      const { data } = await getApiClient().get('/leads/stats');
      return data.data || [];
    },

    getLead: async (id: string): Promise<Lead> => {
      const { data } = await getApiClient().get(`/leads/${id}`);
      return data.data;
    },

    createLead: async (payload: Partial<Lead>): Promise<Lead> => {
      const { data } = await getApiClient().post('/leads', payload);
      return data.data;
    },

    updateLead: async (id: string, patch: Partial<Lead>): Promise<Lead> => {
      const { data } = await getApiClient().patch(`/leads/${id}`, patch);
      return data.data;
    },

    moveStage: async (id: string, stage: string, lostReason?: string): Promise<Lead> => {
      const { data } = await getApiClient().post(`/leads/${id}/stage`, { stage, lostReason });
      return data.data;
    },

    deleteLead: async (id: string): Promise<void> => {
      await getApiClient().delete(`/leads/${id}`);
    },

    getActivities: async (id: string): Promise<LeadActivity[]> => {
      const { data } = await getApiClient().get(`/leads/${id}/activities`);
      return data.data || [];
    },

    addActivity: async (
      id: string,
      activity: { activityType: string; title?: string; description?: string; dueDate?: string | null }
    ): Promise<LeadActivity> => {
      const { data } = await getApiClient().post(`/leads/${id}/activities`, activity);
      return data.data;
    },

    updateActivity: async (
      id: string,
      activityId: string,
      patch: { completed?: boolean; title?: string; description?: string; dueDate?: string | null }
    ): Promise<LeadActivity> => {
      const { data } = await getApiClient().patch(`/leads/${id}/activities/${activityId}`, patch);
      return data.data;
    },

    deleteActivity: async (id: string, activityId: string): Promise<void> => {
      await getApiClient().delete(`/leads/${id}/activities/${activityId}`);
    },
  },

  standardNotes: {
    list: async (includeInactive = false): Promise<Array<{ id: string; noteText: string; sortOrder: number; active: boolean }>> => {
      const { data } = await getApiClient().get('/standard-notes', { params: includeInactive ? { includeInactive: '1' } : undefined });
      return data.data ?? data;
    },
    create: async (noteText: string): Promise<{ id: string; noteText: string; sortOrder: number; active: boolean }> => {
      const { data } = await getApiClient().post('/standard-notes', { noteText });
      return data.data ?? data;
    },
    update: async (id: string, patch: { noteText?: string; sortOrder?: number; active?: boolean }) => {
      const { data } = await getApiClient().put(`/standard-notes/${id}`, patch);
      return data.data ?? data;
    },
    remove: async (id: string): Promise<void> => {
      await getApiClient().delete(`/standard-notes/${id}`);
    },
  },

  contacts: {
    getList: async (options?: {
      showArchived?: boolean;
      search?: string;
      groupId?: string;
      type?: 'supplier' | 'debtor' | 'general' | 'all';
      letter?: string;
      tags?: string[];
    }): Promise<Contact[]> => {
      const params: Record<string, unknown> = { ...options };
      if (options?.tags) {
        params.tags = options.tags.join(',');
      }
      const { data } = await getApiClient().get('/contacts', { params });
      return data;
    },

    getContact: async (code: string): Promise<Contact> => {
      const { data } = await getApiClient().get(`/contacts/${code}`);
      return data;
    },

    generateCode: async (name: string): Promise<string> => {
      const { data } = await getApiClient().get('/contacts/next-code', { params: { name } });
      return data.code;
    },

    createContact: async (contactData: Partial<Contact>): Promise<Contact> => {
      const { data } = await getApiClient().post('/contacts', contactData);
      return data;
    },

    importContacts: async (
      rows: Array<Record<string, unknown>>,
      options?: { dedupe?: 'skip' | 'none'; defaultSupplier?: boolean; defaultDebtor?: boolean; createMissingGroups?: boolean }
    ): Promise<{ total: number; created: number; skipped: number; errors: Array<{ row: number; name: string; reason: string }>; createdCodes: string[] }> => {
      const { data } = await getApiClient().post('/contacts/import', { rows, options });
      return data;
    },

    updateContact: async (code: string, updates: Partial<Contact>): Promise<Contact> => {
      const { data } = await getApiClient().patch(`/contacts/${code}`, updates);
      return data;
    },

    deleteContact: async (code: string): Promise<void> => {
      await getApiClient().delete(`/contacts/${code}`);
    },

    // Groups
    getGroups: async (): Promise<ContactGroup[]> => {
      const { data } = await getApiClient().get('/contacts/groups');
      return data;
    },

    createGroup: async (groupData: Partial<ContactGroup>): Promise<ContactGroup> => {
      const { data } = await getApiClient().post('/contacts/groups', groupData);
      return data;
    },

    updateGroup: async (id: string, updates: Partial<ContactGroup>): Promise<ContactGroup> => {
      const { data } = await getApiClient().patch(`/contacts/groups/${id}`, updates);
      return data;
    },

    deleteGroup: async (id: string): Promise<{ success: boolean }> => {
      const { data } = await getApiClient().delete(`/contacts/groups/${id}`);
      return data;
    },

    // Contact types (controlled list)
    getContactTypes: async (): Promise<ContactTypeDef[]> => {
      const { data } = await getApiClient().get('/contacts/types');
      return data;
    },
    createContactType: async (payload: { name: string }): Promise<ContactTypeDef> => {
      const { data } = await getApiClient().post('/contacts/types', payload);
      return data;
    },
    updateContactType: async (id: string, payload: { name?: string; sortOrder?: number }): Promise<ContactTypeDef> => {
      const { data } = await getApiClient().patch(`/contacts/types/${id}`, payload);
      return data;
    },
    deleteContactType: async (id: string): Promise<{ success: boolean }> => {
      const { data } = await getApiClient().delete(`/contacts/types/${id}`);
      return data;
    },

    // Tags
    getTags: async (): Promise<ContactTag[]> => {
      const { data } = await getApiClient().get('/contacts/tags');
      return data;
    },

    createTag: async (tagData: Partial<ContactTag>): Promise<ContactTag> => {
      const { data } = await getApiClient().post('/contacts/tags', tagData);
      return data;
    },

    updateTag: async (id: string, updates: Partial<ContactTag>): Promise<ContactTag> => {
      const { data } = await getApiClient().patch(`/contacts/tags/${id}`, updates);
      return data;
    },

    deleteTag: async (id: string): Promise<{ success: boolean }> => {
      const { data } = await getApiClient().delete(`/contacts/tags/${id}`);
      return data;
    },

    updateContactTags: async (code: string, tags: string[]): Promise<Contact> => {
      const { data } = await getApiClient().patch(`/contacts/${code}/tags`, { tags });
      return data;
    },

    // A-Z Navigation
    getLetterCounts: async (): Promise<ContactLetterCount[]> => {
      const { data } = await getApiClient().get('/contacts/letters');
      return data;
    },

    getByLetter: async (letter: string, options?: { showArchived?: boolean; type?: string }): Promise<Contact[]> => {
      const { data } = await getApiClient().get(`/contacts/by-letter/${letter}`, { params: options });
      return data;
    },

    // Relationships
    getRelationships: async (code: string): Promise<ContactRelationship[]> => {
      const { data } = await getApiClient().get(`/contacts/${code}/relationships`);
      return data;
    },

    addRelationship: async (code: string, targetCode: string, relationshipType: string, category?: string): Promise<ContactRelationship> => {
      const { data } = await getApiClient().post(`/contacts/${code}/relationships`, { targetCode, relationshipType, category });
      return data;
    },

    removeRelationship: async (code: string, relationshipId: string): Promise<void> => {
      await getApiClient().delete(`/contacts/${code}/relationships/${relationshipId}`);
    },

    // Activities
    getActivities: async (code: string, options?: { activityType?: string; limit?: number }): Promise<ContactActivity[]> => {
      const { data } = await getApiClient().get(`/contacts/${code}/activities`, { params: options });
      return data;
    },

    addActivity: async (code: string, activityData: Partial<ContactActivity>): Promise<ContactActivity> => {
      const { data } = await getApiClient().post(`/contacts/${code}/activities`, activityData);
      return data;
    },

    updateActivity: async (code: string, activityId: string, updates: Partial<ContactActivity>): Promise<ContactActivity> => {
      const { data } = await getApiClient().patch(`/contacts/${code}/activities/${activityId}`, updates);
      return data;
    },

    deleteActivity: async (code: string, activityId: string): Promise<void> => {
      await getApiClient().delete(`/contacts/${code}/activities/${activityId}`);
    },

    // Reminders and Alerts
    getReminders: async (days?: number): Promise<ContactReminder[]> => {
      const { data } = await getApiClient().get('/contacts/reminders', { params: { days } });
      return data;
    },

    getExpiringDocuments: async (days?: number): Promise<DocumentAlert[]> => {
      const { data } = await getApiClient().get('/contacts/expiring-documents', { params: { days } });
      return data;
    },

    // Documents
    getDocuments: async (code: string): Promise<ContactDocument[]> => {
      const { data } = await getApiClient().get(`/contacts/${code}/documents`);
      return data;
    },

    uploadDocument: async (code: string, file: File, metadata: {
      name: string;
      category: string;
      expiryDate?: string;
      description?: string;
      tags?: string[];
    }): Promise<ContactDocument> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('name', metadata.name);
      formData.append('category', metadata.category);
      if (metadata.expiryDate) formData.append('expiryDate', metadata.expiryDate);
      if (metadata.description) formData.append('description', metadata.description);
      if (metadata.tags) formData.append('tags', JSON.stringify(metadata.tags));

      const { data } = await getApiClient().post(`/contacts/${code}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    downloadDocument: async (code: string, documentId: string): Promise<Blob> => {
      const response = await getApiClient().get(`/contacts/${code}/documents/${documentId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },

    updateDocument: async (code: string, documentId: string, updates: {
      name?: string;
      category?: string;
      expiryDate?: string;
      description?: string;
      tags?: string[];
    }): Promise<ContactDocument> => {
      const { data } = await getApiClient().patch(`/contacts/${code}/documents/${documentId}`, updates);
      return data;
    },

    deleteDocument: async (code: string, documentId: string): Promise<void> => {
      await getApiClient().delete(`/contacts/${code}/documents/${documentId}`);
    },
  },

  projects: {
    // Project CRUD
    getList: async (options?: {
      showArchived?: boolean;
      status?: string;
      search?: string;
      jobNo?: string;
    }): Promise<Project[]> => {
      const { data } = await getApiClient().get('/projects', { params: options });
      return data;
    },

    getTemplates: async (): Promise<Project[]> => {
      const { data } = await getApiClient().get('/projects/templates');
      return data;
    },

    search: async (query: string, limit = 5): Promise<Project[]> => {
      const { data } = await getApiClient().get('/projects/search', { params: { q: query, limit } });
      return data;
    },

    getProject: async (id: string): Promise<Project> => {
      const { data } = await getApiClient().get(`/projects/${id}`);
      return data;
    },

    getProjectsByJob: async (jobNo: string): Promise<Project[]> => {
      const { data } = await getApiClient().get(`/projects/by-job/${jobNo}`);
      return data;
    },

    createProject: async (projectData: Partial<Project>): Promise<Project> => {
      const { data } = await getApiClient().post('/projects', projectData);
      return data;
    },

    applyTemplate: async (projectId: string, templateId: string, startDate: string): Promise<{ added: number }> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/apply-template`, { templateId, startDate });
      return data;
    },

    saveAsTemplate: async (projectId: string, name: string): Promise<Project> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/save-as-template`, { name });
      return data;
    },

    createFromTemplate: async (templateId: string, name: string, startDate: string, jobNo?: string): Promise<Project> => {
      const { data } = await getApiClient().post('/projects/from-template', { templateId, name, startDate, jobNo });
      return data;
    },

    createFromJob: async (jobNo: string, name?: string): Promise<Project> => {
      const { data } = await getApiClient().post('/projects/from-job', { jobNo, name });
      return data;
    },

    updateProject: async (id: string, updates: Partial<Project>): Promise<Project> => {
      const { data } = await getApiClient().patch(`/projects/${id}`, updates);
      return data;
    },

    deleteProject: async (id: string): Promise<void> => {
      await getApiClient().delete(`/projects/${id}`);
    },

    archiveProject: async (id: string): Promise<void> => {
      await getApiClient().post(`/projects/${id}/archive`);
    },

    duplicateProject: async (id: string, name: string): Promise<Project> => {
      const { data } = await getApiClient().post(`/projects/${id}/duplicate`, { name });
      return data;
    },

    importFromTemplate: async (projectId: string, templateId: string, startDate?: string): Promise<{ imported: number }> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/import-from-template`, { templateId, startDate });
      return data;
    },

    getSummary: async (id: string): Promise<ProjectSummary> => {
      const { data } = await getApiClient().get(`/projects/${id}/summary`);
      return data;
    },

    // Columns
    getColumns: async (projectId: string): Promise<ProjectColumn[]> => {
      const { data } = await getApiClient().get(`/projects/${projectId}/columns`);
      return data;
    },

    createColumn: async (projectId: string, columnData: { name: string; statusKey: string; color?: string; wipLimit?: number }): Promise<ProjectColumn> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/columns`, columnData);
      return data;
    },

    updateColumn: async (projectId: string, columnId: string, updates: Partial<ProjectColumn>): Promise<ProjectColumn> => {
      const { data } = await getApiClient().patch(`/projects/${projectId}/columns/${columnId}`, updates);
      return data;
    },

    deleteColumn: async (projectId: string, columnId: string): Promise<void> => {
      await getApiClient().delete(`/projects/${projectId}/columns/${columnId}`);
    },

    reorderColumns: async (projectId: string, columnIds: string[]): Promise<void> => {
      await getApiClient().post(`/projects/${projectId}/columns/reorder`, { columnIds });
    },

    // Tasks
    getTasks: async (projectId: string, options?: {
      status?: string;
      priority?: string;
      assignee?: string;
      assignedUserId?: string;
      dueBefore?: string;
      dueAfter?: string;
      parentTaskId?: string | null;
    }): Promise<ProjectTask[]> => {
      const params: Record<string, unknown> = { ...options };
      if (options?.parentTaskId === null) params.parentTaskId = 'null';
      const { data } = await getApiClient().get(`/projects/${projectId}/tasks`, { params });
      return data;
    },

    getTasksKanban: async (projectId: string): Promise<Record<string, ProjectTask[]>> => {
      const { data } = await getApiClient().get(`/projects/${projectId}/tasks/kanban`);
      return data;
    },

    getTasksGantt: async (projectId: string): Promise<GanttTask[]> => {
      const { data } = await getApiClient().get(`/projects/${projectId}/tasks/gantt`);
      return data;
    },

    // Tasks assigned to the logged-in staff user (by assigned_user_id, resolved server-side)
    getMyTasks: async (options?: {
      status?: string;
      taskType?: string;
      jobId?: string;
      entityType?: string;
      includeWorkflowTasks?: boolean;
    }): Promise<ProjectTask[]> => {
      const params: Record<string, unknown> = { ...options };
      const { data } = await getApiClient().get('/projects/tasks/my-tasks', { params });
      return data;
    },

    createTask: async (projectId: string, taskData: Partial<ProjectTask>): Promise<ProjectTask> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/tasks`, taskData);
      return data;
    },

    getTask: async (taskId: string): Promise<ProjectTask> => {
      const { data } = await getApiClient().get(`/projects/tasks/${taskId}`);
      return data;
    },

    updateTask: async (taskId: string, updates: Partial<ProjectTask>): Promise<ProjectTask> => {
      const { data } = await getApiClient().patch(`/projects/tasks/${taskId}`, updates);
      return data;
    },

    deleteTask: async (taskId: string): Promise<void> => {
      await getApiClient().delete(`/projects/tasks/${taskId}`);
    },

    updateTaskStatus: async (taskId: string, status: TaskStatus, columnOrder?: number): Promise<ProjectTask> => {
      const { data } = await getApiClient().patch(`/projects/tasks/${taskId}/status`, { status, columnOrder });
      return data;
    },

    updateTaskPosition: async (taskId: string, sortOrder: number, parentTaskId?: string | null): Promise<ProjectTask> => {
      const { data } = await getApiClient().patch(`/projects/tasks/${taskId}/position`, { sortOrder, parentTaskId });
      return data;
    },

    setParentTask: async (taskId: string, parentTaskId: string | null): Promise<ProjectTask> => {
      const { data } = await getApiClient().post(`/projects/tasks/${taskId}/parent`, { parentTaskId });
      return data;
    },

    getSubtasks: async (taskId: string): Promise<ProjectTask[]> => {
      const { data } = await getApiClient().get(`/projects/tasks/${taskId}/subtasks`);
      return data;
    },

    reorderTasksInColumn: async (projectId: string, status: string, taskIds: string[]): Promise<void> => {
      await getApiClient().post(`/projects/${projectId}/tasks/reorder`, { status, taskIds });
    },

    // Dependencies
    getDependencies: async (taskId: string): Promise<TaskDependency[]> => {
      const { data } = await getApiClient().get(`/projects/tasks/${taskId}/dependencies`);
      return data;
    },

    getPredecessors: async (taskId: string): Promise<TaskDependency[]> => {
      const { data } = await getApiClient().get(`/projects/tasks/${taskId}/predecessors`);
      return data;
    },

    getSuccessors: async (taskId: string): Promise<TaskDependency[]> => {
      const { data } = await getApiClient().get(`/projects/tasks/${taskId}/successors`);
      return data;
    },

    addDependency: async (taskId: string, predecessorId: string, dependencyType?: string, lagDays?: number): Promise<TaskDependency> => {
      const { data } = await getApiClient().post(`/projects/tasks/${taskId}/dependencies`, { predecessorId, dependencyType, lagDays });
      return data;
    },

    removeDependency: async (taskId: string, dependencyId: string): Promise<void> => {
      await getApiClient().delete(`/projects/tasks/${taskId}/dependencies/${dependencyId}`);
    },

    removeDependencyByEdge: async (taskId: string, predecessorId: string): Promise<void> => {
      await getApiClient().delete(`/projects/tasks/${taskId}/dependencies/by-edge`, { data: { predecessorId } });
    },

    // Gantt scheduling: auto-schedule toggle + working calendar, and the cascade reschedule
    updateProjectSettings: async (projectId: string, settings: { autoSchedule?: boolean; calendar?: WorkingCalendar | null }): Promise<Project> => {
      const { data } = await getApiClient().patch(`/projects/${projectId}/settings`, settings);
      return data;
    },
    reschedule: async (projectId: string, change: { changedTaskId: string; startDate: string; dueDate: string }): Promise<ProjectTask[]> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/reschedule`, change);
      return data;
    },
    captureBaseline: async (projectId: string): Promise<{ baselined: number }> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/baseline`);
      return data;
    },
    clearBaseline: async (projectId: string): Promise<{ cleared: number }> => {
      const { data } = await getApiClient().delete(`/projects/${projectId}/baseline`);
      return data;
    },
    exportMsProjectXml: async (projectId: string): Promise<{ xml: string; filename: string }> => {
      const res = await getApiClient().get(`/projects/${projectId}/export/msproject`, { responseType: 'text' });
      const cd = (res.headers?.['content-disposition'] as string) || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      return { xml: res.data as string, filename: m?.[1] || 'project.xml' };
    },
    importMsProjectXml: async (projectId: string, xml: string): Promise<{ tasks: number; links: number }> => {
      const { data } = await getApiClient().post(`/projects/${projectId}/import/msproject`, { xml });
      return data;
    },

    // Linked Items
    addLinkedItem: async (taskId: string, item: LinkedItem): Promise<ProjectTask> => {
      const { data } = await getApiClient().post(`/projects/tasks/${taskId}/linked-items`, item);
      return data;
    },

    removeLinkedItem: async (taskId: string, itemType: string, itemId: string): Promise<ProjectTask> => {
      const { data } = await getApiClient().delete(`/projects/tasks/${taskId}/linked-items/${itemType}/${itemId}`);
      return data;
    },

    // Bulk Operations
    bulkUpdateStatus: async (taskIds: string[], status: TaskStatus): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/projects/tasks/bulk/status', { taskIds, status });
      return data;
    },

    bulkUpdateAssignee: async (taskIds: string[], assigneeName: string | null): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/projects/tasks/bulk/assignee', { taskIds, assigneeName });
      return data;
    },

    // Real staff-user assignment (assigned_user_id). null clears the assignment.
    bulkUpdateAssignedUser: async (taskIds: string[], assignedUserId: string | null): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/projects/tasks/bulk/assignee', { taskIds, assignedUserId });
      return data;
    },

    bulkDeleteTasks: async (taskIds: string[]): Promise<{ deleted: number }> => {
      const { data } = await getApiClient().delete('/projects/tasks/bulk', { data: { taskIds } });
      return data;
    },

    bulkShiftDates: async (taskIds: string[], shiftDays: number): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/projects/tasks/bulk/shift-dates', { taskIds, shiftDays });
      return data;
    },
  },

  accounting: {
    // Transactions
    getTransactions: async (options?: TransactionSearchOptions): Promise<{
      data: Transaction[];
      total: number;
      page: number;
      limit: number;
    }> => {
      const { data } = await getApiClient().get('/accounting/transactions', { params: options });
      return data;
    },

    getTransaction: async (tranNo: number): Promise<Transaction> => {
      const { data } = await getApiClient().get(`/accounting/transactions/${tranNo}`);
      return data.data;
    },

    createTransaction: async (transactionData: CreateTransactionData): Promise<Transaction> => {
      const { data } = await getApiClient().post('/accounting/transactions', transactionData);
      return data.data;
    },

    updateTransaction: async (tranNo: number, updates: Partial<CreateTransactionData>): Promise<Transaction> => {
      const { data } = await getApiClient().patch(`/accounting/transactions/${tranNo}`, updates);
      return data.data;
    },

    deleteTransaction: async (tranNo: number): Promise<void> => {
      await getApiClient().delete(`/accounting/transactions/${tranNo}`);
    },

    // Invoices
    getInvoices: async (options?: TransactionSearchOptions): Promise<{
      data: SupplierInvoice[];
      total: number;
    }> => {
      const { data } = await getApiClient().get('/accounting/invoices', { params: options });
      return data;
    },

    createInvoice: async (invoiceData: Omit<CreateTransactionData, 'tranType'>): Promise<SupplierInvoice> => {
      const { data } = await getApiClient().post('/accounting/invoices', invoiceData);
      return data.data;
    },

    updateInvoice: async (tranNo: number, invoiceData: Partial<Omit<CreateTransactionData, 'tranType'>>): Promise<SupplierInvoice> => {
      const { data } = await getApiClient().patch(`/accounting/invoices/${tranNo}`, invoiceData);
      return data.data;
    },

    getPayableInvoices: async (supplierCode?: string): Promise<PayableInvoice[]> => {
      const { data } = await getApiClient().get('/accounting/invoices/payable', { params: { supplierCode } });
      return data.data;
    },

    // AI-powered invoice data extraction from uploaded document
    extractInvoice: async (file: File): Promise<{
      extracted: {
        invoiceNumber: string | null;
        invoiceDate: string | null;
        dueDate: string | null;
        paymentTermsDays: number | null;
        totalAmount: number | null;
        gstAmount: number | null;
        subtotal: number | null;
        supplierName: string | null;
        supplierABN: string | null;
        poNumber: string | null;
        lineItems: Array<{ description: string; quantity: number | null; unitPrice: number | null; total: number | null }>;
        confidence: { overall: string; invoiceNumber: string | null; amount: string | null; supplier: string | null; dates: string | null };
      };
      supplierMatches: Array<{ id: string; code: string; name: string; abn: string | null; score: number; matchedOn: string }>;
      orderMatch: { orderNumber: string; jobNo: string; costCentreName: string } | null;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getApiClient().post('/accounting/invoices/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });
      return data.data;
    },

    // AI smart-match: extract an invoice and match its lines to a PO, returning
    // suggested qty/price per PO line so the matching wizard can be pre-filled.
    // Pass either an uploaded file or the fileId of an existing invoice attachment.
    smartMatchOrder: async (
      orderNumber: string,
      input: { file?: File; fileId?: string }
    ): Promise<InvoiceSmartMatchResult> => {
      const url = `/accounting/orders/${encodeURIComponent(orderNumber)}/smart-match`;
      if (input.file) {
        const formData = new FormData();
        formData.append('file', input.file);
        const { data } = await getApiClient().post(url, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120_000,
        });
        return data.data;
      }
      const { data } = await getApiClient().post(url, { fileId: input.fileId }, { timeout: 120_000 });
      return data.data;
    },

    // Get payment history with invoices closed (paginated)
    getPaymentHistory: async (options?: {
      supplierCode?: string;
      fromDate?: string;
      toDate?: string;
      search?: string;
      page?: number;
      limit?: number;
      knownTotal?: number;
    }): Promise<{
      items: Array<{
        id: string;
        tranNo: number;
        tranDate: string;
        supplierCode: string;
        supplierName: string;
        reference: string | null;
        amount: number;
        batchNumber: number | null;
        invoicesClosed: Array<{
          tranNo: number;
          reference: string | null;
          amount: number;
          tranDate: string;
        }>;
      }>;
      total: number;
      page: number;
      limit: number;
    }> => {
      const { data } = await getApiClient().get('/accounting/payment-history', { params: options });
      return {
        items: data.data,
        total: data.total,
        page: data.page,
        limit: data.limit,
      };
    },

    // Get transaction audit trail (all job-related transactions) with compound search
    getAuditTrail: async (options?: {
      // Basic filters
      jobNo?: string;
      tranNo?: number;
      supplierCode?: string;
      tranType?: number | number[];
      fromDate?: string;
      toDate?: string;
      search?: string;
      page?: number;
      limit?: number;
      // Compound search filters
      tranNoFrom?: number;
      tranNoTo?: number;
      amountFrom?: number;
      amountTo?: number;
      gstFrom?: number;
      gstTo?: number;
      isPaid?: boolean;
      isAuthorised?: boolean;
      isReconciled?: boolean;
      costCentreCode?: string;
      bank?: number;
      reference?: string;
      comments?: string;
      closingTranFrom?: number;
      closingTranTo?: number;
      includeOverhead?: boolean;
      knownTotal?: number;
    }): Promise<{
      items: Array<{
        id: string;
        tranNo: number;
        tranType: number;
        tranTypeCode: string;
        tranTypeName: string;
        tranDate: string;
        jobNo: string | null;
        jobName: string | null;
        siteAddress: string | null;
        supplierCode: string | null;
        supplierName: string | null;
        costCentreCode: string | null;
        reference: string | null;
        comments: string | null;
        amount: number;
        gst: number;
        bank: number;
        isAuthorised: boolean;
        isReconciled: boolean;
        closingTran: number | null;
        closedByTran: number | null;
      }>;
      total: number;
      page: number;
      limit: number;
    }> => {
      // Serialize tranType array properly for query string
      const params: Record<string, unknown> = { ...options };
      if (options?.tranType && Array.isArray(options.tranType)) {
        // Convert array to comma-separated string for proper URL serialization
        params.tranType = options.tranType.join(',');
      }
      const { data } = await getApiClient().get('/accounting/audit-trail', { params });
      return {
        items: data.data,
        total: data.total,
        page: data.page,
        limit: data.limit,
      };
    },

    approveInvoice: async (tranNo: number, userId?: string): Promise<Transaction> => {
      const { data } = await getApiClient().post(`/accounting/invoices/${tranNo}/approve`, { userId });
      return data.data;
    },

    unapproveInvoice: async (tranNo: number): Promise<Transaction> => {
      const { data } = await getApiClient().post(`/accounting/invoices/${tranNo}/unapprove`);
      return data.data;
    },

    writeOffInvoice: async (tranNo: number, reason: string): Promise<Transaction> => {
      const { data } = await getApiClient().post(`/accounting/invoices/${tranNo}/write-off`, { reason });
      return data.data;
    },

    reverseWriteOff: async (tranNo: number): Promise<Transaction> => {
      const { data } = await getApiClient().post(`/accounting/invoices/${tranNo}/reverse-write-off`);
      return data.data;
    },

    // Receipts
    getOutstandingClaims: async (jobNo?: string): Promise<OutstandingClaim[]> => {
      const { data } = await getApiClient().get('/accounting/claims/outstanding', { params: { jobNo } });
      return data.data;
    },

    // Get all claims history (for Claims tab)
    getAllClaims: async (jobNo?: string): Promise<Array<{
      id: string;
      jobNo: string;
      clientName: string | null;
      siteAddress: string | null;
      milestone: string | null;
      tranNo: number;
      tranDate: string;
      reference: string | null;
      amount: number;
      gstAmount: number;
      totalIncGst: number;
      status: string;
    }>> => {
      const { data } = await getApiClient().get('/accounting/claims', { params: { jobNo } });
      return data.data;
    },

    // Get all receipts history (for Receipts tab)
    getReceipts: async (jobNo?: string): Promise<Array<{
      id: string;
      tranNo: number;
      tranDate: string;
      jobNo: string;
      clientName: string | null;
      siteAddress: string | null;
      reference: string | null;
      amount: number;
      status: string;
    }>> => {
      const { data } = await getApiClient().get('/accounting/receipts', { params: { jobNo } });
      return data.data;
    },

    createReceipt: async (receiptData: {
      receiptDate: string;
      reference?: string | null;
      bankAccount?: string | null;
      comments?: string | null;
      totalAmount: number;
      allocations: Array<{ claimId: string; amount: number }>;
    }): Promise<Transaction> => {
      const { data } = await getApiClient().post('/accounting/receipts', receiptData);
      return data.data;
    },

    // Approval
    getAwaitingApproval: async (tranType?: number): Promise<Transaction[]> => {
      const { data } = await getApiClient().get('/accounting/awaiting-approval', { params: { tranType } });
      return data.data;
    },

    // Payment Batches
    getPaymentBatches: async (options?: { status?: PaymentBatchStatus; limit?: number }): Promise<PaymentBatch[]> => {
      const { data } = await getApiClient().get('/accounting/payment-batches', { params: options });
      return data.data;
    },

    getPaymentBatch: async (id: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().get(`/accounting/payment-batches/${id}`);
      return data.data;
    },

    // Alias for modal compatibility
    getBatch: async (id: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().get(`/accounting/payment-batches/${id}`);
      return data.data;
    },

    createPaymentBatch: async (userId?: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post('/accounting/payment-batches', { userId });
      return data.data;
    },

    addToBatch: async (batchId: string, tranNos: number[]): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post(`/accounting/payment-batches/${batchId}/add`, { tranNos });
      return data.data;
    },

    removeFromBatch: async (batchId: string, tranNos: number[]): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post(`/accounting/payment-batches/${batchId}/remove`, { tranNos });
      return data.data;
    },

    authorizeBatch: async (batchId: string, userId?: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post(`/accounting/payment-batches/${batchId}/authorize`, { userId });
      return data.data;
    },

    processBatch: async (batchId: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post(`/accounting/payment-batches/${batchId}/process`);
      return data.data;
    },

    cancelBatch: async (batchId: string, reason?: string): Promise<PaymentBatch> => {
      const { data } = await getApiClient().post(`/accounting/payment-batches/${batchId}/cancel`, { reason });
      return data.data;
    },

    deleteBatch: async (batchId: string): Promise<void> => {
      await getApiClient().delete(`/accounting/payment-batches/${batchId}`);
    },

    // ABA File
    downloadABA: async (batchId: string, settings?: Partial<ABASettings>): Promise<Blob> => {
      const params = settings ? { ...settings } : {};
      const response = await getApiClient().get(`/accounting/payment-batches/${batchId}/aba`, {
        params,
        responseType: 'blob',
      });
      return response.data;
    },

    getABASettings: async (): Promise<Partial<ABASettings>> => {
      const { data } = await getApiClient().get('/accounting/aba/settings');
      return data.data;
    },

    saveABASettings: async (settings: Partial<ABASettings>): Promise<void> => {
      await getApiClient().put('/accounting/aba/settings', settings);
    },

    // Reports
    getAgedPayables: async (asAtDate?: string): Promise<AgedPayable[]> => {
      const { data } = await getApiClient().get('/accounting/payables/aged', { params: { asAtDate } });
      return data.data;
    },

    getAgedReceivables: async (asAtDate?: string): Promise<AgedReceivable[]> => {
      const { data } = await getApiClient().get('/accounting/receivables/aged', { params: { asAtDate } });
      return data.data;
    },

    getBackCharges: async (): Promise<Array<{ supplierCode: string; supplierName: string; amount: number; invoiceCount: number }>> => {
      const { data } = await getApiClient().get('/accounting/backcharges');
      return data.data;
    },

    getSupplierStatement: async (supplierCode: string, fromDate: string, toDate: string): Promise<SupplierStatement> => {
      const { data } = await getApiClient().get(`/accounting/suppliers/${supplierCode}/statement`, {
        params: { fromDate, toDate },
      });
      return data.data;
    },

    // Order Invoice History
    getOrderInvoices: async (orderNumber: string): Promise<{
      order: {
        orderNumber: string;
        supplierCode: string;
        supplierName: string;
        jobNo: string;
        jobName: string;
        siteAddress: string | null;
        orderDate: string;
        totalAmount: number;
        status: string;
        costCentreCode: string;
        costCentreName: string;
        isFullyInvoiced: boolean;
      } | null;
      invoices: Array<{
        tranNo: number;
        reference: string | null;
        amount: number;
        tranDate: string;
        dueDate: string | null;
        isAuthorised: boolean;
        closingTran: number;
        costCentreCode: string | null;
      }>;
      summary: {
        orderTotal: number;
        totalInvoiced: number;
        remainingBalance: number;
        invoiceCount: number;
      };
    }> => {
      // URL-encode order number since it contains "/" (e.g., "1453/1110")
      const encodedOrderNumber = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().get(`/accounting/orders/${encodedOrderNumber}/invoices`);
      return data.data;
    },

    // Mark order as fully invoiced
    markOrderFullyInvoiced: async (orderNumber: string, isFullyInvoiced: boolean = true): Promise<{ success: boolean }> => {
      const encodedOrderNumber = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().post(`/orders/${encodedOrderNumber}/fully-invoiced`, { isFullyInvoiced });
      return data;
    },

    // Budget Progress - User-entered % complete tracking
    getJobBudgetProgress: async (jobNo: string): Promise<{
      jobNo: string;
      totalBudget: number;
      totalActual: number;
      weightedPercentComplete: number;
      projectedFinal: number | null;
      variance: number | null;
      costCentres: Array<{
        costCentreId: string;
        code: string;
        name: string;
        sortOrder: number;
        budget: number;
        actualCost: number;
        percentComplete: number;
        projectedFinal: number | null;
        variance: number | null;
        updatedAt: string | null;
        updatedBy: string | null;
      }>;
    }> => {
      const { data } = await getApiClient().get(`/accounting/jobs/${jobNo}/budget-progress`);
      return data.data;
    },

    updateBudgetProgress: async (jobNo: string, items: Array<{ costCentreId: string; percentComplete: number }>): Promise<{
      jobNo: string;
      totalBudget: number;
      totalActual: number;
      weightedPercentComplete: number;
      projectedFinal: number | null;
      variance: number | null;
      costCentres: Array<{
        costCentreId: string;
        code: string;
        name: string;
        sortOrder: number;
        budget: number;
        actualCost: number;
        percentComplete: number;
        projectedFinal: number | null;
        variance: number | null;
        updatedAt: string | null;
        updatedBy: string | null;
      }>;
    }> => {
      const { data } = await getApiClient().put(`/accounting/jobs/${jobNo}/budget-progress`, { items });
      return data.data;
    },

    // Dashboard
    getDashboardSummary: async (): Promise<{
      totalPayables: number;
      overduePayables: number;
      awaitingApproval: number;
      totalReceivables: number;
      overdueReceivables: number;
      unpaidInvoiceCount: number;
      pendingClaimCount: number;
      totalBackCharges: number;
      backChargeCount: number;
    }> => {
      const { data } = await getApiClient().get('/accounting/dashboard');
      return data.data;
    },
  },

  files: {
    // Upload file and attach to transaction
    uploadAttachment: async (tranNo: number, file: File): Promise<{
      fileId: string;
      originalName: string;
      mimeType: string;
      size: number;
      uploadedAt: string;
    }> => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getApiClient().post(`/files/transactions/${tranNo}/attachments`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.attachment;
    },

    // Get attachments for transaction
    getAttachments: async (tranNo: number): Promise<Array<{
      fileId: string;
      originalName: string;
      mimeType: string;
      size: number;
      uploadedAt: string;
    }>> => {
      const { data } = await getApiClient().get(`/files/transactions/${tranNo}/attachments`);
      return data;
    },

    // Delete attachment from transaction
    deleteAttachment: async (tranNo: number, fileId: string): Promise<void> => {
      await getApiClient().delete(`/files/transactions/${tranNo}/attachments/${fileId}`);
    },

    // Get file URL for viewing/downloading
    getFileUrl: (fileId: string): string => {
      const baseUrl = API_BASE_URL;
      const token = localStorage.getItem('accessToken');
      if (!token) {
        console.warn('No access token found for file URL');
      }
      const url = `${baseUrl}/files/${fileId}?token=${encodeURIComponent(token || '')}`;
      console.log('File preview URL:', url.substring(0, 100) + '...');
      return url;
    },

    // Download file
    downloadFile: async (fileId: string): Promise<Blob> => {
      const response = await getApiClient().get(`/files/${fileId}/download`, {
        responseType: 'blob',
      });
      return response.data;
    },
  },

  quickbooks: {
    // OAuth
    getAuthUrl: async (): Promise<{ authUrl: string; state: string }> => {
      const { data } = await getApiClient().get('/quickbooks/connect');
      return data.data;
    },

    disconnect: async (): Promise<void> => {
      await getApiClient().post('/quickbooks/disconnect');
    },

    getStatus: async (): Promise<QuickBooksStatus> => {
      const { data } = await getApiClient().get('/quickbooks/status');
      return data.data;
    },

    // Sync
    syncInvoice: async (tranNo: number): Promise<QuickBooksSyncResult> => {
      const { data } = await getApiClient().post(`/quickbooks/sync/invoice/${tranNo}`);
      return data.data;
    },

    syncPayment: async (tranNo: number): Promise<QuickBooksSyncResult> => {
      const { data } = await getApiClient().post(`/quickbooks/sync/payment/${tranNo}`);
      return data.data;
    },

    syncSupplier: async (supplierCode: string): Promise<QuickBooksSyncResult> => {
      const { data } = await getApiClient().post(`/quickbooks/sync/supplier/${supplierCode}`);
      return data.data;
    },

    // GL Mapping
    getAccounts: async (): Promise<QuickBooksAccount[]> => {
      const { data } = await getApiClient().get('/quickbooks/accounts');
      return data.data;
    },

    getMappings: async (): Promise<GLMapping[]> => {
      const { data } = await getApiClient().get('/quickbooks/mappings');
      return data.data;
    },

    saveMapping: async (mapping: Omit<GLMapping, 'id' | 'createdAt' | 'updatedAt'>): Promise<GLMapping> => {
      const { data } = await getApiClient().post('/quickbooks/mappings', mapping);
      return data.data;
    },

    deleteMapping: async (glAccount: string): Promise<void> => {
      await getApiClient().delete(`/quickbooks/mappings/${glAccount}`);
    },
  },

  // ==================== INTEGRATIONS (Multi-platform) ====================
  integration: {
    // Platforms
    getPlatforms: async (): Promise<Array<{
      id: IntegrationPlatform;
      name: string;
      description: string;
      country: string;
      configured: boolean;
      connected: boolean;
    }>> => {
      const { data } = await getApiClient().get('/integration/platforms');
      return data.data;
    },

    // Settings
    getSettings: async (): Promise<IntegrationSettings> => {
      const { data } = await getApiClient().get('/integration/settings');
      return data.data;
    },

    updateSettings: async (settings: Partial<IntegrationSettings>): Promise<IntegrationSettings> => {
      const { data } = await getApiClient().put('/integration/settings', settings);
      return data.data;
    },

    // OAuth connect/disconnect
    getConnectUrl: async (platform: IntegrationPlatform): Promise<{ authUrl: string; state: string }> => {
      const { data } = await getApiClient().get(`/integration/${platform}/connect`);
      return data.data;
    },

    disconnect: async (platform: IntegrationPlatform): Promise<void> => {
      await getApiClient().post(`/integration/${platform}/disconnect`);
    },

    // Status
    getStatus: async (platform: IntegrationPlatform): Promise<{
      platform: string;
      connected: boolean;
      stats: { synced: number; pending: number; failed: number };
      [key: string]: unknown;
    }> => {
      const { data } = await getApiClient().get(`/integration/${platform}/status`);
      return data.data;
    },

    // GL Mappings
    getGLMappings: async (): Promise<GLMappingEntry[]> => {
      const { data } = await getApiClient().get('/integration/gl-mappings');
      return data.data;
    },

    saveGLMapping: async (mapping: Omit<GLMappingEntry, 'id' | 'createdAt' | 'updatedAt'>): Promise<GLMappingEntry> => {
      const { data } = await getApiClient().post('/integration/gl-mappings', mapping);
      return data.data;
    },

    deleteGLMapping: async (accountCode: string): Promise<void> => {
      await getApiClient().delete(`/integration/gl-mappings/${accountCode}`);
    },

    // Remote accounts
    getRemoteAccounts: async (platform: IntegrationPlatform): Promise<RemoteAccount[]> => {
      const { data } = await getApiClient().get(`/integration/${platform}/accounts`);
      return data.data;
    },

    // Sync
    syncEntity: async (platform: IntegrationPlatform, entityType: string, entityId: string): Promise<IntegrationSyncResult> => {
      const { data } = await getApiClient().post(`/integration/${platform}/sync/${entityType}/${entityId}`);
      return data.data;
    },

    syncAll: async (platform: IntegrationPlatform): Promise<{ success: number; failed: number; errors: string[] }> => {
      const { data } = await getApiClient().post(`/integration/${platform}/sync-all`);
      return data.data;
    },

    retryFailed: async (platform: IntegrationPlatform): Promise<{ resetCount: number }> => {
      const { data } = await getApiClient().post(`/integration/${platform}/retry-failed`);
      return data.data;
    },

    // Sync history
    getSyncHistory: async (platform: IntegrationPlatform, params?: {
      limit?: number;
      offset?: number;
      entityType?: string;
      status?: string;
    }): Promise<{ records: IntegrationSyncRecord[]; total: number }> => {
      const { data } = await getApiClient().get(`/integration/${platform}/sync-history`, { params });
      return data.data;
    },
  },

  // DocuSign (Bring-Your-Own) — connect the tenant's own DocuSign account.
  docusign: {
    getStatus: async (): Promise<DocusignConnectionStatus> => {
      const { data } = await getApiClient().get('/docusign/status');
      return data.data;
    },
    getConnectUrl: async (): Promise<{ authUrl: string }> => {
      const { data } = await getApiClient().get('/docusign/connect');
      return data.data;
    },
    disconnect: async (): Promise<void> => {
      await getApiClient().post('/docusign/disconnect');
    },
    sendProposal: async (quoteId: string, payload: { email?: string; name?: string }): Promise<{ envelopeId: string }> => {
      const { data } = await getApiClient().post(`/docusign/send/${quoteId}`, payload);
      return data.data;
    },
  },

  claims: {
    // Get claimable cost centres with BOQ totals and claim progress
    getCostCentres: async (jobNo: string): Promise<ClaimableCostCentre[]> => {
      const { data } = await getApiClient().get(`/claims/jobs/${jobNo}/cost-centres`);
      return data;
    },

    // Preview claim calculation
    previewClaim: async (jobNo: string, items: ClaimProgressItem[]): Promise<ClaimPreviewResult> => {
      const { data } = await getApiClient().post(`/claims/jobs/${jobNo}/preview`, { items });
      return data;
    },

    // Create a claim with cost centre breakdown
    createClaim: async (jobNo: string, claimData: CreateClaimData): Promise<{
      transactions: Transaction[];
      totalExGst: number;
      gstAmount: number;
      totalIncGst: number;
    }> => {
      const { data } = await getApiClient().post(`/claims/jobs/${jobNo}`, claimData);
      return data;
    },

    // Get claim history for a job
    getHistory: async (jobNo: string): Promise<ClaimHistoryItem[]> => {
      const { data } = await getApiClient().get(`/claims/jobs/${jobNo}/history`);
      return data;
    },

    // Get claim summary for a job
    getSummary: async (jobNo: string): Promise<ClaimSummary> => {
      const { data } = await getApiClient().get(`/claims/jobs/${jobNo}/summary`);
      return data;
    },

    // Delete a claim
    deleteClaim: async (tranNo: number): Promise<void> => {
      await getApiClient().delete(`/claims/${tranNo}`);
    },
  },

  invoiceMatching: {
    // Get order data for matching
    getOrderForMatching: async (orderNumber: string): Promise<OrderMatchData> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().get(`/invoice-matching/orders/${encoded}/for-matching`);
      return data.data;
    },

    // Match invoice lines to PO lines
    matchLines: async (
      tranNo: number,
      orderNumber: string,
      lines: InvoiceLineMatchInput[]
    ): Promise<InvoiceMatchResult> => {
      const { data } = await getApiClient().post('/invoice-matching/match', {
        tranNo,
        orderNumber,
        lines,
      });
      return data.data;
    },

    // Handle variance action
    handleVarianceAction: async (matchId: string, request: VarianceActionRequest): Promise<VarianceAction> => {
      const { data } = await getApiClient().post(`/invoice-matching/${matchId}/variance-action`, request);
      return data.data;
    },

    // Apply retention
    applyRetention: async (matchId: string, request: ApplyRetentionRequest): Promise<InvoiceMatch> => {
      const { data } = await getApiClient().post(`/invoice-matching/${matchId}/retention`, request);
      return data.data;
    },

    // Release retention
    releaseRetention: async (matchId: string, request: ReleaseRetentionRequest): Promise<Transaction> => {
      const { data } = await getApiClient().post(`/invoice-matching/${matchId}/retention/release`, request);
      return data.data;
    },

    // Record GRN
    recordGRN: async (request: RecordGRNRequest): Promise<OrderReceipt[]> => {
      const { data } = await getApiClient().post('/invoice-matching/grn', request);
      return data.data;
    },

    // Get receipt history
    getReceiptHistory: async (orderNumber: string): Promise<OrderReceipt[]> => {
      const encoded = encodeURIComponent(orderNumber);
      const { data } = await getApiClient().get(`/invoice-matching/orders/${encoded}/receipts`);
      return data.data;
    },

    // Backorders
    getBackorders: async (supplierCode?: string): Promise<Backorder[]> => {
      const { data } = await getApiClient().get('/invoice-matching/backorders', {
        params: supplierCode ? { supplierCode } : undefined,
      });
      return data.data;
    },

    getBackorder: async (id: string): Promise<Backorder> => {
      const { data } = await getApiClient().get(`/invoice-matching/backorders/${id}`);
      return data.data;
    },

    receiveBackorderItems: async (backorderId: string, items: BackorderReceiptInput[]): Promise<Backorder> => {
      const { data } = await getApiClient().post(`/invoice-matching/backorders/${backorderId}/receive`, { items });
      return data.data;
    },

    // Approval workflow
    submitForApproval: async (matchId: string): Promise<void> => {
      await getApiClient().post(`/invoice-matching/${matchId}/submit-approval`);
    },

    approveMatch: async (matchId: string, userId?: string): Promise<InvoiceMatch> => {
      const { data } = await getApiClient().post(`/invoice-matching/${matchId}/approve`, { userId });
      return data.data;
    },

    rejectMatch: async (matchId: string, reason: string, userId?: string): Promise<InvoiceMatch> => {
      const { data } = await getApiClient().post(`/invoice-matching/${matchId}/reject`, { reason, userId });
      return data.data;
    },

    getAwaitingApproval: async (): Promise<InvoiceMatch[]> => {
      const { data } = await getApiClient().get('/invoice-matching/awaiting-approval');
      return data.data;
    },

    // Queries
    getUnmatchedInvoices: async (): Promise<Transaction[]> => {
      const { data } = await getApiClient().get('/invoice-matching/unmatched-invoices');
      return data.data;
    },

    searchMatches: async (filters: InvoiceMatchFilters): Promise<{ items: InvoiceMatch[]; total: number }> => {
      const { data } = await getApiClient().get('/invoice-matching/search', { params: filters });
      return data.data;
    },

    getSummary: async (): Promise<InvoiceMatchingSummary> => {
      const { data } = await getApiClient().get('/invoice-matching/summary');
      return data.data;
    },
  },

  // ==================== REPORTS ====================
  reports: {
    // ---- AI report customization (Phase 6b.1) ----
    getReportSources: async (): Promise<Array<{
      key: string;
      title: string;
      fields: Array<{ name: string; label: string; type: string; defaultShown: boolean }>;
      params: Array<{ name: string; type: string; values?: string[]; required?: boolean; label?: string }>;
      groupableFields: string[];
      defaultColumnOrder: string[];
    }>> => {
      const { data } = await getApiClient().get('/reports/sources');
      return data.data;
    },

    listDefinitions: async (): Promise<ReportDefinitionRecord[]> => {
      const { data } = await getApiClient().get('/reports/definitions');
      return data.data;
    },

    getDefinition: async (id: string): Promise<ReportDefinitionRecord> => {
      const { data } = await getApiClient().get(`/reports/definitions/${id}`);
      return data.data;
    },

    createDefinition: async (payload: SaveReportDefinitionInput): Promise<ReportDefinitionRecord> => {
      const { data } = await getApiClient().post('/reports/definitions', payload);
      return data.data;
    },

    updateDefinition: async (id: string, payload: Partial<SaveReportDefinitionInput>): Promise<ReportDefinitionRecord> => {
      const { data } = await getApiClient().put(`/reports/definitions/${id}`, payload);
      return data.data;
    },

    publishDefinition: async (id: string): Promise<ReportDefinitionRecord> => {
      const { data } = await getApiClient().post(`/reports/definitions/${id}/publish`, {});
      return data.data;
    },

    unpublishDefinition: async (id: string): Promise<ReportDefinitionRecord> => {
      const { data } = await getApiClient().post(`/reports/definitions/${id}/unpublish`, {});
      return data.data;
    },

    deleteDefinition: async (id: string): Promise<void> => {
      await getApiClient().delete(`/reports/definitions/${id}`);
    },

    // --- Report Designer (banded @reportwriter/engine) templates, stored in
    //     company_settings.metadata. Backs the designer's DB ReportStore. ---
    listRwTemplates: async (kind?: string): Promise<ReportWriterTemplateRecord[]> => {
      const q = kind ? `?kind=${encodeURIComponent(kind)}` : '';
      const { data } = await getApiClient().get(`/reports/rw-templates${q}`);
      return data.data;
    },
    getRwTemplate: async (id: string): Promise<ReportWriterTemplateRecord> => {
      const { data } = await getApiClient().get(`/reports/rw-templates/${id}`);
      return data.data;
    },
    saveRwTemplate: async (payload: { id?: string; kind: string; name: string; definition: unknown }): Promise<ReportWriterTemplateRecord> => {
      const { data } = await getApiClient().post('/reports/rw-templates', payload);
      return data.data;
    },
    deleteRwTemplate: async (id: string): Promise<void> => {
      await getApiClient().delete(`/reports/rw-templates/${id}`);
    },
    // Default banded template per report kind (overrides the stock report output).
    getRwTemplateDefaults: async (): Promise<Record<string, string>> => {
      const { data } = await getApiClient().get('/reports/rw-templates/defaults');
      return data.data;
    },
    setRwTemplateDefault: async (kind: string, templateId: string): Promise<Record<string, string>> => {
      const { data } = await getApiClient().put(`/reports/rw-templates/defaults/${encodeURIComponent(kind)}`, { templateId });
      return data.data;
    },
    clearRwTemplateDefault: async (kind: string): Promise<Record<string, string>> => {
      const { data } = await getApiClient().delete(`/reports/rw-templates/defaults/${encodeURIComponent(kind)}`);
      return data.data;
    },

    renderDefinitionPdf: async (id: string, filters?: Record<string, string | number | boolean>): Promise<Blob> => {
      // ETag/IndexedDB cached: a 304 (unchanged data) serves the stored blob.
      return cachedBlobPost(getApiClient(), `/reports/definitions/${id}/render-pdf`, filters ? { filters } : {}, `defpdf:${id}:${stableKey(filters)}`);
    },

    renderDefinitionExcel: async (id: string, filters?: Record<string, string | number | boolean>): Promise<Blob> => {
      return cachedBlobPost(getApiClient(), `/reports/definitions/${id}/render-excel`, filters ? { filters } : {}, `defxlsx:${id}:${stableKey(filters)}`);
    },

    previewDefinition: async (definition: ReportDefinition): Promise<Blob> => {
      const { data } = await getApiClient().post('/reports/preview', { definition }, { responseType: 'blob' });
      return data;
    },

    getReportBranding: async (): Promise<ReportBrandingDefaults> => {
      const { data } = await getApiClient().get('/reports/branding');
      return data.data;
    },

    setReportBranding: async (branding: ReportBrandingDefaults): Promise<ReportBrandingDefaults> => {
      const { data } = await getApiClient().put('/reports/branding', branding);
      return data.data;
    },

    // Default published layout per report base (catalogue key → definition id).
    getReportDefaults: async (): Promise<Record<string, string>> => {
      const { data } = await getApiClient().get('/reports/defaults');
      return data.data;
    },

    setReportDefault: async (base: string, definitionId: string): Promise<Record<string, string>> => {
      const { data } = await getApiClient().put(`/reports/defaults/${encodeURIComponent(base)}`, { definitionId });
      return data.data;
    },

    clearReportDefault: async (base: string): Promise<Record<string, string>> => {
      const { data } = await getApiClient().delete(`/reports/defaults/${encodeURIComponent(base)}`);
      return data.data;
    },

    // User-created Reports-tab categories + report→category assignments. Array order
    // is the display order; the whole config is read/written in one call.
    getReportCategories: async (): Promise<{ categories: { id: string; label: string }[]; assignments: Record<string, string>; order: string[] }> => {
      const { data } = await getApiClient().get('/reports/report-categories');
      return data.data;
    },

    setReportCategories: async (payload: { categories: { id: string; label: string }[]; assignments: Record<string, string>; order: string[] }): Promise<{ categories: { id: string; label: string }[]; assignments: Record<string, string>; order: string[] }> => {
      const { data } = await getApiClient().put('/reports/report-categories', payload);
      return data.data;
    },

    // ---- Tier B query blocks (Phase 2) — owner/admin only ----
    getQueryBlockSchema: async (): Promise<Array<{ table: string; columns: Array<{ name: string; type: string }> }>> => {
      const { data } = await getApiClient().get('/reports/query-blocks/schema');
      return data.data;
    },

    listQueryBlocks: async (): Promise<QueryBlockRecord[]> => {
      const { data } = await getApiClient().get('/reports/query-blocks');
      return data.data;
    },

    createQueryBlock: async (payload: CreateQueryBlockInput): Promise<QueryBlockRecord> => {
      const { data } = await getApiClient().post('/reports/query-blocks', payload);
      return data.data;
    },

    updateQueryBlock: async (key: string, payload: UpdateQueryBlockInput): Promise<QueryBlockRecord> => {
      const { data } = await getApiClient().put(`/reports/query-blocks/${encodeURIComponent(key)}`, payload);
      return data.data;
    },

    previewQueryBlock: async (
      sqlTemplate: string,
      params: Array<string | number | boolean | null> = [],
    ): Promise<{ rows: Array<Record<string, unknown>>; columns: string[]; rowCount: number; truncated: boolean }> => {
      const { data } = await getApiClient().post('/reports/query-blocks/preview', { sqlTemplate, params });
      return data.data;
    },

    approveQueryBlock: async (key: string): Promise<QueryBlockRecord> => {
      const { data } = await getApiClient().post(`/reports/query-blocks/${encodeURIComponent(key)}/approve`, {});
      return data.data;
    },

    unapproveQueryBlock: async (key: string): Promise<QueryBlockRecord> => {
      const { data } = await getApiClient().post(`/reports/query-blocks/${encodeURIComponent(key)}/unapprove`, {});
      return data.data;
    },

    deleteQueryBlock: async (key: string): Promise<void> => {
      await getApiClient().delete(`/reports/query-blocks/${encodeURIComponent(key)}`);
    },

    // Job List Reports
    getJobListSummary: async (jobNos?: string[]): Promise<Array<{
      jobNo: string;
      clientName: string | null;
      siteAddress: string | null;
      status: string;
      contractPrice: number;
      startDate: string | null;
    }>> => {
      const params = jobNos?.length ? { jobNos: jobNos.join(',') } : undefined;
      const { data } = await getApiClient().get('/reports/job-list/summary', { params });
      return data.data;
    },

    getJobListDetailed: async (jobNos?: string[]): Promise<Array<{
      jobNo: string;
      name: string | null;
      clientName: string | null;
      siteAddress: string | null;
      status: string;
      contractPrice: number;
      contractGst: number;
      startDate: string | null;
      endDate: string | null;
    }>> => {
      const params = jobNos?.length ? { jobNos: jobNos.join(',') } : undefined;
      const { data } = await getApiClient().get('/reports/job-list/detailed', { params });
      return data.data;
    },

    // Job Costing Reports
    getCostCentres: async (jobNo: string, options?: {
      includeAll?: boolean;
      includeLive?: boolean;
      includeBudgeted?: boolean;
    }): Promise<Array<{
      code: string;
      name: string;
      budget: number;
      ordered: number;
      invoiced: number;
      forecast: number;
      variance: number;
      percentComplete: number;
    }>> => {
      const { data } = await getApiClient().get(`/reports/job-costing/${jobNo}/cost-centres`, { params: options });
      return data.data;
    },

    getBudgets: async (jobNo: string): Promise<{
      summary: {
        totalBudget: number;
        totalOrdered: number;
        totalInvoiced: number;
        totalForecast: number;
        totalVariance: number;
        overallPercent: number;
      };
      costCentres: Array<{
        code: string;
        name: string;
        budget: number;
        ordered: number;
        invoiced: number;
        forecast: number;
        variance: number;
        overRun: number;
        percentComplete: number;
      }>;
    }> => {
      const { data } = await getApiClient().get(`/reports/job-costing/${jobNo}/budgets`);
      return data.data;
    },

    getLookingForTrouble: async (jobNo: string): Promise<Array<{
      code: string;
      name: string;
      budget: number;
      invoiced: number;
      variance: number;
      percentOver: number;
    }>> => {
      const { data } = await getApiClient().get(`/reports/job-costing/${jobNo}/looking-for-trouble`);
      return data.data;
    },

    getUnpaidInvoices: async (jobNo: string, options?: {
      fromDate?: string;
      toDate?: string;
    }): Promise<Array<{
      tranNo: number;
      tranDate: string;
      supplierCode: string;
      supplierName: string;
      reference: string | null;
      amount: number;
      costCentreCode: string | null;
      daysPastDue: number;
    }>> => {
      const { data } = await getApiClient().get(`/reports/job-costing/${jobNo}/unpaid-invoices`, { params: options });
      return data.data;
    },

    // Debtors Reports
    getAgedDebtors: async (options?: {
      byDueDate?: boolean;
      jobNo?: string;
      asAtDate?: string;
    }): Promise<Array<{
      jobNo: string;
      clientName: string | null;
      siteAddress: string | null;
      jobName: string | null;
      status: string | null;
      current: number;
      days30: number;
      days60: number;
      days90Plus: number;
      total: number;
    }>> => {
      const { data } = await getApiClient().get('/reports/debtors/aged', { params: options });
      return data.data;
    },

    // The individual outstanding claims behind the aged-debtors totals (sub-report drill-down).
    getAgedDebtorsDetail: async (options?: {
      byDueDate?: boolean;
      jobNo?: string;
      asAtDate?: string;
    }): Promise<Array<{ jobNo: string; claimNo: number; claimDate: string | null; amount: number; bucket: string }>> => {
      const { data } = await getApiClient().get('/reports/debtors/aged/detail', { params: options });
      return data.data;
    },

    getDebtorStatement: async (jobNo: string, options?: {
      fromDate?: string;
      toDate?: string;
    }): Promise<{
      job: {
        jobNo: string;
        clientName: string | null;
        siteAddress: string | null;
      };
      transactions: Array<{
        tranNo: number;
        tranDate: string;
        type: 'CLM' | 'REC';
        reference: string | null;
        description: string | null;
        debit: number;
        credit: number;
        balance: number;
      }>;
      totals: {
        totalClaims: number;
        totalReceipts: number;
        balance: number;
      };
    }> => {
      const { data } = await getApiClient().get(`/reports/debtors/${jobNo}/statement`, { params: options });
      return data.data;
    },

    getContractReport: async (jobNo: string): Promise<{
      job: {
        jobNo: string;
        name: string | null;
        clientName: string | null;
        siteAddress: string | null;
      };
      contract: {
        originalExGst: number;
        originalIncGst: number;
        variationsExGst: number;
        variationsIncGst: number;
        amendedExGst: number;
        amendedIncGst: number;
      };
      variations: Array<{
        tranNo: number;
        tranDate: string;
        reference: string | null;
        description: string | null;
        amountExGst: number;
        amountIncGst: number;
      }>;
      claims: {
        claimedExGst: number;
        claimedIncGst: number;
        receivedIncGst: number;
        outstandingIncGst: number;
      };
    }> => {
      const { data } = await getApiClient().get(`/reports/debtors/${jobNo}/contract`);
      return data.data;
    },

    // Financial Reports
    getRetentionLedger: async (options?: {
      asAtDate?: string;
      jobNo?: string;
      type?: 'receivable' | 'payable' | 'all';
    }): Promise<{
      data: Array<{
        jobNo: string;
        jobName: string | null;
        counterpartyName: string | null;
        counterpartyCode: string | null;
        type: 'Receivable' | 'Payable';
        tranNo: number;
        tranDate: string;
        reference: string | null;
        retentionAmount: number;
        retentionDueDate: string | null;
        daysUntilDue: number | null;
        isOverdue: boolean;
      }>;
      summary: {
        receivable: { total: number; overdue: number; count: number };
        payable: { total: number; overdue: number; count: number };
      };
    }> => {
      const { data } = await getApiClient().get('/reports/financial/retention-ledger', { params: options });
      return { data: data.data, summary: data.summary };
    },

    generateRetentionLedgerPDF: async (options?: {
      asAtDate?: string;
      jobNo?: string;
      type?: 'receivable' | 'payable' | 'all';
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/financial/retention-ledger/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    getAgedPayables: async (options?: {
      asAtDate?: string;
      supplierCode?: string;
      jobNo?: string;
      byDueDate?: boolean;
    }): Promise<{
      data: Array<{
        supplierCode: string;
        supplierName: string | null;
        current: number;
        days30: number;
        days60: number;
        days90Plus: number;
        total: number;
      }>;
      summary: {
        current: number;
        days30: number;
        days60: number;
        days90Plus: number;
        total: number;
        supplierCount: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/financial/aged-payables', { params: options });
      return { data: data.data, summary: data.summary };
    },

    generateAgedPayablesPDF: async (options?: {
      asAtDate?: string;
      supplierCode?: string;
      jobNo?: string;
      byDueDate?: boolean;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/financial/aged-payables/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    getSupplierCompliance: async (options?: {
      includeCompliant?: boolean;
      expiringWithinDays?: number;
    }): Promise<{
      data: Array<{
        supplierCode: string;
        supplierName: string;
        phone: string | null;
        email: string | null;
        hasBankDetails: boolean;
        abn: string | null;
        abnStatus: 'valid' | 'invalid' | 'missing';
        isGstRegistered: boolean;
        insuranceStatus: 'compliant' | 'expired' | 'expiring' | 'missing';
        insuranceDetails: Array<{
          type: string;
          policyNumber: string | null;
          expiryDate: string | null;
          isExpired: boolean;
          isExpiring: boolean;
          daysUntilExpiry: number | null;
        }>;
        issues: string[];
      }>;
      summary: {
        total: number;
        compliant: number;
        nonCompliant: number;
        expiredInsurance: number;
        expiringInsurance: number;
        missingBankDetails: number;
        missingInsurance: number;
        invalidAbn: number;
        missingAbn: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/financial/supplier-compliance', { params: options });
      return { data: data.data, summary: data.summary };
    },

    generateSupplierCompliancePDF: async (options?: {
      includeCompliant?: boolean;
      expiringWithinDays?: number;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/financial/supplier-compliance/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    // Cash Flow Forecast
    getCashFlowForecast: async (options?: {
      periodType?: 'weekly' | 'monthly';
      periodCount?: number;
      startDate?: string;
      jobNo?: string;
      includeDetail?: boolean;
    }): Promise<{
      periods: Array<{
        periodStart: string;
        periodEnd: string;
        periodLabel: string;
        receivables: number;
        retentionReceivable: number;
        totalInflows: number;
        payables: number;
        commitments: number;
        scheduledPayments: number;
        retentionPayable: number;
        totalOutflows: number;
        netCashFlow: number;
      }>;
      summary: {
        totalReceivables: number;
        totalPayables: number;
        totalCommitments: number;
        totalScheduledPayments: number;
        totalRetentionReceivable: number;
        totalRetentionPayable: number;
        netPosition: number;
        periodCount: number;
      };
      asAtDate: string;
      periodType: 'weekly' | 'monthly';
      jobNo?: string;
    }> => {
      const { data } = await getApiClient().get('/reports/financial/cash-flow-forecast', { params: options });
      return data.data;
    },

    generateCashFlowForecastPDF: async (options?: {
      periodType?: 'weekly' | 'monthly';
      periodCount?: number;
      startDate?: string;
      jobNo?: string;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/financial/cash-flow-forecast/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    // WIP Report
    getWipReport: async (options?: {
      jobNo?: string;
      includeCompleted?: boolean;
      status?: string;
    }): Promise<{
      data: Array<{
        jobNo: string;
        jobName: string;
        siteAddress: string | null;
        clientName: string | null;
        status: string;
        contractValue: number;
        costsIncurred: number;
        revenueClaimed: number;
        wipBalance: number;
        percentComplete: number;
        percentClaimed: number;
        variance: number;
      }>;
      summary: {
        totalJobs: number;
        totalContractValue: number;
        totalCostsIncurred: number;
        totalRevenueClaimed: number;
        totalWipBalance: number;
        avgPercentComplete: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/project/wip', { params: options });
      return data;
    },

    // Profitability Report
    getProfitabilityReport: async (options?: {
      jobNo?: string;
      includeCompleted?: boolean;
      status?: string;
      sortBy?: 'jobNo' | 'grossProfit' | 'grossMargin' | 'revenue';
      sortOrder?: 'asc' | 'desc';
    }): Promise<{
      data: Array<{
        jobNo: string;
        jobName: string;
        siteAddress: string | null;
        clientName: string | null;
        status: string;
        contractValue: number;
        budgetCost: number;
        costsIncurred: number;
        revenueClaimed: number;
        grossProfit: number;
        grossMarginPercent: number;
        budgetVariance: number;
        budgetVariancePercent: number;
        costToComplete: number;
        projectedProfit: number;
        projectedMarginPercent: number;
      }>;
      summary: {
        totalJobs: number;
        totalContractValue: number;
        totalBudgetCost: number;
        totalCostsIncurred: number;
        totalRevenueClaimed: number;
        totalGrossProfit: number;
        avgGrossMarginPercent: number;
        totalBudgetVariance: number;
        totalCostToComplete: number;
        totalProjectedProfit: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/project/profitability', { params: options });
      return data;
    },

    // Orders Reports
    getOrdersProfile: async (jobNo: string, options?: {
      fromDate?: string;
      toDate?: string;
      status?: string;
    }): Promise<Array<{
      orderNumber: string;
      orderDate: string;
      supplierCode: string;
      supplierName: string;
      costCentreCode: string;
      costCentreName: string;
      amount: number;
      status: string;
      isFullyInvoiced: boolean;
    }>> => {
      const { data } = await getApiClient().get(`/reports/orders/${jobNo}/profile`, { params: options });
      return data.data;
    },

    getOrderStatuses: async (): Promise<string[]> => {
      const { data } = await getApiClient().get('/reports/orders/statuses');
      return data.data;
    },

    getOverRuns: async (options?: {
      jobNo?: string;
      groupBy?: 'job' | 'costCentre' | 'supplier';
    }): Promise<Array<{
      jobNo?: string;
      costCentreCode?: string;
      costCentreName?: string;
      supplierCode?: string;
      supplierName?: string;
      orderNumber: string;
      budgetAmount: number;
      orderAmount: number;
      overRunAmount: number;
      cause: string | null;
    }>> => {
      const { data } = await getApiClient().get('/reports/orders/over-runs', { params: options });
      return data.data;
    },

    getUnmatchedBoqItems: async (options?: {
      jobNo?: string;
      unmatchedType?: 'all' | 'no-catalogue' | 'price-mismatch';
    }): Promise<{
      data: Array<{
        jobNo: string;
        jobName: string;
        costCentreCode: string;
        costCentreName: string;
        bLoad: number;
        lineNumber: number;
        priceCode: string | null;
        description: string | null;
        quantity: number;
        unitPrice: number;
        lineTotal: number;
        unitCode: string | null;
        unmatchedReason: string;
        catalogueItemId: string | null;
      }>;
      summary: {
        totalItems: number;
        totalValue: number;
        itemsNoCatalogue: number;
        itemsPriceMismatch: number;
        jobsAffected: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/procurement/unmatched-boq', { params: options });
      return { data: data.data, summary: data.summary };
    },

    // Price Movement Report
    getPriceMovementReport: async (options?: {
      priceLevel?: number;
      fromDate?: string;
      toDate?: string;
      costCentreId?: string;
      searchTerm?: string;
    }): Promise<{
      data: Array<{
        catalogueItemId: string;
        priceCode: string;
        description: string;
        unitCode: string | null;
        costCentreCode: string | null;
        costCentreName: string | null;
        priceLevel: number;
        priceLevelName: string;
        effectiveDate: string;
        previousPrice: number | null;
        currentPrice: number;
        priceChange: number | null;
        percentChange: number | null;
        changeReason: string | null;
        isManualOverride: boolean;
        isRecipe: boolean;
      }>;
      summary: {
        totalChanges: number;
        totalItemsAffected: number;
        avgPriceIncrease: number;
        avgPercentChange: number;
        priceIncreases: number;
        priceDecreases: number;
        manualOverrides: number;
        itemChanges: number;
        recipeChanges: number;
        byLevel: Array<{
          level: number;
          levelName: string;
          changes: number;
          avgChange: number;
        }>;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/estimating/price-movement', { params: options });
      return { data: data.data, summary: data.summary };
    },

    generatePriceMovementPDF: async (options?: {
      priceLevel?: number;
      fromDate?: string;
      toDate?: string;
      costCentreId?: string;
      searchTerm?: string;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/estimating/price-movement/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    // Recipe Margin Analysis Report
    getRecipeMarginReport: async (options?: {
      priceLevel?: number;
      costCentreId?: string;
      minMarginPercent?: number;
      maxMarginPercent?: number;
      searchTerm?: string;
      includeNegativeMargin?: boolean;
    }): Promise<{
      data: Array<{
        recipeId: string;
        priceCode: string;
        description: string;
        costCentreCode: string | null;
        costCentreName: string | null;
        unitCode: string | null;
        priceLevel: number;
        priceLevelName: string;
        recipePrice: number;
        componentCount: number;
        totalComponentCost: number;
        margin: number;
        marginPercent: number;
        hasSubRecipes: boolean;
        lastPriceUpdate: string | null;
        components: Array<{
          priceCode: string;
          description: string;
          quantity: number;
          unitPrice: number;
          lineCost: number;
          isRecipe: boolean;
        }>;
      }>;
      summary: {
        totalRecipes: number;
        totalRecipeValue: number;
        totalComponentCost: number;
        totalMargin: number;
        avgMarginPercent: number;
        recipesWithPositiveMargin: number;
        recipesWithNegativeMargin: number;
        recipesWithZeroMargin: number;
        avgComponentsPerRecipe: number;
      };
    }> => {
      const { data } = await getApiClient().get('/reports/estimating/recipe-margin', { params: options });
      return { data: data.data, summary: data.summary };
    },

    generateRecipeMarginPDF: async (options?: {
      priceLevel?: number;
      costCentreId?: string;
      minMarginPercent?: number;
      maxMarginPercent?: number;
      searchTerm?: string;
      includeNegativeMargin?: boolean;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/estimating/recipe-margin/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    generateUnmatchedBoqPDF: async (options?: {
      jobNo?: string;
      unmatchedType?: 'all' | 'no-catalogue' | 'price-mismatch';
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/procurement/unmatched-boq/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    // PDF Generation
    generatePDF: async (html: string, options?: {
      filename?: string;
      landscape?: boolean;
    }, cacheKey?: string): Promise<Blob> => {
      // When a stable cacheKey is supplied (e.g. report+filters), route through the
      // ETag/IndexedDB cache so an unchanged render is served locally (server 304s).
      if (cacheKey) {
        return cachedBlobPost(getApiClient(), '/reports/generate-pdf', { html, options }, cacheKey);
      }
      const response = await getApiClient().post('/reports/generate-pdf', { html, options }, {
        responseType: 'blob',
      });
      return response.data;
    },

    // Excel Generation
    generateExcel: async (options: {
      title: string;
      subtitle?: string;
      columns: Array<{
        header: string;
        key: string;
        width?: number;
        type?: 'text' | 'number' | 'currency' | 'percentage' | 'date' | 'boolean';
      }>;
      data: Record<string, unknown>[];
      filename?: string;
      sheetName?: string;
      includeTotals?: boolean;
      totalColumns?: string[];
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/generate-excel', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    generateCostCentresPDF: async (jobNo: string, options?: {
      includeAll?: boolean;
      includeLive?: boolean;
      includeBudgeted?: boolean;
      jobName?: string;
      clientName?: string;
    }): Promise<Blob> => {
      const response = await getApiClient().post(`/reports/job-costing/${jobNo}/cost-centres/pdf`, options, {
        responseType: 'blob',
      });
      return response.data;
    },

    generateAgedDebtorsPDF: async (options?: {
      byDueDate?: boolean;
      jobNo?: string;
    }): Promise<Blob> => {
      const response = await getApiClient().post('/reports/debtors/aged/pdf', options, {
        responseType: 'blob',
      });
      return response.data;
    },

    generateStatementPDF: async (jobNo: string, options?: {
      fromDate?: string;
      toDate?: string;
    }): Promise<Blob> => {
      const response = await getApiClient().post(`/reports/debtors/${jobNo}/statement/pdf`, options, {
        responseType: 'blob',
      });
      return response.data;
    },
  },

  jobCommunications: {
    getByJob: async (jobNo: string, options?: { communicationType?: string }): Promise<JobCommunication[]> => {
      const { data } = await getApiClient().get(`/jobs/${jobNo}/communications`, { params: options });
      return data;
    },

    create: async (jobNo: string, commData: Partial<JobCommunication>): Promise<JobCommunication> => {
      const { data } = await getApiClient().post(`/jobs/${jobNo}/communications`, commData);
      return data;
    },

    update: async (jobNo: string, id: string, commData: Partial<JobCommunication>): Promise<JobCommunication> => {
      const { data } = await getApiClient().patch(`/jobs/${jobNo}/communications/${id}`, commData);
      return data;
    },

    delete: async (jobNo: string, id: string): Promise<void> => {
      await getApiClient().delete(`/jobs/${jobNo}/communications/${id}`);
    },

    toggleFlag: async (jobNo: string, id: string): Promise<{ id: string; isFlagged: boolean }> => {
      const { data } = await getApiClient().patch(`/jobs/${jobNo}/communications/${id}/flag`);
      return data;
    },
  },

  contractClaims: {
    // Templates
    getTemplates: async (): Promise<ClaimTemplate[]> => {
      const { data } = await getApiClient().get('/contract-claims/templates');
      return data;
    },

    getTemplate: async (id: string): Promise<ClaimTemplate> => {
      const { data } = await getApiClient().get(`/contract-claims/templates/${id}`);
      return data;
    },

    createTemplate: async (templateData: CreateClaimTemplateData): Promise<ClaimTemplate> => {
      const { data } = await getApiClient().post('/contract-claims/templates', templateData);
      return data;
    },

    updateTemplate: async (id: string, updates: UpdateClaimTemplateData): Promise<ClaimTemplate> => {
      const { data } = await getApiClient().patch(`/contract-claims/templates/${id}`, updates);
      return data;
    },

    deleteTemplate: async (id: string): Promise<void> => {
      await getApiClient().delete(`/contract-claims/templates/${id}`);
    },

    // Schedule
    getSchedule: async (jobNo: string): Promise<ClaimScheduleStage[]> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/schedule`);
      return data;
    },

    applyTemplate: async (jobNo: string, templateId: string): Promise<ClaimScheduleStage[]> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/apply-template`, { templateId });
      return data;
    },

    updateStage: async (jobNo: string, stageNumber: number, updates: Partial<ClaimScheduleStage>): Promise<ClaimScheduleStage> => {
      const { data } = await getApiClient().patch(`/contract-claims/jobs/${jobNo}/schedule/${stageNumber}`, updates);
      return data;
    },

    markStageReady: async (jobNo: string, stageNumber: number, ready: boolean): Promise<ClaimScheduleStage> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/schedule/${stageNumber}/ready`, { ready });
      return data;
    },

    closeStage: async (jobNo: string, stageNumber: number, options?: { tranNo?: number; comments?: string }): Promise<ClaimScheduleStage> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/schedule/${stageNumber}/close`, options || {});
      return data;
    },

    recalculateAmounts: async (jobNo: string): Promise<ClaimScheduleStage[]> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/recalculate`);
      return data;
    },

    // Summary
    getSummary: async (jobNo: string): Promise<ContractSummary> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/summary`);
      return data;
    },

    // Claims
    postClaim: async (jobNo: string, stageNumber: number): Promise<ContractTransaction> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/claims/${stageNumber}/post`);
      return data;
    },

    unpostClaim: async (jobNo: string, stageNumber: number): Promise<ClaimScheduleStage> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/claims/${stageNumber}/unpost`);
      return data;
    },

    getClaims: async (jobNo: string): Promise<PostedClaim[]> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/claims`);
      return data;
    },

    // Variations
    createVariation: async (jobNo: string, variationData: CreateVariationData): Promise<ContractTransaction> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/variations`, variationData);
      return data;
    },

    getVariations: async (jobNo: string): Promise<ContractVariation[]> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/variations`);
      return data;
    },

    authoriseVariation: async (tranNo: number): Promise<ContractTransaction> => {
      const { data } = await getApiClient().post(`/contract-claims/variations/${tranNo}/authorise`);
      return data;
    },

    // Variation Orders (enhanced)
    variationOrders: {
      list: async (jobNo: string) => {
        const { data } = await getApiClient().get(`/contract-claims/variation-orders/jobs/${jobNo}`);
        return data.data;
      },
      get: async (id: string) => {
        const { data } = await getApiClient().get(`/contract-claims/variation-orders/${id}`);
        return data.data;
      },
      create: async (voData: Record<string, unknown>) => {
        const { data } = await getApiClient().post('/contract-claims/variation-orders', voData);
        return data.data;
      },
      update: async (id: string, updates: Record<string, unknown>) => {
        const { data } = await getApiClient().patch(`/contract-claims/variation-orders/${id}`, updates);
        return data.data;
      },
      delete: async (id: string) => {
        await getApiClient().delete(`/contract-claims/variation-orders/${id}`);
      },
      addLine: async (voId: string, lineData: Record<string, unknown>) => {
        const { data } = await getApiClient().post(`/contract-claims/variation-orders/${voId}/lines`, lineData);
        return data.data;
      },
      updateLine: async (voId: string, lineId: string, updates: Record<string, unknown>) => {
        const { data } = await getApiClient().patch(`/contract-claims/variation-orders/${voId}/lines/${lineId}`, updates);
        return data.data;
      },
      removeLine: async (voId: string, lineId: string) => {
        await getApiClient().delete(`/contract-claims/variation-orders/${voId}/lines/${lineId}`);
      },
      submit: async (id: string) => {
        const { data } = await getApiClient().post(`/contract-claims/variation-orders/${id}/submit`);
        return data.data;
      },
      authorise: async (id: string, authorisedBy: string) => {
        const { data } = await getApiClient().post(`/contract-claims/variation-orders/${id}/authorise`, { authorisedBy });
        return data.data;
      },
      reject: async (id: string, reason?: string) => {
        const { data } = await getApiClient().post(`/contract-claims/variation-orders/${id}/reject`, { reason });
        return data.data;
      },
      okToClaim: async (id: string) => {
        const { data } = await getApiClient().post(`/contract-claims/variation-orders/${id}/ok-to-claim`);
        return data.data;
      },
      downloadPDF: async (id: string) => {
        const response = await getApiClient().get(`/contract-claims/variation-orders/${id}/pdf`, { responseType: 'blob' });
        return response.data;
      },
    },

    // Receipts
    recordReceipt: async (jobNo: string, receiptData: RecordReceiptData): Promise<ContractTransaction> => {
      const { data } = await getApiClient().post(`/contract-claims/jobs/${jobNo}/receipts`, receiptData);
      return data;
    },

    getReceipts: async (jobNo: string): Promise<ContractReceipt[]> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/receipts`);
      return data;
    },

    // All Transactions
    getAllTransactions: async (jobNo: string): Promise<ContractTransaction[]> => {
      const { data } = await getApiClient().get(`/contract-claims/jobs/${jobNo}/transactions`);
      return data;
    },
  },

  // Document Management API
  documents: {
    // Documents CRUD
    getList: async (params?: {
      search?: string;
      status?: string;
      discipline?: string;
      collectionId?: string;
      entityType?: string;
      entityId?: string;
      jobId?: string;
      latestOnly?: boolean;
      tags?: string[];
      page?: number;
      limit?: number;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
    }): Promise<{ documents: Document[]; total: number }> => {
      const { data } = await getApiClient().get('/documents', { params });
      return data;
    },

    search: async (params: {
      q: string;
      entityType?: string;
      entityId?: string;
      jobId?: string;
      status?: string;
      discipline?: string;
      collectionId?: string;
      projectId?: string;
      tags?: string[];
      latestOnly?: boolean;
      limit?: number;
      offset?: number;
    }): Promise<{ documents: Document[]; total: number }> => {
      const { data } = await getApiClient().get('/documents/search', { params });
      return data.data || data;
    },

    copy: async (id: string): Promise<{ id: string; fileName: string }> => {
      const { data } = await getApiClient().post(`/documents/${id}/copy`);
      return data.data || data;
    },

    setPortalVisibilityBulk: async (documentIds: string[], visible: boolean): Promise<{ updated: number; skipped: Array<{ id: string; fileName: string; reason: string }> }> => {
      const { data } = await getApiClient().patch('/documents/portal-visibility-bulk', { documentIds, visible });
      return data;
    },

    setStarred: async (id: string, starred: boolean): Promise<void> => {
      if (starred) await getApiClient().post(`/documents/${id}/star`);
      else await getApiClient().delete(`/documents/${id}/star`);
    },

    downloadZip: async (input: { collectionId?: string; documentIds?: string[] }): Promise<Blob> => {
      const { data } = await getApiClient().post('/documents/download-zip', input, { responseType: 'blob' });
      return data;
    },

    getThumbnail: async (id: string): Promise<Blob | null> => {
      try {
        const { data } = await getApiClient().get(`/documents/${id}/thumbnail`, { responseType: 'blob' });
        return data;
      } catch {
        return null; // non-image or missing — caller falls back to an icon
      }
    },

    getDocument: async (id: string): Promise<Document> => {
      const { data } = await getApiClient().get(`/documents/${id}`);
      // The route wraps in { success, data } — returning the envelope handed
      // callers a "document" with no fileName/id (broke transmittal preview).
      return data.data ?? data;
    },

    // Share / unshare a document with the client portal.
    setPortalVisibility: async (id: string, visible: boolean): Promise<void> => {
      await getApiClient().patch(`/documents/${id}/portal-visibility`, { visible });
    },

    // Measurement scale (per drawing page), stored in the document's metadata.
    setCalibration: async (
      id: string,
      page: number,
      calibration: { unitsPerPixel: number; unit: string } | null,
    ): Promise<void> => {
      const body = calibration === null ? { page, clear: true } : { page, ...calibration };
      await getApiClient().patch(`/documents/${id}/calibration`, body);
    },
    getCalibration: async (
      id: string,
    ): Promise<Record<string, { unitsPerPixel: number; unit: string }>> => {
      const { data } = await getApiClient().get(`/documents/${id}/calibration`);
      return (data?.data ?? {}) as Record<string, { unitsPerPixel: number; unit: string }>;
    },

    upload: async (
      file: File,
      metadata: Partial<CreateDocumentData>,
      onProgress?: (percent: number) => void
    ): Promise<Document> => {
      const formData = new FormData();
      formData.append('file', file);
      Object.entries(metadata).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });

      // For FormData uploads, use a fresh axios instance without default Content-Type
      // This ensures axios properly detects FormData and sets multipart/form-data with boundary
      const token = localStorage.getItem('accessToken');
      const response = await axios.post(`${API_BASE_URL}/documents`, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      return response.data.data || response.data;
    },

    update: async (id: string, updates: UpdateDocumentData): Promise<Document> => {
      const { data } = await getApiClient().patch(`/documents/${id}`, updates);
      return data;
    },

    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/documents/${id}`);
    },

    /** Which of these documents are protected from deletion (on issued transmittals). */
    checkDelete: async (documentIds: string[]): Promise<{
      protected: Array<{ documentId: string; fileName: string; transmittalNumber: string; status: string }>;
    }> => {
      const { data } = await getApiClient().post('/documents/check-delete', { documentIds });
      return data.data || { protected: [] };
    },

    /** The workspace's document naming convention (null when not set). */
    getNamingConvention: async (): Promise<{ convention: string | null }> => {
      const { data } = await getApiClient().get('/documents/naming-convention');
      return data.data || { convention: null };
    },

    /** Save/clear the naming convention (owner/admin). Empty string clears. */
    setNamingConvention: async (convention: string): Promise<{ convention: string | null }> => {
      const { data } = await getApiClient().put('/documents/naming-convention', { convention });
      return data.data || { convention: null };
    },

    /** Which SHA-256 hashes already exist as live documents (identical content). */
    checkDuplicates: async (hashes: string[]): Promise<{
      matches: Array<{ hash: string; documentId: string; fileName: string; versionNumber: number }>;
    }> => {
      const { data } = await getApiClient().post('/documents/check-duplicates', { hashes });
      return data.data || { matches: [] };
    },

    download: async (id: string): Promise<Blob> => {
      const { data } = await getApiClient().get(`/documents/${id}/download`, {
        responseType: 'blob',
        timeout: 120000, // 2 minutes for large files on slow connections
      });
      return data;
    },

    getDownloadUrl: async (id: string): Promise<{ url: string; expiresAt: string }> => {
      const { data } = await getApiClient().get(`/documents/${id}/download-url`);
      return data;
    },

    // Versions
    getVersions: async (documentId: string): Promise<DocumentVersion[]> => {
      const { data } = await getApiClient().get(`/documents/${documentId}/versions`);
      return data.data || [];
    },

    uploadNewVersion: async (
      documentId: string,
      file: File,
      options?: { versionType?: string; changeNotes?: string; revisionCode?: string; copyMarkups?: boolean },
      onProgress?: (percent: number) => void
    ): Promise<Document> => {
      const formData = new FormData();
      formData.append('file', file);
      if (options?.versionType) formData.append('versionType', options.versionType);
      if (options?.changeNotes) formData.append('changeNotes', options.changeNotes);
      if (options?.revisionCode) formData.append('revisionCode', options.revisionCode);
      // Only send when opting OUT — backend defaults to carrying markups forward.
      if (options?.copyMarkups === false) formData.append('copyMarkups', 'false');

      const { data } = await getApiClient().post(`/documents/${documentId}/versions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
      });
      return data;
    },

    restoreVersion: async (documentId: string, versionNumber: number): Promise<Document> => {
      const { data } = await getApiClient().post(
        `/documents/${documentId}/versions/${versionNumber}/restore`
      );
      return data;
    },

    uploadHistoricalVersion: async (
      documentId: string,
      file: File,
      options: {
        versionNumber: number;
        revisionCode?: string;
        changeNotes?: string;
      }
    ): Promise<Document> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('versionNumber', options.versionNumber.toString());
      formData.append('isHistorical', 'true');
      if (options.revisionCode) formData.append('revisionCode', options.revisionCode);
      if (options.changeNotes) formData.append('changeNotes', options.changeNotes);

      const { data } = await getApiClient().post(`/documents/${documentId}/versions`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data;
    },

    // Tags
    getTags: async (): Promise<string[]> => {
      const { data } = await getApiClient().get('/documents/tags');
      return data.data || data;
    },

    // Collections
    reorderCollections: async (orderedIds: string[]): Promise<void> => {
      await getApiClient().post('/documents/collections/reorder', { orderedIds });
    },
    getCollections: async (options?: {
      jobId?: string;
      projectId?: string;
      scope?: string;
      tree?: boolean;
    }): Promise<DocumentCollection[]> => {
      const params: Record<string, unknown> = {};
      if (options?.jobId) params.jobId = options.jobId;
      if (options?.projectId) params.projectId = options.projectId;
      if (options?.scope) params.scope = options.scope;
      if (options?.tree) params.tree = 'true';
      const { data } = await getApiClient().get('/documents/collections', { params });
      // API returns { success: true, data: [...] }
      return data.data || data;
    },

    createCollection: async (collection: CreateCollectionData): Promise<DocumentCollection> => {
      const { data } = await getApiClient().post('/documents/collections', collection);
      return data;
    },

    updateCollection: async (
      id: string,
      updates: UpdateCollectionData
    ): Promise<DocumentCollection> => {
      const { data } = await getApiClient().patch(`/documents/collections/${id}`, updates);
      return data;
    },

    deleteCollection: async (id: string): Promise<void> => {
      await getApiClient().delete(`/documents/collections/${id}`);
    },

    // Folder-structure templates
    listFolderTemplates: async (): Promise<Array<{ id: string; name: string; folderCount: number; createdAt: string }>> => {
      const { data } = await getApiClient().get('/documents/collections/templates');
      return data.data || [];
    },

    saveFolderTemplate: async (name: string, fromJobId?: string): Promise<{ id: string; name: string }> => {
      const { data } = await getApiClient().post('/documents/collections/templates', { name, fromJobId });
      return data.data;
    },

    applyFolderTemplate: async (templateId: string, jobId: string): Promise<{ created: number; reused: number }> => {
      const { data } = await getApiClient().post(`/documents/collections/templates/${templateId}/apply`, { jobId });
      return data.data;
    },

    getFolderTemplateTree: async (templateId: string): Promise<DocumentCollection[]> => {
      const { data } = await getApiClient().get(`/documents/collections/templates/${templateId}/tree`);
      return data.data || [];
    },

    updateFolderTemplateFromJob: async (templateId: string, fromJobId: string): Promise<{ folderCount: number }> => {
      const { data } = await getApiClient().post(`/documents/collections/templates/${templateId}/update-from-job`, { fromJobId });
      return data.data;
    },

    deleteFolderTemplate: async (templateId: string): Promise<void> => {
      await getApiClient().delete(`/documents/collections/templates/${templateId}`);
    },

    addToCollection: async (collectionId: string, documentIds: string[]): Promise<void> => {
      await getApiClient().post(`/documents/collections/${collectionId}/documents`, {
        documentIds,
      });
    },

    removeFromCollection: async (collectionId: string, documentIds: string[]): Promise<void> => {
      await getApiClient().delete(`/documents/collections/${collectionId}/documents`, {
        data: { documentIds },
      });
    },

    // Saved Views
    getSavedViews: async (): Promise<DocumentSavedView[]> => {
      const { data } = await getApiClient().get('/documents/views');
      return data;
    },

    createSavedView: async (view: CreateSavedViewData): Promise<DocumentSavedView> => {
      const { data } = await getApiClient().post('/documents/views', view);
      return data;
    },

    updateSavedView: async (id: string, updates: UpdateSavedViewData): Promise<DocumentSavedView> => {
      const { data } = await getApiClient().patch(`/documents/views/${id}`, updates);
      return data;
    },

    deleteSavedView: async (id: string): Promise<void> => {
      await getApiClient().delete(`/documents/views/${id}`);
    },

    executeSavedView: async (
      id: string
    ): Promise<{ documents: Document[]; total: number }> => {
      const { data } = await getApiClient().get(`/documents/views/${id}/execute`);
      return data;
    },

    // Attribute Definitions
    getAttributeDefinitions: async (): Promise<DocumentAttributeDefinition[]> => {
      const { data } = await getApiClient().get('/documents/attributes/definitions');
      return data.data || data;
    },

    createAttributeDefinition: async (
      definition: CreateAttributeDefinitionData
    ): Promise<DocumentAttributeDefinition> => {
      const { data } = await getApiClient().post('/documents/attributes/definitions', definition);
      return data.data || data;
    },

    updateAttributeDefinition: async (
      id: string,
      updates: UpdateAttributeDefinitionData
    ): Promise<DocumentAttributeDefinition> => {
      const { data } = await getApiClient().patch(`/documents/attributes/definitions/${id}`, updates);
      return data.data || data;
    },

    deleteAttributeDefinition: async (id: string): Promise<void> => {
      await getApiClient().delete(`/documents/attributes/definitions/${id}`);
    },

    // Markups (Annotations)
    getMarkups: async (documentId: string): Promise<DocumentMarkup[]> => {
      const { data } = await getApiClient().get(`/documents/${documentId}/markups`);
      return data.data || []; // API returns { success: true, data: markups }
    },

    createMarkup: async (documentId: string, markup: CreateMarkupData): Promise<DocumentMarkup> => {
      const { data } = await getApiClient().post(`/documents/${documentId}/markups`, markup);
      return data.data; // API returns { success: true, data: markup }
    },

    updateMarkup: async (markupId: string, updates: UpdateMarkupData): Promise<DocumentMarkup> => {
      const { data } = await getApiClient().patch(`/documents/markups/${markupId}`, updates);
      return data.data; // API returns { success: true, data: markup }
    },

    deleteMarkup: async (markupId: string): Promise<void> => {
      await getApiClient().delete(`/documents/markups/${markupId}`);
    },

    resolveMarkup: async (markupId: string, resolution?: string): Promise<DocumentMarkup> => {
      const { data } = await getApiClient().post(`/documents/markups/${markupId}/resolve`, {
        resolution,
      });
      return data.data; // API returns { success: true, data: markup }
    },

    // Save all markups for a document (bulk update)
    saveMarkups: async (documentId: string, markups: any[]): Promise<void> => {
      await getApiClient().put(`/documents/${documentId}/markups`, { markups });
    },

    // Document Comments (threaded discussions)
    getComments: async (documentId: string): Promise<DocumentComment[]> => {
      const { data } = await getApiClient().get(`/documents/${documentId}/comments`);
      return data.data || [];
    },

    createComment: async (documentId: string, content: string, mentionedUserIds?: string[]): Promise<DocumentComment> => {
      const { data } = await getApiClient().post(`/documents/${documentId}/comments`, {
        content,
        mentionedUserIds,
      });
      return data.data;
    },

    replyToComment: async (
      documentId: string,
      commentId: string,
      content: string,
      mentionedUserIds?: string[]
    ): Promise<DocumentComment> => {
      const { data } = await getApiClient().post(`/documents/${documentId}/comments/${commentId}/reply`, {
        content,
        mentionedUserIds,
      });
      return data.data;
    },

    updateComment: async (documentId: string, commentId: string, content: string): Promise<DocumentComment> => {
      const { data } = await getApiClient().patch(`/documents/${documentId}/comments/${commentId}`, {
        content,
      });
      return data.data;
    },

    resolveComment: async (documentId: string, commentId: string): Promise<DocumentComment> => {
      const { data } = await getApiClient().post(`/documents/${documentId}/comments/${commentId}/resolve`);
      return data.data;
    },

    deleteComment: async (documentId: string, commentId: string): Promise<void> => {
      await getApiClient().delete(`/documents/${documentId}/comments/${commentId}`);
    },

    getCommentCount: async (documentId: string): Promise<{ total: number; unresolved: number }> => {
      const { data } = await getApiClient().get(`/documents/${documentId}/comments/count`);
      return data.data || { total: 0, unresolved: 0 };
    },

    // Workflow Templates
    getWorkflowTemplates: async (options?: { includeSteps?: boolean; isActive?: boolean }): Promise<WorkflowTemplate[]> => {
      const params: Record<string, string> = {};
      if (options?.includeSteps !== false) params.includeSteps = 'true'; // Include steps by default
      if (options?.isActive !== undefined) params.isActive = String(options.isActive);
      const { data } = await getApiClient().get('/documents/workflows/templates', { params });
      return data.data || data;
    },

    createWorkflowTemplate: async (
      template: CreateWorkflowTemplateData
    ): Promise<WorkflowTemplate> => {
      const { data } = await getApiClient().post('/documents/workflows/templates', template);
      return data;
    },

    updateWorkflowTemplate: async (
      id: string,
      updates: UpdateWorkflowTemplateData
    ): Promise<WorkflowTemplate> => {
      const { data } = await getApiClient().patch(`/documents/workflows/templates/${id}`, updates);
      return data;
    },

    deleteWorkflowTemplate: async (id: string): Promise<void> => {
      await getApiClient().delete(`/documents/workflows/templates/${id}`);
    },

    updateWorkflowTemplateSteps: async (
      id: string,
      steps: { stepNumber: number; name: string; [key: string]: unknown }[]
    ): Promise<WorkflowTemplate> => {
      const { data } = await getApiClient().put(`/documents/workflows/templates/${id}/steps`, { steps });
      return data.data;
    },

    // Workflow Instances
    getMyTasks: async (): Promise<WorkflowTask[]> => {
      const { data } = await getApiClient().get('/documents/workflows/my-tasks');
      return data;
    },

    startWorkflow: async (workflowData: StartWorkflowData): Promise<WorkflowInstance> => {
      // The API route is POST /documents/:id/workflows (there is no /workflows/start —
      // that path 404'd, so starting a workflow from the UI never worked).
      const { data } = await getApiClient().post(`/documents/${workflowData.entityId}/workflows`, workflowData);
      return data.data ?? data;
    },

    getWorkflowInstance: async (instanceId: string): Promise<WorkflowInstance> => {
      const { data } = await getApiClient().get(`/documents/workflows/instances/${instanceId}`);
      return data;
    },

    approveStep: async (stepId: string, comments?: string): Promise<void> => {
      await getApiClient().post(`/documents/workflows/steps/${stepId}/approve`, { comments });
    },

    rejectStep: async (stepId: string, reason: string): Promise<void> => {
      await getApiClient().post(`/documents/workflows/steps/${stepId}/reject`, { reason });
    },

    delegateStep: async (stepId: string, toUserId: string, reason?: string): Promise<void> => {
      await getApiClient().post(`/documents/workflows/steps/${stepId}/delegate`, {
        toUserId,
        reason,
      });
    },

    cancelWorkflow: async (instanceId: string, reason?: string): Promise<void> => {
      await getApiClient().post(`/documents/workflows/instances/${instanceId}/cancel`, { reason });
    },

    getWorkflowAuditTrail: async (
      instanceId: string
    ): Promise<Array<{ action: string; timestamp: string; userId: string; comments?: string }>> => {
      const { data } = await getApiClient().get(
        `/documents/workflows/instances/${instanceId}/audit`
      );
      return data;
    },

    // Entity Linking
    getLinkedDocuments: async (
      entityType: string,
      entityId: string
    ): Promise<Document[]> => {
      const { data } = await getApiClient().get(
        `/documents/linked/${entityType}/${entityId}`
      );
      return data;
    },

    linkDocument: async (
      documentId: string,
      entityType: string,
      entityId: string
    ): Promise<void> => {
      await getApiClient().post(`/documents/${documentId}/link`, { entityType, entityId });
    },

    unlinkDocument: async (
      documentId: string,
      entityType: string,
      entityId: string
    ): Promise<void> => {
      await getApiClient().delete(`/documents/${documentId}/link`, {
        data: { entityType, entityId },
      });
    },

    // Bulk Operations
    bulkDelete: async (documentIds: string[]): Promise<{ deleted: number; failed: number }> => {
      const { data } = await getApiClient().post('/documents/bulk/delete', { documentIds });
      return data;
    },

    bulkUpdateTags: async (
      documentIds: string[],
      addTags?: string[],
      removeTags?: string[]
    ): Promise<{ updated: number }> => {
      const { data } = await getApiClient().post('/documents/bulk/tags', {
        documentIds,
        addTags,
        removeTags,
      });
      return data;
    },

    bulkMoveToCollection: async (
      documentIds: string[],
      collectionId: string
    ): Promise<{ moved: number }> => {
      const { data } = await getApiClient().post('/documents/bulk/move', {
        documentIds,
        collectionId,
      });
      return data;
    },

    // Drawing Splitter
    drawingSplitter: {
      upload: async (
        file: File,
        options?: { discipline?: string; setName?: string },
        onProgress?: (percent: number) => void
      ): Promise<{
        jobId: string;
        status: string;
        totalPages: number;
        fileName: string;
      }> => {
        const formData = new FormData();
        formData.append('file', file);
        if (options?.discipline) formData.append('discipline', options.discipline);
        if (options?.setName) formData.append('setName', options.setName);

        const token = localStorage.getItem('accessToken');
        const response = await axios.post(
          `${API_BASE_URL}/documents/drawing-splitter/upload`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            onUploadProgress: (progressEvent) => {
              if (onProgress && progressEvent.total) {
                onProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
              }
            },
          }
        );
        return response.data.data;
      },

      getStatus: async (jobId: string): Promise<{
        jobId: string;
        status: string;
        totalPages: number;
        processedPages: number;
        error?: string;
      }> => {
        const { data } = await getApiClient().get(
          `/documents/drawing-splitter/status/${jobId}`
        );
        return data.data;
      },

      getPreview: async (jobId: string): Promise<{
        jobId: string;
        status: string;
        sourceFileName: string;
        totalPages: number;
        drawings: Array<{
          pageIndex: number;
          drawingNumber: string | null;
          title: string | null;
          revision: string | null;
          confidence: number;
          hasThumbnail: boolean;
        }>;
      }> => {
        const { data } = await getApiClient().get(
          `/documents/drawing-splitter/${jobId}/preview`
        );
        return data.data;
      },

      getThumbnailUrl: (jobId: string, pageIndex: number): string => {
        const token = localStorage.getItem('accessToken');
        return `${API_BASE_URL}/documents/drawing-splitter/${jobId}/thumbnail/${pageIndex}?token=${token}`;
      },

      getThumbnail: async (jobId: string, pageIndex: number): Promise<Blob> => {
        const { data } = await getApiClient().get(
          `/documents/drawing-splitter/${jobId}/thumbnail/${pageIndex}`,
          { responseType: 'blob' }
        );
        return data;
      },

      getPageImage: async (jobId: string, pageIndex: number): Promise<Blob> => {
        const { data } = await getApiClient().get(
          `/documents/drawing-splitter/${jobId}/page-image/${pageIndex}`,
          { responseType: 'blob' }
        );
        return data;
      },

      ocrRegion: async (
        jobId: string,
        pageIndex: number,
        region: { x: number; y: number; width: number; height: number }
      ): Promise<{ text: string; confidence: number }> => {
        const { data } = await getApiClient().post(
          `/documents/drawing-splitter/${jobId}/ocr-region/${pageIndex}`,
          region
        );
        return data.data;
      },

      updateDrawing: async (
        jobId: string,
        pageIndex: number,
        updates: { drawingNumber?: string; title?: string; revision?: string }
      ): Promise<{
        pageIndex: number;
        drawingNumber: string | null;
        title: string | null;
        revision: string | null;
        confidence: number;
      }> => {
        const { data } = await getApiClient().patch(
          `/documents/drawing-splitter/${jobId}/drawing/${pageIndex}`,
          updates
        );
        return data.data;
      },

      finalize: async (
        jobId: string,
        options: {
          collectionName: string;
          discipline?: string;
          drawings: Array<{
            pageIndex: number;
            drawingNumber: string;
            title: string;
            revision: string;
          }>;
          entityType?: string;
          entityId?: string;
        }
      ): Promise<{
        collectionId: string;
        documentIds: string[];
        fileNames: string[];
      }> => {
        const { data } = await getApiClient().post(
          `/documents/drawing-splitter/${jobId}/finalize`,
          options
        );
        return data.data;
      },

      cancel: async (jobId: string): Promise<void> => {
        await getApiClient().delete(`/documents/drawing-splitter/${jobId}`);
      },
    },
  },

  // Notifications API
  notifications: {
    getList: async (options?: {
      isRead?: boolean;
      type?: string;
      limit?: number;
      offset?: number;
    }): Promise<WorkflowNotification[]> => {
      const { data } = await getApiClient().get('/notifications', { params: options });
      return data.data || data;
    },

    getUnreadCount: async (): Promise<number> => {
      const { data } = await getApiClient().get('/notifications/unread-count');
      return data.data?.count ?? 0;
    },

    markAsRead: async (notificationId: string): Promise<WorkflowNotification> => {
      const { data } = await getApiClient().put(`/notifications/${notificationId}/read`);
      return data.data;
    },

    markAllAsRead: async (): Promise<{ markedAsRead: number }> => {
      const { data } = await getApiClient().put('/notifications/read-all');
      return data.data;
    },

    deleteNotification: async (notificationId: string): Promise<void> => {
      await getApiClient().delete(`/notifications/${notificationId}`);
    },

    deleteOldNotifications: async (days?: number): Promise<{ deleted: number }> => {
      const { data } = await getApiClient().delete('/notifications/old', { params: { days } });
      return data.data;
    },
  },

  // Daily Logs / Site Diary API
  dailyLogs: {
    list: async (filters?: DailyLogFilters): Promise<DailyLog[]> => {
      const { data } = await getApiClient().get('/daily-logs', { params: filters });
      return data.data;
    },
    get: async (id: string): Promise<DailyLog & { photos: DailyLogPhoto[] }> => {
      const { data } = await getApiClient().get(`/daily-logs/${id}`);
      return data.data;
    },
    create: async (logData: CreateDailyLogData): Promise<DailyLog> => {
      const { data } = await getApiClient().post('/daily-logs', logData);
      return data.data;
    },
    update: async (id: string, logData: UpdateDailyLogData): Promise<DailyLog> => {
      const { data } = await getApiClient().patch(`/daily-logs/${id}`, logData);
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/daily-logs/${id}`);
    },
    publish: async (id: string): Promise<DailyLog> => {
      const { data } = await getApiClient().post(`/daily-logs/${id}/publish`);
      return data.data;
    },
    getStats: async (jobId?: string): Promise<Record<string, unknown>> => {
      const { data } = await getApiClient().get('/daily-logs/stats', { params: { jobId } });
      return data.data;
    },
    getCalendar: async (params: { year: number; month: number; jobId?: string }): Promise<Array<{ date: string; status: string; id: string; jobId: string; jobNo: string; jobName: string; siteAddress?: string }>> => {
      const { data } = await getApiClient().get('/daily-logs/calendar', { params });
      return data.data;
    },
    getJobs: async (): Promise<Array<{ id: string; jobNo: string; name: string }>> => {
      const { data } = await getApiClient().get('/daily-logs/jobs');
      return data.data;
    },
    getSettings: async (): Promise<Record<string, unknown>> => {
      const { data } = await getApiClient().get('/daily-logs/settings');
      return data.data;
    },
    addPhoto: async (dailyLogId: string, formData: FormData): Promise<DailyLogPhoto> => {
      const { data } = await getApiClient().post(`/daily-logs/${dailyLogId}/photos`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    removePhoto: async (dailyLogId: string, photoId: string): Promise<void> => {
      await getApiClient().delete(`/daily-logs/${dailyLogId}/photos/${photoId}`);
    },
    updatePhotoCaption: async (dailyLogId: string, photoId: string, caption: string): Promise<DailyLogPhoto> => {
      const { data } = await getApiClient().patch(`/daily-logs/${dailyLogId}/photos/${photoId}`, { caption });
      return data.data;
    },
    reorderPhotos: async (dailyLogId: string, photoIds: string[]): Promise<void> => {
      await getApiClient().post(`/daily-logs/${dailyLogId}/photos/reorder`, { photoIds });
    },
  },

  // Checklist templates (site quality & safety)
  checklistTemplates: {
    list: async (includeArchived = false): Promise<ChecklistTemplate[]> => {
      const { data } = await getApiClient().get('/checklist-templates', { params: includeArchived ? { includeArchived: 'true' } : undefined });
      return data.data;
    },
    get: async (id: string): Promise<ChecklistTemplate> => {
      const { data } = await getApiClient().get(`/checklist-templates/${id}`);
      return data.data;
    },
    create: async (body: { name: string; category?: string; description?: string; requiresSignoff?: boolean; items?: unknown[] }): Promise<ChecklistTemplate> => {
      const { data } = await getApiClient().post('/checklist-templates', body);
      return data.data;
    },
    update: async (id: string, body: Partial<{ name: string; category: string; description: string; status: string; requiresSignoff: boolean }>): Promise<ChecklistTemplate> => {
      const { data } = await getApiClient().patch(`/checklist-templates/${id}`, body);
      return data.data;
    },
    replaceItems: async (id: string, items: unknown[]): Promise<ChecklistTemplate> => {
      const { data } = await getApiClient().put(`/checklist-templates/${id}/items`, { items });
      return data.data;
    },
    seedLibrary: async (): Promise<{ added: number; skipped: number }> => {
      const { data } = await getApiClient().post('/checklist-templates/seed-library');
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/checklist-templates/${id}`);
    },
  },

  // Inspections (conducted checklists)
  inspections: {
    list: async (filters?: { jobId?: string; status?: InspectionStatus; assignedTo?: string; search?: string }): Promise<Inspection[]> => {
      const { data } = await getApiClient().get('/inspections', { params: filters });
      return data.data;
    },
    get: async (id: string): Promise<Inspection> => {
      const { data } = await getApiClient().get(`/inspections/${id}`);
      return data.data;
    },
    create: async (body: { templateId: string; jobId?: string; assignedTo?: string; scheduledFor?: string }): Promise<Inspection> => {
      const { data } = await getApiClient().post('/inspections', body);
      return data.data;
    },
    update: async (id: string, body: Record<string, unknown>): Promise<Inspection> => {
      const { data } = await getApiClient().patch(`/inspections/${id}`, body);
      return data.data;
    },
    answerItem: async (id: string, itemId: string, body: { answer?: string | null; note?: string | null }): Promise<InspectionItem> => {
      const { data } = await getApiClient().post(`/inspections/${id}/items/${itemId}/answer`, body);
      return data.data;
    },
    uploadPhoto: async (id: string, file: File, itemId?: string, caption?: string): Promise<{ id: string; documentId: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      if (itemId) formData.append('itemId', itemId);
      if (caption) formData.append('caption', caption);
      const { data } = await getApiClient().post(`/inspections/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.data;
    },
    submit: async (id: string, body: { signature: { name: string; imageData: string }; notes?: string; location?: { lat: number; lng: number; accuracyM?: number }; defects?: unknown[] }): Promise<Inspection> => {
      const { data } = await getApiClient().post(`/inspections/${id}/submit`, body);
      return data.data;
    },
    pdf: async (id: string): Promise<Blob> => {
      const { data } = await getApiClient().get(`/inspections/${id}/pdf`, { responseType: 'blob' });
      return data;
    },
    stats: async (): Promise<InspectionStats> => {
      const { data } = await getApiClient().get('/inspections/stats');
      return data.data;
    },
    signOff: async (id: string, signature: { name: string; imageData: string }): Promise<Inspection> => {
      const { data } = await getApiClient().post(`/inspections/${id}/signoff`, { signature });
      return data.data;
    },
    sharePortal: async (id: string, visible: boolean): Promise<Inspection> => {
      const { data } = await getApiClient().post(`/inspections/${id}/share-portal`, { visible });
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/inspections/${id}`);
    },
  },

  // Safety pack: certifications, inductions, SWMS, incidents, toolbox talks
  safety: {
    listCertifications: async (employeeId?: string): Promise<EmployeeCertification[]> => {
      const { data } = await getApiClient().get('/safety/certifications', { params: employeeId ? { employeeId } : undefined });
      return data.data;
    },
    createCertification: async (body: Record<string, unknown>): Promise<EmployeeCertification> => {
      const { data } = await getApiClient().post('/safety/certifications', body);
      return data.data;
    },
    updateCertification: async (id: string, body: Record<string, unknown>): Promise<EmployeeCertification> => {
      const { data } = await getApiClient().patch(`/safety/certifications/${id}`, body);
      return data.data;
    },
    deleteCertification: async (id: string): Promise<void> => {
      await getApiClient().delete(`/safety/certifications/${id}`);
    },
    listInductions: async (filters?: { jobId?: string; search?: string }): Promise<Induction[]> => {
      const { data } = await getApiClient().get('/safety/inductions', { params: filters });
      return data.data;
    },
    createInduction: async (body: Record<string, unknown>): Promise<Induction> => {
      const { data } = await getApiClient().post('/safety/inductions', body);
      return data.data;
    },
    deleteInduction: async (id: string): Promise<void> => {
      await getApiClient().delete(`/safety/inductions/${id}`);
    },
    listSwms: async (includeArchived = false): Promise<SwmsEntry[]> => {
      const { data } = await getApiClient().get('/safety/swms', { params: includeArchived ? { includeArchived: 'true' } : undefined });
      return data.data;
    },
    createSwms: async (body: Record<string, unknown>): Promise<SwmsEntry> => {
      const { data } = await getApiClient().post('/safety/swms', body);
      return data.data;
    },
    updateSwms: async (id: string, body: Record<string, unknown>): Promise<SwmsEntry> => {
      const { data } = await getApiClient().patch(`/safety/swms/${id}`, body);
      return data.data;
    },
    deleteSwms: async (id: string): Promise<void> => {
      await getApiClient().delete(`/safety/swms/${id}`);
    },
    listIncidents: async (filters?: { jobId?: string; status?: string; search?: string }): Promise<Incident[]> => {
      const { data } = await getApiClient().get('/safety/incidents', { params: filters });
      return data.data;
    },
    getIncident: async (id: string): Promise<Incident> => {
      const { data } = await getApiClient().get(`/safety/incidents/${id}`);
      return data.data;
    },
    createIncident: async (body: Record<string, unknown>): Promise<Incident> => {
      const { data } = await getApiClient().post('/safety/incidents', body);
      return data.data;
    },
    updateIncident: async (id: string, body: Record<string, unknown>): Promise<Incident> => {
      const { data } = await getApiClient().patch(`/safety/incidents/${id}`, body);
      return data.data;
    },
    uploadIncidentPhoto: async (id: string, file: File, caption?: string): Promise<{ id: string; documentId: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      if (caption) formData.append('caption', caption);
      const { data } = await getApiClient().post(`/safety/incidents/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.data;
    },
    deleteIncident: async (id: string): Promise<void> => {
      await getApiClient().delete(`/safety/incidents/${id}`);
    },
    listToolboxTalks: async (filters?: { jobId?: string }): Promise<ToolboxTalk[]> => {
      const { data } = await getApiClient().get('/safety/toolbox-talks', { params: filters });
      return data.data;
    },
    getToolboxTalk: async (id: string): Promise<ToolboxTalk> => {
      const { data } = await getApiClient().get(`/safety/toolbox-talks/${id}`);
      return data.data;
    },
    createToolboxTalk: async (body: Record<string, unknown>): Promise<ToolboxTalk> => {
      const { data } = await getApiClient().post('/safety/toolbox-talks', body);
      return data.data;
    },
    addTalkAttendee: async (talkId: string, body: Record<string, unknown>): Promise<ToolboxTalkAttendee> => {
      const { data } = await getApiClient().post(`/safety/toolbox-talks/${talkId}/attendees`, body);
      return data.data;
    },
    deleteToolboxTalk: async (id: string): Promise<void> => {
      await getApiClient().delete(`/safety/toolbox-talks/${id}`);
    },
  },

  // Recurring inspection schedules
  inspectionSchedules: {
    list: async (): Promise<InspectionSchedule[]> => {
      const { data } = await getApiClient().get('/inspection-schedules');
      return data.data;
    },
    create: async (body: { templateId: string; jobId?: string; assignedTo?: string; frequency: string; nextDate: string }): Promise<InspectionSchedule> => {
      const { data } = await getApiClient().post('/inspection-schedules', body);
      return data.data;
    },
    update: async (id: string, body: Record<string, unknown>): Promise<InspectionSchedule> => {
      const { data } = await getApiClient().patch(`/inspection-schedules/${id}`, body);
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/inspection-schedules/${id}`);
    },
  },

  // Defects / punch list
  defects: {
    list: async (filters?: { jobId?: string; status?: DefectStatus; assignedTo?: string; supplierId?: string; inspectionId?: string; search?: string }): Promise<Defect[]> => {
      const { data } = await getApiClient().get('/defects', { params: filters });
      return data.data;
    },
    get: async (id: string): Promise<Defect> => {
      const { data } = await getApiClient().get(`/defects/${id}`);
      return data.data;
    },
    create: async (body: Record<string, unknown>): Promise<Defect> => {
      const { data } = await getApiClient().post('/defects', body);
      return data.data;
    },
    update: async (id: string, body: Record<string, unknown>): Promise<Defect> => {
      const { data } = await getApiClient().patch(`/defects/${id}`, body);
      return data.data;
    },
    uploadPhoto: async (id: string, file: File, stage: 'before' | 'after' = 'before', caption?: string): Promise<{ id: string; documentId: string }> => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('stage', stage);
      if (caption) formData.append('caption', caption);
      const { data } = await getApiClient().post(`/defects/${id}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/defects/${id}`);
    },
  },

  // RFI (Request for Information) API
  rfis: {
    list: async (filters?: {
      jobId?: string;
      status?: string;
      priority?: string;
      type?: string;
      stage?: string;
      label?: string;
      search?: string;
    }): Promise<RFI[]> => {
      const { data } = await getApiClient().get('/rfis', { params: filters });
      return data.data;
    },
    get: async (id: string): Promise<RFI> => {
      const { data } = await getApiClient().get(`/rfis/${id}`);
      return data.data;
    },
    create: async (rfiData: CreateRFIData): Promise<RFI> => {
      const { data } = await getApiClient().post('/rfis', rfiData);
      return data.data;
    },
    update: async (id: string, rfiData: UpdateRFIData): Promise<RFI> => {
      const { data } = await getApiClient().patch(`/rfis/${id}`, rfiData);
      return data.data;
    },
    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/rfis/${id}`);
    },
    getStats: async (jobId?: string): Promise<Record<string, number>> => {
      const { data } = await getApiClient().get('/rfis/stats', { params: { jobId } });
      return data.data;
    },
    getSettings: async (): Promise<Record<string, unknown>> => {
      const { data } = await getApiClient().get('/rfis/settings');
      return data.data;
    },
    addAttachment: async (id: string, attachment: { documentId: string; fileName: string }): Promise<RFI> => {
      const { data } = await getApiClient().post(`/rfis/${id}/attachments`, attachment);
      return data.data;
    },
    removeAttachment: async (id: string, documentId: string): Promise<RFI> => {
      const { data } = await getApiClient().delete(`/rfis/${id}/attachments/${documentId}`);
      return data.data;
    },
    // Notes
    getNotes: async (rfiId: string): Promise<RFINote[]> => {
      const { data } = await getApiClient().get(`/rfis/${rfiId}/notes`);
      return data.data;
    },
    createNote: async (rfiId: string, content: string, parentNoteId?: string): Promise<RFINote> => {
      const { data } = await getApiClient().post(`/rfis/${rfiId}/notes`, { content, parentNoteId });
      return data.data;
    },
    updateNote: async (rfiId: string, noteId: string, content: string): Promise<RFINote> => {
      const { data } = await getApiClient().patch(`/rfis/${rfiId}/notes/${noteId}`, { content });
      return data.data;
    },
    deleteNote: async (rfiId: string, noteId: string): Promise<void> => {
      await getApiClient().delete(`/rfis/${rfiId}/notes/${noteId}`);
    },
    // Recipients
    getRecipients: async (rfiId: string): Promise<RFIRecipient[]> => {
      const { data } = await getApiClient().get(`/rfis/${rfiId}/recipients`);
      return data.data;
    },
    addRecipients: async (rfiId: string, recipients: Array<{ contactId?: string; email: string; name?: string }>): Promise<RFIRecipient[]> => {
      const { data } = await getApiClient().post(`/rfis/${rfiId}/recipients`, { recipients });
      return data.data;
    },
    removeRecipient: async (rfiId: string, recipientId: string): Promise<void> => {
      await getApiClient().delete(`/rfis/${rfiId}/recipients/${recipientId}`);
    },
    // Sending
    sendToAll: async (rfiId: string): Promise<{ sent: number; failed: number; errors: string[] }> => {
      const { data } = await getApiClient().post(`/rfis/${rfiId}/send`);
      return data.data;
    },
    sendToRecipient: async (rfiId: string, recipientId: string): Promise<void> => {
      await getApiClient().post(`/rfis/${rfiId}/send/${recipientId}`);
    },
  },

  // Quotes / Proposals API
  quotes: {
    list: async (filters?: {
      jobId?: string;
      contactId?: string;
      status?: string;
      isTemplate?: boolean;
      search?: string;
      limit?: number;
      offset?: number;
    }) => {
      const { data } = await getApiClient().get('/quotes', { params: filters });
      return data.data;
    },
    getStats: async (filters?: { jobId?: string; contactId?: string }) => {
      const { data } = await getApiClient().get('/quotes/stats', { params: filters });
      return data.data;
    },
    get: async (id: string) => {
      const { data } = await getApiClient().get(`/quotes/${id}`);
      return data.data;
    },

    // Management: extend/clear the validity date on an issued proposal
    extendValidity: async (id: string, validUntil: string | null) => {
      const { data } = await getApiClient().patch(`/quotes/${id}/validity`, { validUntil });
      return data.data;
    },

    // BYO Word templates (cover letter / preliminary agreement)
    getWordTemplates: async (): Promise<{ converterAvailable: boolean; templates: Record<string, { fileName: string; uploadedAt: string }> }> => {
      const { data } = await getApiClient().get('/quotes/word-templates');
      return data.data;
    },
    uploadWordTemplate: async (slot: string, file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await getApiClient().post(`/quotes/word-templates/${slot}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },
    deleteWordTemplate: async (slot: string) => {
      const { data } = await getApiClient().delete(`/quotes/word-templates/${slot}`);
      return data.data;
    },
    downloadWordTemplate: async (slot: string): Promise<Blob> => {
      const { data } = await getApiClient().get(`/quotes/word-templates/${slot}/download`, { responseType: 'blob' });
      return data;
    },
    create: async (quoteData: Record<string, unknown>) => {
      const { data } = await getApiClient().post('/quotes', quoteData);
      return data.data;
    },
    update: async (id: string, updates: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/quotes/${id}`, updates);
      return data.data;
    },
    delete: async (id: string) => {
      await getApiClient().delete(`/quotes/${id}`);
    },
    // Items
    addItem: async (quoteId: string, itemData: Record<string, unknown>) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/items`, itemData);
      return data.data;
    },
    updateItem: async (quoteId: string, itemId: string, updates: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/quotes/${quoteId}/items/${itemId}`, updates);
      return data.data;
    },
    removeItem: async (quoteId: string, itemId: string) => {
      await getApiClient().delete(`/quotes/${quoteId}/items/${itemId}`);
    },
    reorderItems: async (quoteId: string, items: { id: string; sortOrder: number; parentItemId?: string | null }[]) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/items/reorder`, { items });
      return data;
    },
    addItemsFromCatalogue: async (quoteId: string, catalogueItemIds: string[]) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/items/from-catalogue`, { catalogueItemIds });
      return data.data;
    },
    // Status transitions
    send: async (quoteId: string, email?: string): Promise<{ quote: Quote; emailSent: boolean; emailError?: string }> => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/send`, { email });
      return { quote: data.data as Quote, emailSent: data.emailSent !== false, emailError: data.emailError as string | undefined };
    },
    accept: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/accept`);
      return data.data;
    },
    reject: async (quoteId: string, reason?: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/reject`, { reason });
      return data.data;
    },
    expire: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/expire`);
      return data.data;
    },
    markWon: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/won`);
      return data.data;
    },
    markLost: async (quoteId: string, reason?: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/lost`, { reason });
      return data.data;
    },
    // Duplicate / Revise / Template
    duplicate: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/duplicate`);
      return data.data;
    },
    revise: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/revise`);
      return data.data;
    },
    createFromTemplate: async (templateId: string, quoteData: Record<string, unknown>) => {
      const { data } = await getApiClient().post(`/quotes/from-template/${templateId}`, quoteData);
      return data.data;
    },
    updateMetadata: async (quoteId: string, metadata: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/quotes/${quoteId}/metadata`, { metadata });
      return data;
    },
    // ---- Tender extension ----
    // A job's estimate total (ex GST) — tender base price / template-delta helper
    getTenderJobTotal: async (jobNo: string): Promise<{ jobNo: string; jobName: string | null; total: number }> => {
      const { data } = await getApiClient().get(`/quotes/tender/job-total/${encodeURIComponent(jobNo)}`);
      return data.data;
    },

    // Save the tender payload (sections + pricing) onto a draft quote.
    // Returns the internal margin analysis so the builder can advise the estimator.
    updateTender: async (quoteId: string, tender: TenderData): Promise<{ quote: Quote; analysis: TenderMarginAnalysis }> => {
      const { data } = await getApiClient().put(`/quotes/${quoteId}/tender`, tender);
      return { quote: data.data, analysis: data.analysis };
    },

    convertToJob: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/convert-to-job`);
      return data.data;
    },
    downloadPDF: async (quoteId: string) => {
      const response = await getApiClient().get(`/quotes/${quoteId}/pdf`, { responseType: 'blob' });
      return response.data;
    },
    recalculate: async (quoteId: string) => {
      const { data } = await getApiClient().post(`/quotes/${quoteId}/recalculate`);
      return data.data;
    },
  },

  // RFQ (Request for Quote) API
  rfqs: {
    list: async (filters?: {
      jobId?: string;
      costCentreId?: string;
      status?: RFQStatus;
      search?: string;
      limit?: number;
      offset?: number;
    }): Promise<RFQ[]> => {
      const { data } = await getApiClient().get('/rfqs', { params: filters });
      return data.data;
    },

    getStats: async (jobId?: string): Promise<{
      total: number;
      draft: number;
      sent: number;
      inProgress: number;
      completed: number;
      cancelled: number;
    }> => {
      const { data } = await getApiClient().get('/rfqs/stats', { params: jobId ? { jobId } : {} });
      return data.data;
    },

    getByJobNo: async (jobNo: string): Promise<RFQ[]> => {
      const { data } = await getApiClient().get(`/rfqs/job/${encodeURIComponent(jobNo)}`);
      return data.data;
    },

    getCostCentres: async (jobNo?: string): Promise<Array<{ id: string; code: string; name: string; tier: number; sortOrder: number }>> => {
      const url = jobNo ? `/rfqs/cost-centres/${encodeURIComponent(jobNo)}` : '/rfqs/cost-centres';
      const { data } = await getApiClient().get(url);
      return data.data;
    },

    get: async (id: string): Promise<RFQ> => {
      const { data } = await getApiClient().get(`/rfqs/${id}`);
      return data.data;
    },

    create: async (rfqData: CreateRFQData): Promise<RFQ> => {
      const { data } = await getApiClient().post('/rfqs', rfqData);
      return data.data;
    },

    update: async (id: string, updates: UpdateRFQData): Promise<RFQ> => {
      const { data } = await getApiClient().patch(`/rfqs/${id}`, updates);
      return data.data;
    },

    delete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/rfqs/${id}`);
    },

    // Items
    addItems: async (rfqId: string, items: CreateRFQItemData[]): Promise<RFQItem[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/items`, items);
      return data.data;
    },

    addItemsFromBOQ: async (rfqId: string, jobId: string, costCentreId?: string, bLoad?: number): Promise<RFQItem[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/items/from-boq`, { jobId, costCentreId, bLoad });
      return data.data;
    },

    addItemsFromCatalogue: async (rfqId: string, catalogueItemIds: string[]): Promise<RFQItem[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/items/from-catalogue`, { catalogueItemIds });
      return data.data;
    },

    removeItem: async (rfqId: string, itemId: string): Promise<void> => {
      await getApiClient().delete(`/rfqs/${rfqId}/items/${itemId}`);
    },

    // Suppliers
    addSuppliers: async (rfqId: string, supplierIds: string[]): Promise<RFQSupplier[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers`, { supplierIds });
      return data.data;
    },

    getAvailableSuppliers: async (rfqId: string): Promise<{ id: string; code: string; name: string; email: string | null }[]> => {
      const { data } = await getApiClient().get(`/rfqs/${rfqId}/suppliers/available`);
      return data.data;
    },

    removeSupplier: async (rfqId: string, supplierId: string): Promise<void> => {
      await getApiClient().delete(`/rfqs/${rfqId}/suppliers/${supplierId}`);
    },

    // Status transitions
    send: async (rfqId: string): Promise<{ rfq: RFQ; emailResult?: { sent: number; failed: number; errors: string[] } }> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/send`);
      return { rfq: data.data, emailResult: data.emailResult };
    },

    acknowledgeSupplier: async (rfqId: string, supplierId: string): Promise<RFQSupplier[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers/${supplierId}/acknowledge`);
      return data.data;
    },

    recordQuote: async (rfqId: string, supplierId: string, quoteData: RecordQuoteData): Promise<RFQSupplier[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers/${supplierId}/quote`, quoteData);
      return data.data;
    },

    /** AI-read a supplier quote (file or pasted text) → match proposal for the Record Quote form. */
    extractQuote: async (rfqId: string, input: { file?: File; text?: string }): Promise<{
      quoteReference?: string;
      quoteTotal?: number;
      validUntil?: string;
      matched: Array<{ rfqItemId: string; description: string; unitPrice: number; quantity?: number; notes?: string }>;
      unmatched: Array<{ description: string; unitPrice?: number; quantity?: number; notes?: string }>;
      model: string;
    }> => {
      let body: FormData | { text: string };
      if (input.file) {
        const form = new FormData();
        form.append('file', input.file);
        if (input.text) form.append('text', input.text);
        body = form;
      } else {
        body = { text: input.text ?? '' };
      }
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/extract-quote`, body, { timeout: 120_000 });
      return data.data;
    },

    markSuccessful: async (rfqId: string, supplierId: string): Promise<RFQSupplier[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers/${supplierId}/successful`);
      return data.data;
    },

    markUnsuccessful: async (rfqId: string, supplierId: string, reason?: string): Promise<RFQSupplier[]> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers/${supplierId}/unsuccessful`, { reason });
      return data.data;
    },

    cancel: async (rfqId: string, reason?: string): Promise<RFQ> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/cancel`, { reason });
      return data.data;
    },

    // Quote comparison & price integration
    getComparison: async (rfqId: string): Promise<QuoteComparisonData> => {
      const { data } = await getApiClient().get(`/rfqs/${rfqId}/comparison`);
      return data.data;
    },

    applyPrices: async (rfqId: string, supplierId: string): Promise<{ appliedCount: number; skippedCount: number }> => {
      const { data } = await getApiClient().post(`/rfqs/${rfqId}/suppliers/${supplierId}/apply-prices`);
      return data.data;
    },
  },

  // Transmittals API
  transmittals: {
    // Folder operations
    getFolders: async (options?: {
      jobId?: string;
      parentId?: string | null;
      folderType?: TransmittalFolderType;
      includeTransmittalCount?: boolean;
    }): Promise<TransmittalFolder[]> => {
      const params: Record<string, unknown> = { ...options };
      if (options?.parentId === null) params.parentId = 'null';
      const { data } = await getApiClient().get('/transmittals/folders', { params });
      return data.data || data;
    },

    getFolder: async (id: string): Promise<TransmittalFolder> => {
      const { data } = await getApiClient().get(`/transmittals/folders/${id}`);
      return data.data;
    },

    getFolderTree: async (jobId?: string): Promise<TransmittalFolder[]> => {
      const { data } = await getApiClient().get('/transmittals/folders/tree', { params: { jobId } });
      return data.data || data;
    },

    getFolderPath: async (folderId: string): Promise<TransmittalFolder[]> => {
      const { data } = await getApiClient().get(`/transmittals/folders/${folderId}/path`);
      return data.data || data;
    },

    createFolder: async (folderData: CreateTransmittalFolderData): Promise<TransmittalFolder> => {
      const { data } = await getApiClient().post('/transmittals/folders', folderData);
      return data.data;
    },

    updateFolder: async (id: string, updates: UpdateTransmittalFolderData): Promise<TransmittalFolder> => {
      const { data } = await getApiClient().patch(`/transmittals/folders/${id}`, updates);
      return data.data;
    },

    deleteFolder: async (id: string): Promise<void> => {
      await getApiClient().delete(`/transmittals/folders/${id}`);
    },

    moveFolder: async (id: string, newParentId: string | null): Promise<TransmittalFolder> => {
      const { data } = await getApiClient().post(`/transmittals/folders/${id}/move`, { newParentId });
      return data.data;
    },

    createDefaultFolders: async (jobId: string): Promise<TransmittalFolder[]> => {
      const { data } = await getApiClient().post('/transmittals/folders/default', { jobId });
      return data.data || data;
    },

    // Transmittal operations
    getList: async (options?: {
      jobId?: string;
      supplierId?: string;
      contactId?: string;
      folderId?: string;
      discipline?: string;
      documentType?: string;
      status?: string | string[];
      search?: string;
      limit?: number;
      offset?: number;
    }): Promise<Transmittal[]> => {
      const { data } = await getApiClient().get('/transmittals', { params: options });
      return data.data || data;
    },

    getTransmittal: async (id: string): Promise<Transmittal> => {
      const { data } = await getApiClient().get(`/transmittals/${id}`);
      return data.data;
    },

    createTransmittal: async (transmittalData: CreateTransmittalData): Promise<Transmittal> => {
      const { data } = await getApiClient().post('/transmittals', transmittalData);
      return data.data;
    },

    updateTransmittal: async (id: string, updates: UpdateTransmittalData): Promise<Transmittal> => {
      const { data } = await getApiClient().patch(`/transmittals/${id}`, updates);
      return data.data;
    },

    deleteTransmittal: async (id: string): Promise<void> => {
      await getApiClient().delete(`/transmittals/${id}`);
    },

    issueTransmittal: async (id: string): Promise<Transmittal> => {
      const { data } = await getApiClient().post(`/transmittals/${id}/issue`);
      return data.data;
    },

    resubmitTransmittal: async (id: string, updates?: Partial<CreateTransmittalData> & { keepDocumentVersions?: boolean }): Promise<Transmittal> => {
      const { data } = await getApiClient().post(`/transmittals/${id}/resubmit`, updates || {});
      return data.data;
    },

    getRevisionHistory: async (id: string): Promise<Transmittal[]> => {
      const { data } = await getApiClient().get(`/transmittals/${id}/revision-history`);
      return data.data || data;
    },

    // Documents in transmittal
    getDocuments: async (transmittalId: string): Promise<TransmittalDocument[]> => {
      const { data } = await getApiClient().get(`/transmittals/${transmittalId}/documents`);
      return data.data || data;
    },

    addDocuments: async (transmittalId: string, documentIds: string[]): Promise<TransmittalDocument[]> => {
      const { data } = await getApiClient().post(`/transmittals/${transmittalId}/documents`, { documentIds });
      return data.data;
    },

    removeDocument: async (transmittalId: string, documentId: string): Promise<void> => {
      await getApiClient().delete(`/transmittals/${transmittalId}/documents/${documentId}`);
    },

    // Swap an attached document for the latest version in its lineage (draft only)
    useLatestVersion: async (transmittalId: string, documentId: string): Promise<TransmittalDocument[]> => {
      const { data } = await getApiClient().post(`/transmittals/${transmittalId}/documents/${documentId}/use-latest`);
      return data.data;
    },

    // Copy a transmittal (any status) as a fresh draft — new number, no supersede link
    duplicate: async (transmittalId: string): Promise<Transmittal> => {
      const { data } = await getApiClient().post(`/transmittals/${transmittalId}/duplicate`);
      return data.data;
    },

    // Transmittals carrying an out-of-date version of this document
    getDocumentUsage: async (documentId: string): Promise<TransmittalDocumentUsage[]> => {
      const { data } = await getApiClient().get(`/transmittals/document-usage/${documentId}`);
      return data.data || [];
    },

    reorderDocuments: async (transmittalId: string, documentIds: string[]): Promise<TransmittalDocument[]> => {
      const { data } = await getApiClient().put(`/transmittals/${transmittalId}/documents/order`, { documentIds });
      return data.data;
    },

    // Access links
    createAccessLink: async (transmittalId: string, linkData: CreateAccessLinkData): Promise<TransmittalAccessLink> => {
      const { data } = await getApiClient().post(`/transmittals/${transmittalId}/access-links`, linkData);
      return data.data;
    },
    sendTransmittal: async (
      transmittalId: string,
      payload: {
        recipients: Array<{ contactId?: string; email?: string; name?: string }>;
        tokenType?: 'view' | 'download';
        expiresInDays?: number;
        message?: string;
      }
    ): Promise<{ results: Array<{ name: string; email?: string; sent: boolean; error?: string }>; sentCount: number; failedCount: number }> => {
      const { data } = await getApiClient().post(`/transmittals/${transmittalId}/send`, payload);
      return data.data;
    },

    getAccessLinks: async (transmittalId: string): Promise<TransmittalAccessLink[]> => {
      const { data } = await getApiClient().get(`/transmittals/${transmittalId}/access-links`);
      return data.data || data;
    },

    revokeAccessLink: async (linkId: string, reason?: string): Promise<void> => {
      await getApiClient().delete(`/transmittals/access-links/${linkId}`, { data: { reason } });
    },

    getAccessLog: async (transmittalId: string): Promise<TransmittalAccessLog[]> => {
      const { data } = await getApiClient().get(`/transmittals/${transmittalId}/access-log`);
      return data.data || data;
    },

    getAcknowledgements: async (transmittalId: string): Promise<TransmittalAcknowledgement[]> => {
      const { data } = await getApiClient().get(`/transmittals/${transmittalId}/acknowledgements`);
      return data.data || data;
    },

    getRecipientStatus: async (transmittalId: string): Promise<TransmittalRecipientStatus[]> => {
      const { data } = await getApiClient().get(`/transmittals/${transmittalId}/recipient-status`);
      return data.data || data;
    },

    // Downloads
    downloadCoverSheet: async (transmittalId: string): Promise<Blob> => {
      const response = await getApiClient().get(`/transmittals/${transmittalId}/cover-sheet`, {
        responseType: 'blob',
      });
      return response.data;
    },

    downloadZip: async (transmittalId: string): Promise<Blob> => {
      const response = await getApiClient().get(`/transmittals/${transmittalId}/download-zip`, {
        responseType: 'blob',
      });
      return response.data;
    },
  },

  // ==================== SETTINGS ====================
  settings: {
    // Company Settings
    getCompanySettings: async (): Promise<CompanySettings | null> => {
      const { data } = await getApiClient().get('/settings/company');
      return data.data;
    },

    updateCompanySettings: async (settings: Partial<CompanySettings>): Promise<CompanySettings> => {
      const { data } = await getApiClient().patch('/settings/company', settings);
      return data.data;
    },

    updateCompanyMetadata: async (metadata: Partial<CompanySettingsMetadata>): Promise<CompanySettings> => {
      const { data } = await getApiClient().patch('/settings/company/metadata', metadata);
      return data.data;
    },

    updateTimesheetSettings: async (payload: { timesheetSelfEntryLockoutDays: number }): Promise<CompanySettings> => {
      const { data } = await getApiClient().patch('/settings/company/timesheet', payload);
      return data.data;
    },

    updatePortalContent: async (payload: PortalContentInput): Promise<CompanySettings> => {
      const { data } = await getApiClient().patch('/settings/company/portal-content', payload);
      return data.data;
    },

    uploadLogo: async (file: File): Promise<{ path: string }> => {
      const formData = new FormData();
      formData.append('logo', file);
      const { data } = await getApiClient().post('/settings/company/logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return data.data;
    },

    deleteLogo: async (): Promise<void> => {
      await getApiClient().delete('/settings/company/logo');
    },

    // AI Extraction (BYOK)
    saveAiApiKey: async (apiKey: string): Promise<void> => {
      await getApiClient().post('/settings/ai/save-key', { apiKey });
    },

    testAiApiKey: async (apiKey: string): Promise<{ valid: boolean }> => {
      const { data } = await getApiClient().post('/settings/ai/test-key', { apiKey });
      return data.data;
    },

    removeAiApiKey: async (): Promise<void> => {
      await getApiClient().delete('/settings/ai/key');
    },

    // Assistant models (Claude / ChatGPT / Gemini / DeepSeek) — multiple, switchable
    listAssistantModels: async (): Promise<Array<{ id: string; label: string; provider: string; model: string; isDefault: boolean }>> => {
      const { data } = await getApiClient().get('/settings/ai/assistant-models');
      return data.data ?? [];
    },
    addAssistantModel: async (config: { label?: string; provider: string; model: string; apiKey: string }): Promise<{ id: string; label: string; provider: string; model: string }> => {
      const { data } = await getApiClient().post('/settings/ai/assistant-models', config);
      return data.data;
    },
    removeAssistantModel: async (id: string): Promise<void> => {
      await getApiClient().delete(`/settings/ai/assistant-models/${id}`);
    },
    setDefaultAssistantModel: async (id: string): Promise<void> => {
      await getApiClient().post(`/settings/ai/assistant-models/${id}/default`);
    },
    testAssistantConfig: async (config: { provider: string; model: string; apiKey: string }): Promise<{ valid: boolean }> => {
      const { data } = await getApiClient().post('/settings/ai/assistant-test', config);
      return data.data;
    },

    // Units
    getUnits: async (): Promise<UnitFull[]> => {
      const { data } = await getApiClient().get('/settings/units');
      return data.data;
    },

    getUnit: async (id: string): Promise<UnitFull | null> => {
      const { data } = await getApiClient().get(`/settings/units/${id}`);
      return data.data;
    },

    getUnitUsage: async (id: string): Promise<{ catalogueItems: number; billItems: number }> => {
      const { data } = await getApiClient().get(`/settings/units/${id}/usage`);
      return data.data;
    },

    createUnit: async (unit: Partial<UnitFull>): Promise<UnitFull> => {
      const { data } = await getApiClient().post('/settings/units', unit);
      return data.data;
    },

    updateUnit: async (id: string, unit: Partial<UnitFull>): Promise<UnitFull> => {
      const { data } = await getApiClient().patch(`/settings/units/${id}`, unit);
      return data.data;
    },

    deleteUnit: async (id: string): Promise<void> => {
      await getApiClient().delete(`/settings/units/${id}`);
    },

    // Cost Centres
    getCostCentres: async (includeArchived = false): Promise<CostCentreFull[]> => {
      const { data } = await getApiClient().get('/settings/cost-centres', {
        params: { includeArchived },
      });
      return data.data;
    },

    getCostCentreTree: async (): Promise<Array<CostCentreFull & { children: CostCentreFull[] }>> => {
      const { data } = await getApiClient().get('/settings/cost-centres/tree');
      return data.data;
    },

    getCostCentre: async (id: string): Promise<CostCentreFull | null> => {
      const { data } = await getApiClient().get(`/settings/cost-centres/${id}`);
      return data.data;
    },

    createCostCentre: async (costCentre: Partial<CostCentreFull>): Promise<CostCentreFull> => {
      const { data } = await getApiClient().post('/settings/cost-centres', costCentre);
      return data.data;
    },

    updateCostCentre: async (id: string, costCentre: Partial<CostCentreFull>): Promise<CostCentreFull> => {
      const { data } = await getApiClient().patch(`/settings/cost-centres/${id}`, costCentre);
      return data.data;
    },

    archiveCostCentre: async (id: string): Promise<void> => {
      await getApiClient().post(`/settings/cost-centres/${id}/archive`);
    },

    restoreCostCentre: async (id: string): Promise<void> => {
      await getApiClient().post(`/settings/cost-centres/${id}/restore`);
    },

    deleteCostCentre: async (id: string): Promise<void> => {
      await getApiClient().delete(`/settings/cost-centres/${id}`);
    },

    reorderCostCentres: async (orders: { id: string; sortOrder: number }[]): Promise<void> => {
      await getApiClient().post('/settings/cost-centres/reorder', { orders });
    },

    // Users
    getUsers: async (): Promise<UserConfig[]> => {
      const { data } = await getApiClient().get('/settings/users');
      return data.data;
    },

    getUser: async (id: string): Promise<UserConfig | null> => {
      const { data } = await getApiClient().get(`/settings/users/${id}`);
      return data.data;
    },

    createUser: async (user: Partial<UserConfig>): Promise<UserConfig> => {
      const { data } = await getApiClient().post('/settings/users', user);
      return data.data;
    },

    updateUser: async (id: string, user: Partial<UserConfig>): Promise<UserConfig> => {
      const { data } = await getApiClient().patch(`/settings/users/${id}`, user);
      return data.data;
    },

    deleteUser: async (id: string): Promise<void> => {
      await getApiClient().delete(`/settings/users/${id}`);
    },

    // Audit Log
    getAuditLog: async (
      options: AuditLogSearchOptions = {}
    ): Promise<{ items: AuditLogEntry[]; total: number; page: number; limit: number }> => {
      const { data } = await getApiClient().get('/settings/audit-log', { params: options });
      return {
        items: data.data,
        total: data.total,
        page: data.page,
        limit: data.limit,
      };
    },

    // Classification Import
    getClassificationSystems: async (): Promise<ClassificationSystem[]> => {
      const { data } = await getApiClient().get('/settings/classifications');
      return data.data;
    },

    getClassificationTables: async (systemId: string): Promise<ClassificationTable[]> => {
      const { data } = await getApiClient().get(`/settings/classifications/${systemId}/tables`);
      return data.data;
    },

    previewClassificationImport: async (
      systemId: string,
      tableId: string,
      codePrefix?: string
    ): Promise<ClassificationPreview> => {
      const { data } = await getApiClient().post('/settings/classifications/preview', {
        systemId,
        tableId,
        codePrefix,
      });
      return data.data;
    },

    executeClassificationImport: async (
      systemId: string,
      tableId: string,
      options: ClassificationImportOptions
    ): Promise<ClassificationImportResult> => {
      const { data } = await getApiClient().post('/settings/classifications/import', {
        systemId,
        tableId,
        options,
      });
      return data.data;
    },

    // Cost Centre Reassignment
    getCostCentresWithUsage: async (): Promise<CostCentreWithUsage[]> => {
      const { data } = await getApiClient().get('/settings/cost-centres/reassignment/usage');
      return data.data;
    },

    suggestReassignmentMappings: async (
      sourceCostCentreIds: string[],
      targetCostCentreIds: string[]
    ): Promise<ReassignmentMapping[]> => {
      const { data } = await getApiClient().post('/settings/cost-centres/reassignment/suggest', {
        sourceCostCentreIds,
        targetCostCentreIds,
      });
      return data.data;
    },

    previewReassignment: async (mappings: ReassignmentMapping[]): Promise<ReassignmentPreview> => {
      const { data } = await getApiClient().post('/settings/cost-centres/reassignment/preview', {
        mappings,
      });
      return data.data;
    },

    executeReassignment: async (
      mappings: ReassignmentMapping[],
      archiveOldCostCentres: boolean = true
    ): Promise<ReassignmentResult> => {
      const { data } = await getApiClient().post('/settings/cost-centres/reassignment/execute', {
        mappings,
        archiveOldCostCentres,
      });
      return data.data;
    },

    // Classification Seed (Local Files with Sample Data)
    getLocalClassificationFiles: async (): Promise<ClassificationFile[]> => {
      const { data } = await getApiClient().get('/settings/classifications/local-files');
      return data.data;
    },

    previewClassificationSeed: async (
      fileName: string,
      maxLevel: number = 3,
      createSampleItems: boolean = true
    ): Promise<SeedPreview> => {
      const { data } = await getApiClient().post('/settings/classifications/seed/preview', {
        fileName,
        maxLevel,
        createSampleItems,
      });
      return data.data;
    },

    executeClassificationSeed: async (
      fileName: string,
      maxLevel: number = 3,
      clearExisting: boolean = true,
      createSampleItems: boolean = true
    ): Promise<SeedResult> => {
      const { data } = await getApiClient().post('/settings/classifications/seed/execute', {
        fileName,
        maxLevel,
        clearExisting,
        createSampleItems,
      });
      return data.data;
    },

    // Bulk Delete
    deleteMultipleCostCentres: async (ids: string[]): Promise<{
      deleted: number;
      skipped: number;
      errors: Array<{ id: string; code: string; reason: string }>;
    }> => {
      const { data } = await getApiClient().post('/settings/cost-centres/delete-multiple', { ids });
      return data.data;
    },

    deleteAllCostCentres: async (): Promise<{
      costCentresDeleted: number;
      catalogueItemsDeleted: number;
      pricesDeleted: number;
      supplierPricesDeleted: number;
      billItemsCleared: number;
    }> => {
      const { data } = await getApiClient().delete('/settings/cost-centres/all');
      return data.data;
    },
  },

  // ==================== INVITATIONS ====================
  invitations: {
    list: async (): Promise<Invitation[]> => {
      const { data } = await getApiClient().get('/invitations');
      return data.data;
    },

    create: async (invitation: CreateInvitationData): Promise<CreateInvitationResponse> => {
      const { data } = await getApiClient().post('/invitations', invitation);
      return data;
    },

    // Bulk invite (max 200/batch). Per-row results; re-running a batch is
    // idempotent — existing members / pending invites come back as 'skipped'.
    bulkCreate: async (
      invitations: Array<{ email: string; role?: string }>,
    ): Promise<{
      results: Array<{ email: string; status: 'invited' | 'skipped' | 'error'; reason?: string; inviteLink?: string }>;
      summary: { invited: number; skipped: number; errors: number; emailConfigured: boolean };
    }> => {
      const { data } = await getApiClient().post('/invitations/bulk', { invitations });
      return data.data;
    },

    revoke: async (id: string): Promise<void> => {
      await getApiClient().delete(`/invitations/${id}`);
    },

    validate: async (token: string): Promise<InvitationValidation> => {
      const { data } = await getApiClient().get(`/auth/invitation/${token}`);
      return data.data;
    },

    accept: async (token: string): Promise<{ tenantId: string; tenantSlug: string }> => {
      const { data } = await getApiClient().post(`/auth/invitation/${token}/accept`);
      return data.data;
    },

    // Create a brand-new account from an invite and join the workspace in one
    // step. Returns the same shape as login — the caller is now signed in.
    register: async (token: string, name: string, password: string): Promise<LoginResponse> => {
      const { data } = await getApiClient().post(`/auth/invitation/${token}/register`, { name, password });
      return data;
    },
  },

  // ==================== RECYCLE BIN ====================
  recycleBin: {
    getItems: async (options?: {
      entityType?: string;
      search?: string;
      limit?: number;
      offset?: number;
    }): Promise<{
      items: Array<{
        id: string;
        entityType: string;
        entityId: string;
        entityCode: string | null;
        entityName: string | null;
        deletedBy: string | null;
        deletedAt: string;
        expiresAt: string;
        daysRemaining?: number;
        metadata: Record<string, unknown>;
      }>;
      total: number;
    }> => {
      const { data } = await getApiClient().get('/recycle-bin', { params: options });
      return data.data;
    },

    getStats: async (): Promise<{
      total: number;
      byType: Record<string, number>;
      expiringWithin7Days: number;
    }> => {
      const { data } = await getApiClient().get('/recycle-bin/stats');
      return data.data;
    },

    restore: async (id: string): Promise<{ success: boolean; entityType: string; entityId: string }> => {
      const { data } = await getApiClient().post(`/recycle-bin/${id}/restore`);
      return data.data || data;
    },

    permanentDelete: async (id: string): Promise<void> => {
      await getApiClient().delete(`/recycle-bin/${id}`);
    },

    restoreMultiple: async (ids: string[]): Promise<{
      success: number;
      failed: number;
      results: Array<{ id: string; success: boolean; error?: string }>;
    }> => {
      const { data } = await getApiClient().post('/recycle-bin/restore-multiple', { ids });
      return data.data || data;
    },

    deleteMultiple: async (ids: string[]): Promise<{
      success: number;
      failed: number;
      results: Array<{ id: string; success: boolean; error?: string }>;
    }> => {
      const { data } = await getApiClient().post('/recycle-bin/delete-multiple', { ids });
      return data.data || data;
    },

    empty: async (entityType?: string): Promise<{ deleted: number }> => {
      const params = entityType ? { entityType } : undefined;
      const { data } = await getApiClient().delete('/recycle-bin/empty', { params });
      return data.data || data;
    },
  },

  // ==================== BILLING ====================
  billing: {
    // Get subscription info
    getSubscription: async () => {
      const { data } = await getApiClient().get('/billing/subscription');
      return data.data;
    },

    // Get available plans
    getPlans: async () => {
      const { data } = await getApiClient().get('/billing/plans');
      return data.data;
    },

    // Get usage stats
    getUsage: async () => {
      const { data } = await getApiClient().get('/billing/usage');
      return data.data;
    },

    // Get licenses with user info
    getLicenses: async (): Promise<{
      users: Array<{
        id: string;
        email: string;
        name: string;
        role: string;
        hasLicense: boolean;
        licenseAssignedAt?: string;
      }>;
      counts: {
        used: number;
        unlimited: boolean;
        planName: string;
      };
    }> => {
      const { data } = await getApiClient().get('/billing/licenses');
      return data.data;
    },

    // Assign license to user
    assignLicense: async (userId: string) => {
      const { data } = await getApiClient().post(`/billing/licenses/${userId}/assign`);
      return data.data;
    },

    // Revoke license from user
    revokeLicense: async (userId: string) => {
      const { data } = await getApiClient().post(`/billing/licenses/${userId}/revoke`);
      return data.data;
    },

    // Remove a user from the workspace entirely (frees their seat + revokes access)
    removeWorkspaceUser: async (userId: string): Promise<void> => {
      await getApiClient().delete(`/billing/workspace-users/${userId}`);
    },

    // Change a member's tenant role (user_tenants.role — what the API gates on).
    updateWorkspaceUserRole: async (userId: string, role: string): Promise<{ message?: string }> => {
      const { data } = await getApiClient().patch(`/billing/workspace-users/${userId}/role`, { role });
      return data;
    },

    // Create checkout session
    createCheckout: async (priceId: string, quantity: number = 1): Promise<{ url: string }> => {
      const { data } = await getApiClient().post('/billing/checkout', { priceId, quantity });
      return data.data;
    },

    // Create billing portal session
    createPortal: async (): Promise<{ url: string }> => {
      const { data } = await getApiClient().post('/billing/portal');
      return data.data;
    },

    // Cancel subscription
    cancelSubscription: async (immediately: boolean = false) => {
      const { data } = await getApiClient().post('/billing/subscription/cancel', { immediately });
      return data.data;
    },
  },

  // Feedback / Issue Reporting API
  feedback: {
    create: async (report: {
      category: string;
      title: string;
      description?: string;
      screenshot?: string;
      pageRoute?: string;
      browserInfo?: Record<string, unknown>;
    }) => {
      const { data } = await getApiClient().post('/feedback', report);
      return data.data;
    },
    list: async () => {
      const { data } = await getApiClient().get('/feedback');
      return data.data;
    },
    get: async (id: string) => {
      const { data } = await getApiClient().get(`/feedback/${id}`);
      return data.data;
    },
  },

  // ============================================================
  // EMPLOYEES + TIMESHEETS — Tier-2 feature, Phase 1 backend
  // ============================================================
  employees: {
    list: async (filters: { workerType?: 'employee' | 'subcontractor'; includeArchived?: boolean } = {}) => {
      const params: Record<string, string> = {};
      if (filters.workerType) params.workerType = filters.workerType;
      if (filters.includeArchived) params.includeArchived = '1';
      const { data } = await getApiClient().get('/employees', { params });
      return data.data;
    },
    get: async (id: string) => {
      const { data } = await getApiClient().get(`/employees/${id}`);
      return data.data;
    },
    create: async (payload: Record<string, unknown>) => {
      const { data } = await getApiClient().post('/employees', payload);
      return data.data;
    },
    async bulkCreate(workers: Array<Record<string, unknown>>): Promise<{ results: Array<{ label: string; status: string; reason?: string }>; summary: { created: number; skipped: number; errors: number } }> {
      const { data } = await getApiClient().post('/employees/bulk', { workers });
      return data.data;
    },
    update: async (id: string, updates: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/employees/${id}`, updates);
      return data.data;
    },
    archive: async (id: string) => {
      await getApiClient().delete(`/employees/${id}`);
    },
  },
  timesheets: {
    list: async (filters: {
      employeeId?: string;
      jobId?: string;
      costCentreId?: string;
      fromDate?: string;
      toDate?: string;
      status?: 'draft' | 'submitted' | 'approved' | 'rejected';
      allocatedToAllowance?: string;
    } = {}) => {
      const params: Record<string, string> = {};
      for (const [k, v] of Object.entries(filters)) {
        if (v !== undefined && v !== null && v !== '') params[k] = String(v);
      }
      const { data } = await getApiClient().get('/timesheets', { params });
      return data.data;
    },
    get: async (id: string) => {
      const { data } = await getApiClient().get(`/timesheets/${id}`);
      return data.data;
    },
    create: async (payload: Record<string, unknown>) => {
      const { data } = await getApiClient().post('/timesheets', payload);
      return data.data;
    },
    update: async (id: string, updates: Record<string, unknown>) => {
      const { data } = await getApiClient().patch(`/timesheets/${id}`, updates);
      return data.data;
    },
    delete: async (id: string) => {
      await getApiClient().delete(`/timesheets/${id}`);
    },
    submit: async (entryIds: string[]) => {
      const { data } = await getApiClient().post('/timesheets/submit', { entryIds });
      return data.data;
    },
    approve: async (entryIds: string[]) => {
      const { data } = await getApiClient().post('/timesheets/approve', { entryIds });
      return data.data;
    },
    reject: async (entryIds: string[], reason?: string) => {
      const { data } = await getApiClient().post('/timesheets/reject', { entryIds, reason });
      return data.data;
    },
    unlock: async (entryIds: string[]) => {
      const { data } = await getApiClient().post('/timesheets/unlock', { entryIds });
      return data.data;
    },
  },

  // Expose the axios client for direct API calls
  getClient: () => getApiClient(),
};

export function useApi() {
  return api;
}
